/**
 * 🌍 LifeFlo AI - Global Locale Context
 * Supports 24+ countries with auto-detection and user overrides
 * 
 * Source of Truth:
 * - Language: User preference → browser default
 * - Country: IP → payment country → user override
 * - Timezone: Device timezone
 * - Currency: Payment provider (Stripe)
 * - Date/Time Format: Locale (language + country)
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== REGIONAL PRICING TABLE ====================
// Market-tested psychological prices matching local affordability,
// App Store price tier norms, and regional purchasing power

const REGIONAL_PRICING = {
  USD: { monthly: 9.99, yearly: 69.99 },      // 🇺🇸 USA
  CAD: { monthly: 12.99, yearly: 89.99 },     // 🇨🇦 Canada
  GBP: { monthly: 8.99, yearly: 62.99 },      // 🇬🇧 UK
  EUR: { monthly: 8.99, yearly: 62.99 },      // 🇩🇪🇫🇷 Germany, France (default)
  EUR_ES: { monthly: 7.99, yearly: 54.99 },   // 🇪🇸 Spain (lower tier)
  MXN: { monthly: 149, yearly: 999 },         // 🇲🇽 Mexico
  INR: { monthly: 299, yearly: 1999 },        // 🇮🇳 India
  CNY: { monthly: 38, yearly: 258 },          // 🇨🇳 China
  TWD: { monthly: 249, yearly: 1790 },        // 🇹🇼 Taiwan
  HKD: { monthly: 58, yearly: 398 },          // 🇭🇰 Hong Kong
  IDR: { monthly: 49000, yearly: 299000 },    // 🇮🇩 Indonesia
  PHP: { monthly: 249, yearly: 1699 },        // 🇵🇭 Philippines
  SGD: { monthly: 7.99, yearly: 54.99 },      // 🇸🇬 Singapore
  JPY: { monthly: 980, yearly: 6800 },        // 🇯🇵 Japan
  KRW: { monthly: 8900, yearly: 59900 },      // 🇰🇷 South Korea
  AUD: { monthly: 10.99, yearly: 79.99 },     // 🇦🇺 Australia
  BRL: { monthly: 14.90, yearly: 99.90 },     // 🇧🇷 Brazil
  NGN: { monthly: 2900, yearly: 19900 },      // 🇳🇬 Nigeria
  // Fallback currencies
  NZD: { monthly: 12.99, yearly: 89.99 },     // 🇳🇿 New Zealand
  CHF: { monthly: 9.99, yearly: 69.99 },      // 🇨🇭 Switzerland
  SEK: { monthly: 99, yearly: 699 },          // 🇸🇪 Sweden
  THB: { monthly: 299, yearly: 1999 },        // 🇹🇭 Thailand
  VND: { monthly: 199000, yearly: 1399000 },  // 🇻🇳 Vietnam
  MYR: { monthly: 39.90, yearly: 279.90 },    // 🇲🇾 Malaysia
  AED: { monthly: 29.99, yearly: 199.99 },    // 🇦🇪 UAE
};

// ==================== 24+ COUNTRY CONFIGURATIONS ====================

const COUNTRIES = {
  // North America
  US: { name: "United States", currency: "USD", symbol: "$", locale: "en-US", language: "en", flag: "🇺🇸", taxInclusive: false },
  CA: { name: "Canada", currency: "CAD", symbol: "C$", locale: "en-CA", language: "en", flag: "🇨🇦", taxInclusive: false },
  MX: { name: "Mexico", currency: "MXN", symbol: "$", locale: "es-MX", language: "es", flag: "🇲🇽", taxInclusive: true },
  
  // Europe (Tax-inclusive pricing required for EU/UK)
  GB: { name: "United Kingdom", currency: "GBP", symbol: "£", locale: "en-GB", language: "en", flag: "🇬🇧", taxInclusive: true },
  DE: { name: "Germany", currency: "EUR", symbol: "€", locale: "de-DE", language: "de", flag: "🇩🇪", taxInclusive: true },
  FR: { name: "France", currency: "EUR", symbol: "€", locale: "fr-FR", language: "fr", flag: "🇫🇷", taxInclusive: true },
  ES: { name: "Spain", currency: "EUR", symbol: "€", locale: "es-ES", language: "es", flag: "🇪🇸", taxInclusive: true, priceTier: "EUR_ES" },
  NL: { name: "Netherlands", currency: "EUR", symbol: "€", locale: "nl-NL", language: "nl", flag: "🇳🇱", taxInclusive: true },
  SE: { name: "Sweden", currency: "SEK", symbol: "kr", locale: "sv-SE", language: "sv", flag: "🇸🇪", taxInclusive: true },
  IE: { name: "Ireland", currency: "EUR", symbol: "€", locale: "en-IE", language: "en", flag: "🇮🇪", taxInclusive: true },
  CH: { name: "Switzerland", currency: "CHF", symbol: "CHF", locale: "de-CH", language: "de", flag: "🇨🇭", taxInclusive: false },
  
  // Asia
  IN: { name: "India", currency: "INR", symbol: "₹", locale: "en-IN", language: "en", flag: "🇮🇳", taxInclusive: true },
  ID: { name: "Indonesia", currency: "IDR", symbol: "Rp", locale: "id-ID", language: "id", flag: "🇮🇩", taxInclusive: true },
  PH: { name: "Philippines", currency: "PHP", symbol: "₱", locale: "en-PH", language: "en", flag: "🇵🇭", taxInclusive: true },
  SG: { name: "Singapore", currency: "SGD", symbol: "S$", locale: "en-SG", language: "en", flag: "🇸🇬", taxInclusive: true },
  JP: { name: "Japan", currency: "JPY", symbol: "¥", locale: "ja-JP", language: "ja", flag: "🇯🇵", taxInclusive: true },
  KR: { name: "South Korea", currency: "KRW", symbol: "₩", locale: "ko-KR", language: "ko", flag: "🇰🇷", taxInclusive: true },
  CN: { name: "China", currency: "CNY", symbol: "¥", locale: "zh-CN", language: "zh", flag: "🇨🇳", taxInclusive: true },
  TW: { name: "Taiwan", currency: "TWD", symbol: "NT$", locale: "zh-TW", language: "zh", flag: "🇹🇼", taxInclusive: true },
  HK: { name: "Hong Kong", currency: "HKD", symbol: "HK$", locale: "zh-HK", language: "zh", flag: "🇭🇰", taxInclusive: false },
  TH: { name: "Thailand", currency: "THB", symbol: "฿", locale: "th-TH", language: "th", flag: "🇹🇭", taxInclusive: true },
  VN: { name: "Vietnam", currency: "VND", symbol: "₫", locale: "vi-VN", language: "vi", flag: "🇻🇳", taxInclusive: true },
  MY: { name: "Malaysia", currency: "MYR", symbol: "RM", locale: "ms-MY", language: "ms", flag: "🇲🇾", taxInclusive: true },
  
  // Middle East
  AE: { name: "United Arab Emirates", currency: "AED", symbol: "د.إ", locale: "en-AE", language: "en", flag: "🇦🇪", taxInclusive: false },
  
  // Oceania (Tax-inclusive for AU)
  AU: { name: "Australia", currency: "AUD", symbol: "A$", locale: "en-AU", language: "en", flag: "🇦🇺", taxInclusive: true },
  NZ: { name: "New Zealand", currency: "NZD", symbol: "NZ$", locale: "en-NZ", language: "en", flag: "🇳🇿", taxInclusive: true },
  
  // South America
  BR: { name: "Brazil", currency: "BRL", symbol: "R$", locale: "pt-BR", language: "pt", flag: "🇧🇷", taxInclusive: true },
  
  // Africa
  NG: { name: "Nigeria", currency: "NGN", symbol: "₦", locale: "en-NG", language: "en", flag: "🇳🇬", taxInclusive: false }
};

// Helper function to get regional pricing for a country
const getRegionalPricing = (countryCode) => {
  const country = COUNTRIES[countryCode];
  if (!country) return REGIONAL_PRICING.USD;
  
  // Check if country has a specific price tier (like Spain with EUR_ES)
  const priceTier = country.priceTier || country.currency;
  return REGIONAL_PRICING[priceTier] || REGIONAL_PRICING.USD;
};

// ==================== SUPPORTED LANGUAGES ====================

const LANGUAGES = {
  en: { name: "English", nativeName: "English" },
  de: { name: "German", nativeName: "Deutsch" },
  fr: { name: "French", nativeName: "Français" },
  ja: { name: "Japanese", nativeName: "日本語" },
  ko: { name: "Korean", nativeName: "한국어" },
  pt: { name: "Portuguese", nativeName: "Português" },
  id: { name: "Indonesian", nativeName: "Bahasa Indonesia" },
  es: { name: "Spanish", nativeName: "Español" },
  zh: { name: "Chinese (Simplified)", nativeName: "简体中文" },
  nl: { name: "Dutch", nativeName: "Nederlands" },
  sv: { name: "Swedish", nativeName: "Svenska" },
  th: { name: "Thai", nativeName: "ไทย" },
  vi: { name: "Vietnamese", nativeName: "Tiếng Việt" },
  ms: { name: "Malay", nativeName: "Bahasa Melayu" },
  ar: { name: "Arabic", nativeName: "العربية" },
  he: { name: "Hebrew", nativeName: "עברית" }
};

// RTL Languages
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Timezone to Country/Region mapping for travel detection
const TIMEZONE_TO_COUNTRY = {
  // Americas
  'America/New_York': { country: 'US', region: 'New York', flag: '🇺🇸' },
  'America/Los_Angeles': { country: 'US', region: 'Los Angeles', flag: '🇺🇸' },
  'America/Chicago': { country: 'US', region: 'Chicago', flag: '🇺🇸' },
  'America/Denver': { country: 'US', region: 'Denver', flag: '🇺🇸' },
  'America/Toronto': { country: 'CA', region: 'Toronto', flag: '🇨🇦' },
  'America/Vancouver': { country: 'CA', region: 'Vancouver', flag: '🇨🇦' },
  'America/Sao_Paulo': { country: 'BR', region: 'São Paulo', flag: '🇧🇷' },
  'America/Mexico_City': { country: 'MX', region: 'Mexico City', flag: '🇲🇽' },
  // Europe
  'Europe/London': { country: 'GB', region: 'London', flag: '🇬🇧' },
  'Europe/Paris': { country: 'FR', region: 'Paris', flag: '🇫🇷' },
  'Europe/Berlin': { country: 'DE', region: 'Berlin', flag: '🇩🇪' },
  'Europe/Rome': { country: 'IT', region: 'Rome', flag: '🇮🇹' },
  'Europe/Madrid': { country: 'ES', region: 'Madrid', flag: '🇪🇸' },
  'Europe/Amsterdam': { country: 'NL', region: 'Amsterdam', flag: '🇳🇱' },
  'Europe/Zurich': { country: 'CH', region: 'Zurich', flag: '🇨🇭' },
  'Europe/Stockholm': { country: 'SE', region: 'Stockholm', flag: '🇸🇪' },
  'Europe/Moscow': { country: 'RU', region: 'Moscow', flag: '🇷🇺' },
  // Asia
  'Asia/Tokyo': { country: 'JP', region: 'Tokyo', flag: '🇯🇵' },
  'Asia/Seoul': { country: 'KR', region: 'Seoul', flag: '🇰🇷' },
  'Asia/Shanghai': { country: 'CN', region: 'Shanghai', flag: '🇨🇳' },
  'Asia/Hong_Kong': { country: 'HK', region: 'Hong Kong', flag: '🇭🇰' },
  'Asia/Singapore': { country: 'SG', region: 'Singapore', flag: '🇸🇬' },
  'Asia/Bangkok': { country: 'TH', region: 'Bangkok', flag: '🇹🇭' },
  'Asia/Jakarta': { country: 'ID', region: 'Jakarta', flag: '🇮🇩' },
  'Asia/Kolkata': { country: 'IN', region: 'India', flag: '🇮🇳' },
  'Asia/Mumbai': { country: 'IN', region: 'Mumbai', flag: '🇮🇳' },
  'Asia/Dubai': { country: 'AE', region: 'Dubai', flag: '🇦🇪' },
  'Asia/Taipei': { country: 'TW', region: 'Taipei', flag: '🇹🇼' },
  'Asia/Manila': { country: 'PH', region: 'Manila', flag: '🇵🇭' },
  'Asia/Ho_Chi_Minh': { country: 'VN', region: 'Ho Chi Minh City', flag: '🇻🇳' },
  'Asia/Kuala_Lumpur': { country: 'MY', region: 'Kuala Lumpur', flag: '🇲🇾' },
  // Oceania
  'Australia/Sydney': { country: 'AU', region: 'Sydney', flag: '🇦🇺' },
  'Australia/Melbourne': { country: 'AU', region: 'Melbourne', flag: '🇦🇺' },
  'Pacific/Auckland': { country: 'NZ', region: 'Auckland', flag: '🇳🇿' },
  // Middle East
  'Asia/Jerusalem': { country: 'IL', region: 'Israel', flag: '🇮🇱' },
  'Asia/Riyadh': { country: 'SA', region: 'Saudi Arabia', flag: '🇸🇦' },
  // Africa
  'Africa/Cairo': { country: 'EG', region: 'Cairo', flag: '🇪🇬' },
  'Africa/Johannesburg': { country: 'ZA', region: 'Johannesburg', flag: '🇿🇦' },
  'Africa/Lagos': { country: 'NG', region: 'Lagos', flag: '🇳🇬' }
};

// Get friendly timezone name
const getTimezoneDisplayName = (timezone) => {
  const mapping = TIMEZONE_TO_COUNTRY[timezone];
  if (mapping) {
    return `${mapping.flag} ${mapping.region}`;
  }
  // Fallback: convert timezone string to readable format
  return timezone.replace(/_/g, ' ').split('/').pop();
};

// ==================== UI TRANSLATIONS ====================

const TRANSLATIONS = {
  en: {
    greeting: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening", night: "Hope you're having a restful night" },
    auth: {
      tagline: "I remember how yesterday felt",
      tagline2: "and help you live today better.",
      welcomeBack: "Welcome back",
      goodToSeeYou: "Good to see you again",
      email: "Email",
      password: "Password",
      forgotPassword: "Forgot password?",
      signingIn: "Signing in...",
      signIn: "Sign In",
      newToApp: "New to LifeFlo?",
      createAccount: "Create an account"
    },
    subscription: { cta: "Start free trial", upgrade: "Upgrade to Pro", monthly: "Monthly", yearly: "Yearly", billedMonthly: "Billed monthly", billedAnnually: "Billed annually", currentPlan: "Current Plan", freeTrialActive: "Free Trial Active", trialDaysLeft: "days left in trial", choosePlan: "Choose your plan", perMonth: "/month", perYear: "/year",
      lastDayTrial: "Last day of trial", trialEndsToday: "Trial ends today", daysLeftTrial: "days left in trial", then: "Then", mo: "mo"
    },
    actions: { save: "Save", cancel: "Cancel", delete: "Delete", done: "Done", add: "Add", edit: "Edit", update: "Update", create: "Create", remove: "Remove", confirm: "Confirm", search: "Search", filter: "Filter", sort: "Sort", refresh: "Refresh", upload: "Upload", download: "Download", export: "Export", import: "Import", send: "Send", connect: "Connect", disconnect: "Disconnect", sync: "Sync", signOut: "Sign Out" },
    settings: { 
      profile: "Profile", integrations: "Integrations", subscription: "Subscription", support: "Support", memory: "Memory", language: "Language & Region", theme: "Theme",
      gender: "Gender", memberSince: "Member Since", status: "Status", active: "Active", plan: "Plan",
      calendarIntegrations: "Calendar Integrations", connectCalendars: "Connect your calendars for automatic sync",
      googleCalendar: "Google Calendar", appleCalendar: "Apple Calendar", syncEvents: "Sync events and appointments",
      connected: "Connected", notConnected: "Not connected",
      contactSupport: "Contact Support", sendMessage: "Send us a message and we'll help you out",
      requestFeature: "Request a Feature", shareIdeas: "Share your ideas to help us improve",
      whatAlloraRemembers: "What Allora Remembers", appTheme: "App Theme",
      appleIdHint: "Create an app-specific password in your Apple ID settings",
      editProfile: "Edit Profile", manageSubscription: "Manage Subscription", paymentHistory: "Payment History", logOut: "Log out",
      notifications: "Notifications",
      chooseLanguageRegion: "Choose your preferred language and region for a personalized experience",
      currentSettings: "Current Settings", detectedLocation: "Your detected location and preferences",
      country: "Country", currency: "Currency", timezone: "Timezone",
      chooseLanguage: "Choose your preferred language", updateProfile: "Update your personal information",
      regionalPricing: "Your Regional Pricing", pricesInLocalCurrency: "Prices in your local currency",
      proMonthly: "Pro Monthly", proAnnual: "Pro Annual", perMonth: "per month", perYear: "per year",
      pricesIncludeTax: "Prices include applicable taxes",
      // Goals Settings
      goals: "Goals", personalGoals: "Personal Goals", personalGoalsDesc: "Set your daily and monthly targets to track your progress",
      todaysProgress: "Today's Progress", calorieGoal: "Daily Calorie Goal", calorieGoalDesc: "Track your daily calorie intake",
      budgetGoal: "Monthly Budget Goal", budgetGoalDesc: "Set your monthly spending limit",
      waterGoal: "Daily Water Goal", waterGoalDesc: "Stay hydrated throughout the day",
      stepsGoal: "Daily Steps Goal", stepsGoalDesc: "Track your daily activity",
      sleepGoal: "Daily Sleep Goal", sleepGoalDesc: "Set your target sleep duration",
      dailyTarget: "Daily Target", monthlyLimit: "Monthly Limit", targetHours: "Target Hours",
      recommended: "Recommended", glasses: "glasses", saveGoals: "Save Goals",
      budgetTip: "Your expenses from Life Mode will be tracked against this goal"
    },
    nav: { home: "Home", work: "Work", health: "Health", life: "Life", chat: "Chat", settings: "Settings", yourspace: "YourSpace" },
    cards: { 
      chatDesc: "I'm listening.", 
      workDesc: "Let's get clarity today.", 
      healthDesc: "Your energy matters.", 
      lifeDesc: "We'll handle it together.",
      talkToAllora: "Talk to Allora",
      yourspaceDesc: "General AI chat"
    },
    allora: {
      name: "Allora",
      subtitle: "Your personal companion",
      emotionalAI: "Active",
      welcome: "Hey! I'm Allora. I remember how yesterday felt and I'm here to help you today. How are you feeling?",
      placeholder: "Share what's on your mind...",
      privacyNote: "Allora uses what you share to give more helpful and caring responses 💜",
      thinking: "Allora is thinking...",
      loading: "Allora is preparing...",
      goodMorning: "Good morning",
      goodAfternoon: "Good afternoon",
      goodEvening: "Good evening",
      lateNight: "It's late",
      imHereForYou: "I'm Allora. I remember how you've been feeling and I'm here to help. What's on your mind?"
    },
    yourspace: {
      welcome: "Welcome to YourSpace! I'm here to help with any questions or discussions. What can I help you with?",
      title: "YourSpace",
      subtitle: "General AI Chat",
      placeholder: "Ask me anything..."
    },
    suggested: {
      title: "Suggested for You",
      subtitle: "Personalized tips and actions",
      loading: "Loading your suggestions...",
      dailyTip: "Daily Tip",
      stayHydrated: "Stay Hydrated",
      waterReminder: "You've had a few glasses today. Aim for 8 glasses!",
      logWater: "Log Water",
      trackSleep: "Track Your Sleep",
      sleepReminder: "How did you sleep last night? Logging helps Allora understand your patterns.",
      logSleep: "Log Sleep",
      logBreakfast: "Log Breakfast",
      logLunch: "Log Lunch",
      logDinner: "Log Dinner",
      mealReminder: "Don't forget to log your meal for better nutrition tracking.",
      logMeal: "Log Meal",
      moveMore: "Time to Move",
      exerciseReminder: "A short walk or stretch can boost your energy and mood!",
      logWorkout: "Log Workout",
      pendingTasks: "Pending Tasks",
      tasksReminder: "You have {count} tasks waiting. Focus on one at a time!",
      viewTasks: "View Tasks",
      proactiveTip: "Proactive Tip",
      allCaughtUp: "You're all caught up!",
      nothingNew: "No new suggestions for now. Keep up the great work!",
      chatWithAllora: "Chat with Allora",
      talkToAllora: "Talk to Allora",
      important: "Important",
      water: "Water",
      calories: "Calories",
      tasks: "Tasks",
      streak: "Streak"
    },
    home: {
      todayProgress: "Today's Progress", hydration: "Hydration", nutrition: "Nutrition", tasks: "Tasks", points: "Points",
      badges: "Badges", viewAll: "View all", noBadges: "No badges yet", keepStreak: "Keep up your streaks to earn badges!",
      achievements: "achievements", allCaughtUp: "You're all caught up!", enjoyMorning: "Enjoy your morning",
      morningBriefing: "Your Morning Briefing", whatsOnPlate: "Here's what's on your plate today",
      suggestionForYou: "Suggestion for you",
      smallStepsCount: "Small steps count. I'm here for you.",
      todaysPulse: "Today's Pulse", energy: "Energy", mood: "Mood", stress: "Stress", focus: "Focus",
      balancedAndReady: "Balanced and ready", moderate: "Moderate", stable: "Stable", low: "Low", peak: "Peak", high: "High", ok: "OK",
      quickActions: "Quick Actions", mealPlanner: "Meal Planner", planYourMeals: "Plan your meals",
      workouts: "Workouts", exerciseLibrary: "Exercise library", medicines: "Medicines", checkInteractions: "Check interactions",
      logWater: "Log Water", trackHydration: "Track hydration", goalsAndChallenges: "Goals & Challenges",
      activeChallenges: "Active Challenges", newChallenge: "New Challenge", dashboard: "Dashboard",
      sleep: "Sleep", history: "History", water: "Water", calories: "Calories",
      talkToAllora: "Talk to Allora", youDontHaveToCarry: "You don't have to carry this alone.",
      oneThingAtATime: "One thing at a time. What matters most?",
      showTodaysProgress: "Show today's progress", hideTodaysProgress: "Hide today's progress",
      heyName: "Hey {name}. I'm here.", goodMorning: "Good morning", morning: "Morning", evening: "Evening",
      lateNight: "Late night", readyWhenYouAre: "Ready when you are.", howAreYou: "How are you doing?",
      windingDown: "Winding down or still going strong?", imHere: "I'm here. Talk to me.",
      imHereToHelp: "I'm here to help, not to overwhelm you.", voice: "Voice",
      eveningReflection: "Evening reflection", howWasYourDay: "How was your day? Tap to check in.",
      great: "Great", good: "Good", okay: "Okay", tough: "Tough", maybeLater: "Maybe later",
      tasksToComplete: "tasks to complete", billsDueSoon: "bills due soon", appointmentsToday: "appointments today",
      rememberWater: "Remember to drink water", bestFocus: "Best focus",
      // Emotional nudge messages
      cantSleep: "Can't sleep? I'm here. No judgments, no reminders.",
      letsTakeCare: "Let's take care of you today. What do you need?",
      howAreYouDoing: "How are you doing so far? I'm here if you need me.",
      afternoonCheckin: "Afternoon check-in: need anything from me?",
      windingDownRest: "Winding down? You did enough today. Rest is productive too.",
      imHereNoRush: "I'm here whenever you need me. No rush, no agenda.",
      failedLogMood: "Failed to log mood. Please try again.",
      takeYourTime: "Take your time. I'm here when you're ready.",
      tapToContinue: "Tap to continue",
      viewAllSuggestions: "View all suggestions",
      tapToRespond: "Tap to respond",
      // Memory section
      alloraRemembers: "Allora remembers...",
      badgesEarned: "Badges Earned",
      collector: "Collector"
    },
    work: {
      title: "Work Mode", tasks: "Tasks", documents: "Documents", addTask: "Add Task", newTask: "New Task", editTask: "Edit Task",
      taskTitle: "Task Title", taskDescription: "Description", priority: "Priority", dueDate: "Due Date", dueTime: "Time", status: "Status",
      high: "High", medium: "Medium", low: "Low", urgent: "Urgent", pending: "Pending", inProgress: "In Progress", completed: "Completed",
      addDocument: "Add Document", uploadDocument: "Upload Document", analyzeDocument: "Analyze Document", documentSummary: "Document Summary",
      askQuestion: "Ask a question about this document", todaysFocus: "Today's Focus", productivityScore: "Productivity Score",
      tasksCompleted: "Tasks Completed", noTasks: "No tasks yet", noDocuments: "No documents yet", createFirstTask: "Create your first task",
      allCaughtUp: "All Caught Up!", noUrgentTasks: "No urgent tasks for now. Great job staying on top of things!",
      taskTemplates: "Task Templates", batchActions: "Batch Actions", aiDocumentAnalysis: "AI Document Analysis",
      documentLibrary: "Document Library", 
      uploadDescription: "Upload MP3/MP4 recordings, PDF documents, or paste text for instant AI analysis & task generation",
      noDocumentsYet: "No documents analyzed yet",
      useAIDocumentAnalysis: "Use AI Document Analysis above to analyze files",
      compare: "Compare",
      enterTaskTitle: "Enter task title", addDetails: "Add details...", createTask: "Create Task", updateTask: "Update Task",
      error: "Error", pleaseEnterTitle: "Please enter a task title",
      documentAnalysis: "Document Analysis", pasteText: "Paste document text, meeting notes, or transcription here...",
      analyzing: "Analyzing...", analyze: "Analyze", summary: "Summary", keyPoints: "Key Points",
      generateTasks: "Generate Tasks", saveAndDone: "Save & Done", savedToLibrary: "Document analysis saved successfully",
      noDocumentsAnalyzed: "No documents analyzed yet", useAIAnalysis: "Use AI Document Analysis above to analyze files",
      source: "Source", unknownSource: "Unknown source", noAnalysisAvailable: "No analysis available",
      viewEdit: "View/Edit", export: "Export", search: "Search",
      dueToday: "Due today", dueTomorrow: "Due tomorrow", overdue: "Overdue", estimatedTime: "Est. time",
      comingUp: "Coming Up", next3Days: "Next 3 Days", dueIn: "Due in", moreUpcomingTasks: "more upcoming tasks",
      noTasksFound: "No Tasks Found", couldNotExtract: "Could not extract actionable tasks from the analysis",
      generatedFrom: "Generated from document analysis",
      noItemsSelected: "No Items Selected", pleaseSelectItems: "Please select items first",
      batchOperationComplete: "Batch Operation Complete", appliedToItems: "applied to", items: "items",
      unableToBatch: "Unable to complete batch operation", batchOperations: "Batch Operations",
      selected: "selected", markComplete: "Mark Complete", archive: "Archive", addTag: "Add Tag",
      quickTaskTemplates: "Quick Task Templates", loadingTemplates: "Loading templates...",
      chooseTemplate: "Choose a template to instantly create related tasks:",
      templateApplied: "Template Applied!", createdTasksFrom: "Created", tasksFromTemplate: "tasks from",
      templateFailed: "Template Failed", unableToCreateTasks: "Unable to create tasks. Please try again.",
      tasksCount: "tasks", useTemplate: "Use Template",
      // New keys for i18n
      subscription: "Subscription", pro: "Pro", freePlan: "Free Plan", aiAccess: "AI Access", unlimited: "Unlimited", credits: "credits",
      upgradeToPro: "Upgrade to Pro", uploadFile: "Upload File (MP3, MP4, PDF, TXT, DOC)", clickToSelect: "Click to select or drag & drop file",
      audioVideoDetected: "Audio/Video Detected", analyzeWhisper: "Click \"Analyze\" to transcribe and analyze your audio using AI (Whisper)",
      pasteUrl: "Paste URL (Article, Blog Post, Google Doc, etc.)", aiFetchContent: "AI will fetch and analyze content from the URL",
      documentTextOptional: "Document Text / Transcription (Optional)", or: "Or", insights: "Insights", suggestedTasks: "Suggested Tasks",
      askAnythingDoc: "Ask anything about this document:", titleRequired: "Title *", descriptionLabel: "Description"
    },
    nudge: {
      iNoticed: "I noticed something...",
      suggestedAction: "Suggested action:",
      wantMeToHelp: "Want me to help with this?",
      quickHydrationCheck: "Quick hydration check",
      hadGlassesToday: "You've had {count} glasses today. A water break might feel good!",
      tryShortBursts: "Try short bursts"
    },
    common: { loading: "Loading...", error: "Something went wrong", retry: "Retry", close: "Close", back: "Back", next: "Next", done: "Done", pending: "pending", completed: "completed", glasses: "glasses", cal: "cal", today: "Today", yesterday: "Yesterday", thisWeek: "This Week", thisMonth: "This Month", all: "All", none: "None", total: "Total", average: "Average", creating: "Creating...", add: "Add", save: "save", day: "day", days: "days",
      youSaid: "You said:", processingCommand: "Processing command...", exampleCommands: "Example Commands:", action: "Action",
      search: "Search", selectAll: "Select All", deselectAll: "Deselect All", untitled: "Untitled",
      yes: "Yes", no: "No", skip: "Skip", adjust: "Adjust"
    },
    health: {
      title: "Health Mode", water: "Water", food: "Food", exercise: "Exercise", sleep: "Sleep", medications: "Medications", vitals: "Vitals",
      addWater: "Add Water", logFood: "Log Food", logExercise: "Log Exercise", logSleep: "Log Sleep", addMedication: "Add Medication", logWorkout: "Log Workout", addFood: "Add Food",
      glasses: "glasses", calories: "calories", steps: "steps", hours: "hours", goal: "Goal", progress: "Progress",
      waterGoal: "Water Goal", calorieGoal: "Calorie Goal", stepsGoal: "Steps Goal", sleepGoal: "Sleep Goal",
      morningRoutine: "Morning Routine", eveningRoutine: "Evening Routine", medicationReminder: "Medication Reminder",
      weight: "Weight", bloodPressure: "Blood Pressure", heartRate: "Heart Rate", bloodSugar: "Blood Sugar",
      noMedications: "No medications tracked", noWorkouts: "No workouts logged today",
      avgWater: "AVG WATER", avgCalories: "AVG CALORIES", daysTracked: "DAYS TRACKED",
      keepMoving: "Keep moving!", stayHydrated: "Stay hydrated!", burnIt: "Burn it!", keepLogging: "Keep logging!",
      healthCalendar: "Health Calendar", trackYourHealthJourney: "Track your health journey",
      sleepTracking: "Sleep Tracking", autoSleepTracking: "Auto Sleep Tracking Active", recentSleepLogs: "Recent Sleep Logs",
      noSleepLogs: "No sleep logs yet. Start tracking to see insights!", logYourFirstSleep: "Log Your First Sleep",
      nutritionTracker: "Nutrition Tracker", protein: "Protein", carbs: "Carbs", fats: "Fats", fiber: "Fiber", sugar: "Sugar",
      ofGoal: "of goal", limit: "limit", womensHealth: "Women's Health",
      startYourFirstChallenge: "Start Your First Challenge", healthGoals: "Health Goals", newGoal: "New Goal",
      noGoalsYet: "No goals set yet", clickNewGoal: "Click 'New Goal' to get started", noActiveChallenges: "No active challenges", quickActions: "Quick Actions",
      createNewGoal: "Create New Health Goal", goalTitle: "Goal Title", goalPlaceholder: "e.g., Do 5 workouts per week",
      targetValue: "Target Value", unit: "Unit", glassesWater: "Glasses (water)", kg: "Kg", lbs: "Lbs",
      category: "Category", fitness: "Fitness", createGoal: "Create Goal",
      foodScanner: "Food Scanner", scanLogMeal: "Scan / Log Meal", snapPhoto: "Snap a photo to get instant nutrition breakdown",
      foodAnalysis: "Food Analysis", captureUploadFood: "Capture or Upload Food Image",
      addMedicine: "Add Medicine", medicineName: "Medicine Name", medicineNamePlaceholder: "e.g., Vitamin D",
      dosage: "Dosage", dosagePlaceholder: "e.g., 1000 IU", frequency: "Frequency", time: "Time",
      daily: "Daily", twiceDaily: "Twice Daily", weekly: "Weekly",
      describeMeal: "Describe your meal", mealPlaceholder: "e.g., Grilled chicken breast with brown rice and broccoli",
      analyzing: "Analyzing...", analyzeFood: "Analyze Food",
      healthAssistant: "Health Assistant", askHealthQuestion: "Ask Health Question",
      getPersonalizedAdvice: "Get personalized health advice based on your tracked data",
      medicines: "Medicines", noMedicinesAdded: "No medicines added yet", addMedicinesToGetReminders: "Add your daily medicines to get reminders",
      takenToday: "Taken", takeNow: "Take Now", logSymptoms: "Log Symptoms", symptomsLogged: "Your symptoms have been recorded",
      symptoms: "Symptoms", describeSymptomsPlaceholder: "Describe your symptoms...", severity: "Severity",
      mild: "Mild", moderate: "Moderate", severe: "Severe", notesOptional: "Notes (optional)", additionalNotesPlaceholder: "Any additional notes...",
      taken: "Taken",
      foodDeleted: "Food entry removed from your diary", waterIntake: "Water Intake", burned: "Burned", caloriesIntake: "Calories Intake",
      nutrition: "Nutrition", workoutsCount: "Workouts", mealsCount: "Meals", noHealthData: "No health data logged for this day",
      healthDataFor: "Health Data for", duration: "duration", minutes: "minutes",
      // Sleep tracker specific translations
      lastNight: "Last Night",
      quality: "Quality",
      bedtime: "Bedtime",
      wakeTime: "Wake",
      log: "Log",
      Good: "Good",
      good: "Good",
      Fair: "Fair",
      fair: "Fair",
      Poor: "Poor",
      poor: "Poor",
      Excellent: "Excellent",
      excellent: "Excellent",
      sleepStages: "Sleep Stages",
      // Calorie goals
      setCalorieGoal: "Set Calorie Goal", dailyCalorieTarget: "Daily Calorie Target",
      calorieGoalSet: "Calorie goal set successfully", trackAgainstFoodLogs: "Track against food logs",
      wantToTrackMeals: "Want to track today's meals?", remainingCalories: "Remaining",
      calorieProgress: "Calorie Progress", ofDailyGoal: "of daily goal", mealsLogged: "meals logged",
      updateGoal: "Update Goal", calorieRange: "Enter between 500-10,000 kcal",
      paymentHistory: "Payment History", viewTransactions: "View all your past transactions and subscription payments",
      noPaymentsYet: "No payments yet", paymentHistoryAppears: "Your payment history will appear here after your first transaction",
      // New keys for i18n
      loadingHealthMode: "Loading Health Mode...", subscription: "Subscription", pro: "Pro", freePlan: "Free Plan",
      aiAccess: "AI Access", unlimited: "Unlimited", credits: "credits", upgradeToPro: "Upgrade to Pro",
      takePhoto: "Take Photo", uploadPhoto: "Upload Photo", viewAIAnalysis: "View full AI analysis",
      healthAssistantTitle: "Your Personal Health Assistant", iHaveAccess: "I have access to your health data:",
      todaysActivity: "Today's activity (steps, calories, water)", recentWorkouts: "Recent workouts and exercises",
      currentMedicines: "Current medicines and dosages", recentMeals: "Recent meals and nutrition", loggedSymptoms: "Logged symptoms",
      todaysMeals: "Today's Meals", addToDiary: "Add to Diary", tryAgain: "Try Again"
    },
    womensHealth: {
      cycleDay: "Day", phase: "Phase", welcome: "Welcome", logPeriodToStart: "Log your period to start tracking",
      menstrual: "Menstrual", follicular: "Follicular", ovulation: "Ovulation", luteal: "Luteal",
      nextPeriodIn: "Next period in", days: "days", logPeriod: "Log Period", dailyLog: "Daily Log",
      todaysMood: "Today's Mood", symptomsLogged: "symptoms logged", today: "Today", calendar: "Calendar",
      insights: "Insights", settings: "Settings", articles: "Articles", tips: "Tips",
      pregnancyMode: "Pregnancy Mode", trackPregnancy: "Track your pregnancy journey",
      partnerAccess: "Partner Access", shareCycleInfo: "Share cycle info with partner", share: "Share",
      reminders: "Reminders", pillPeriodAlerts: "Pill, period & fertility alerts",
      week: "Week", trimester: "Trimester", dueDate: "Due", weeksToGo: "weeks to go",
      logSymptoms: "Log Symptoms", exitPregnancyMode: "Exit Pregnancy Mode", weekTips: "Tips",
      babyDevelopment: "Baby Development", selfCare: "Self Care",
      period: "Period", predicted: "Predicted", fertile: "Fertile",
      fertileWindow: "Fertile Window", ovulationDate: "Ovulation", nextPeriodPredicted: "Next Period Predicted",
      recentPeriods: "Recent Periods", noPeriodsLogged: "No periods logged yet", ongoing: "Ongoing", flow: "flow",
      cycleInsights: "Cycle Insights", averageCycle: "Average Cycle", averagePeriod: "Average Period", cyclesTracked: "Cycles Tracked",
      enablePregnancyMode: "Enable Pregnancy Mode", disablePregnancyMode: "Disable Pregnancy Mode?",
      enterDueDate: "Enter your estimated due date", switchToPregnancy: "This will switch to pregnancy tracking mode with week-by-week updates.",
      switchToPeriod: "This will switch back to regular period tracking.",
      shareWithPartner: "Share with Partner", generateShareLink: "Generate Share Link",
      partnerCanSee: "Generate a secure link to share your cycle information with your partner. They'll be able to see:",
      currentPhase: "Your current cycle phase", predictedDates: "Predicted period dates", fertileWindowOptional: "Fertile window (optional)",
      addReminder: "Add Reminder", reminderType: "Reminder Type", pillReminder: "Pill Reminder", periodReminder: "Period Reminder",
      fertilityWindow: "Fertility Window", custom: "Custom", time: "Time", labelOptional: "Label (Optional)",
      saveReminder: "Save Reminder", howAreYouFeeling: "How are you feeling?", energyLevel: "Energy Level",
      notes: "Notes", additionalNotes: "Any additional notes...", saveDailyLog: "Save Daily Log",
      startDate: "Start Date", endDate: "End Date", endDateOptional: "End Date (Optional)", flowIntensity: "Flow Intensity",
      light: "light", medium: "medium", heavy: "heavy", savePeriod: "Save Period", saving: "Saving...",
      great: "Great", good: "Good", okay: "Okay", low: "Low", bad: "Bad",
      physical: "Physical", discharge: "Discharge", mood: "Mood", sleepQuality: "Sleep", activity: "Activity", intimacy: "Intimacy",
      cramps: "Cramps", headache: "Headache", fatigue: "Fatigue", bloating: "Bloating", breastTenderness: "Breast Tenderness",
      acne: "Acne", backPain: "Back Pain", nausea: "Nausea", dizziness: "Dizziness",
      none: "None", creamy: "Creamy", eggWhite: "Egg White", watery: "Watery", sticky: "Sticky", spotting: "Spotting",
      happy: "Happy", calm: "Calm", energetic: "Energetic", moodSwings: "Mood Swings", irritable: "Irritable",
      anxious: "Anxious", sad: "Sad", sensitive: "Sensitive",
      goodSleep: "Good Sleep", insomnia: "Insomnia", restless: "Restless", oversleep: "Oversleep",
      vividDreams: "Vivid Dreams", nightSweats: "Night Sweats",
      exercise: "Exercise", yoga: "Yoga", walking: "Walking", running: "Running", swimming: "Swimming", restDay: "Rest Day",
      protected: "Protected", unprotected: "Unprotected", highDrive: "High Drive", lowDrive: "Low Drive",
      tipsFor: "Tips for", phaseTips: "Phase",
      dataPrivate: "Your data stays private and secure.",
      // Phase tips - menstrual
      tipMenstrualFood: "Foods to Eat During Your Period", tipMenstrualFoodDesc: "Iron-rich foods and anti-inflammatory options to ease cramps",
      tipMenstrualExercise: "Gentle Exercises for Period Days", tipMenstrualExerciseDesc: "Low-impact workouts that can help relieve discomfort",
      tipMenstrualSelfCare: "Self-Care Tips for Menstruation", tipMenstrualSelfCareDesc: "Rest, warmth, and comfort during your period",
      // Phase tips - follicular
      tipFollicularEnergy: "Energy Boost Activities", tipFollicularEnergyDesc: "Your energy is rising - great time for new projects",
      tipFollicularSkin: "Skin Care During Follicular Phase", tipFollicularSkinDesc: "Your skin may be clearer - time for exfoliation",
      tipFollicularSocial: "Social Activities to Try", tipFollicularSocialDesc: "Rising estrogen makes this a great social time",
      // Phase tips - ovulation
      tipOvulationFertility: "Peak Fertility Days", tipOvulationFertilityDesc: "Understanding your most fertile window",
      tipOvulationWorkout: "High Energy Workouts", tipOvulationWorkoutDesc: "Your body is at peak performance",
      tipOvulationCommunication: "Communication & Confidence", tipOvulationCommunicationDesc: "Verbal skills peak during ovulation",
      // Phase tips - luteal
      tipLutealPMS: "Managing PMS Symptoms", tipLutealPMSDesc: "Tips for the pre-menstrual phase",
      tipLutealFood: "Comfort Foods That Help", tipLutealFoodDesc: "Healthy comfort food alternatives",
      tipLutealStress: "Stress Management Techniques", tipLutealStressDesc: "Relaxation methods for the luteal phase"
    },
    life: {
      title: "Life Mode", bills: "Bills", expenses: "Expenses", appointments: "Appointments", budget: "Budget", goals: "Goals",
      addBill: "Add Bill", addExpense: "Add Expense", addAppointment: "Add Appointment", setBudget: "Set Budget", addGoal: "Add Goal",
      billName: "Bill Name", amount: "Amount", dueDate: "Due Date", category: "Category", recurring: "Recurring", paid: "Paid", unpaid: "Unpaid",
      financialDashboard: "Financial Dashboard", monthlyExpenses: "Monthly Expenses", upcomingBills: "Upcoming Bills",
      spendingTrends: "Spending Trends", savingsGoals: "Savings Goals", currentBalance: "Current Balance",
      noBills: "No bills added", noExpenses: "No expenses logged", noAppointments: "No appointments scheduled",
      smartFinancial: "Smart Financial", billsAndGoals: "Bills & Goals", calendar: "Calendar",
      smartFinancialDashboard: "Smart Financial Dashboard", aiPoweredInsights: "AI-powered insights to manage your finances",
      thisMonthSpending: "This Month's Spending", topSpendingCategory: "Top Spending Category", billStatus: "Bill Status",
      upcoming: "Upcoming", overdue: "Overdue", sixMonthTrend: "6-Month Spending Trend",
      totalExpenses: "TOTAL EXPENSES", transactions: "TRANSACTIONS", appointmentsCount: "APPOINTMENTS", billsDue: "BILLS DUE",
      monthlySpend: "Monthly spend", totalCount: "Total count", scheduled: "Scheduled", pending: "Pending",
      lifeCalendar: "Life Calendar", trackExpensesAppointments: "Track expenses, appointments, and bills",
      visualIndicators: "Visual indicators on each date",
      sun: "Su", mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa",
      vsLastMonth: "vs last month", loadingFinancialInsights: "Loading financial insights...",
      spendingByCategory: "Spending by Category", noSpendingData: "No spending data yet",
      budgetTracker: "Budget Tracker", used: "Used", setBudgetBtn: "Set Budget",
      totalMonthlyBudget: "Total Monthly Budget", categoryBudgets: "Category Budgets",
      saveBudget: "Save Budget", overall: "Overall", left: "left",
      // Budget goals translations
      setBudgetGoal: "Set Budget Goal", monthlySpendingLimit: "Monthly Spending Limit",
      budgetGoalSet: "Budget goal set successfully", trackAgainstExpenses: "Track against expenses and bills",
      budgetProgress: "Budget Progress", ofMonthlyBudget: "of monthly budget",
      passedBudgetGoal: "Looks like you passed your goal. Want to adjust next month?",
      overBudget: "Over Budget", underBudget: "Under Budget", onTrack: "On Track",
      categoryLimits: "Category Limits", food: "Food", travel: "Travel",
      shopping: "Shopping", billsCategory: "Bills", spentThisMonth: "Spent this month",
      daysLeftInMonth: "days left in month", noBudgetSet: "No budget set yet",
      setBudgetToTrack: "Set a monthly budget to track your spending",
      financialGoals: "Financial Goals", addNewGoal: "Add New Goal", goalName: "Goal Name",
      targetAmount: "Target Amount", currentAmount: "Current Amount", targetDate: "Target Date",
      goalCategory: "Category", createGoal: "Create Goal", noGoals: "No financial goals yet",
      setFirstGoal: "Set your first savings goal to start tracking",
      overdueBills: "Overdue Bills", due: "Due", addAppointmentTitle: "Add Appointment",
      type: "Type", date: "Date", time: "Time", location: "Location",
      utilities: "Utilities", groceries: "Groceries", transportation: "Transportation",
      entertainment: "Entertainment", healthcare: "Healthcare", other: "Other", savings: "Savings",
      categories: {
        utilities: "Utilities", groceries: "Groceries", transportation: "Transportation",
        entertainment: "Entertainment", healthcare: "Healthcare", other: "Other", savings: "Savings"
      },
      theseAreHere: "These are here when you're ready — no rush",
      allClear: "All clear!", helpRemember: "I'll help you remember when bills come up",
      noAppointmentsShort: "No appointments", subscription: "Subscription", pro: "Pro", freePlan: "Free Plan",
      aiAccess: "AI Access", unlimited: "Unlimited", credits: "credits",
      smartFinancialDashboardTitle: "💰 Smart Financial Dashboard",
      expensesMoneySpent: "Money spent", appointmentsScheduled: "Scheduled events", billsPaymentReminders: "Payment reminders",
      clickDateToView: "Click on a date to view expenses, appointments, and bills",
      expensesFor: "Expenses for", totalSpent: "Total Spent", categoryBreakdown: "Category Breakdown",
      allTransactions: "All Transactions", noExpensesRecorded: "No expenses recorded for this day",
      startTrackingSpending: "Start tracking to see your spending patterns",
      hasData: "Has Data", noData: "No Data", protein: "Protein", carbs: "Carbs", fat: "Fat"
    },
    notifications: {
      title: "Notification Settings", active: "Notifications Active",
      receiveReminders: "You'll receive reminders even when app is closed",
      disable: "Disable", enable: "Enable", enableNotifications: "Enable Notifications",
      customize: "Customize Your Notifications",
      morningGreeting: "Morning Greeting", morningDesc: "Daily briefing at 8 AM with tasks & appointments",
      eveningSummary: "Evening Summary", eveningDesc: "Daily wrap-up at 8 PM with accomplishments",
      waterReminders: "Water Reminders", waterDesc: "Stay hydrated with regular reminders",
      medicineReminders: "Medicine Reminders", medicineDesc: "Never miss your medication",
      appointmentAlerts: "Appointment Alerts", appointmentDesc: "Reminders before meetings & appointments",
      motivationBoosts: "Motivation Boosts", motivationDesc: "Afternoon pick-me-ups to keep you going",
      taskReminders: "Task Reminders", taskDesc: "Get notified about upcoming deadlines",
      workoutReminders: "Workout Reminders", workoutDesc: "Stay on track with your fitness goals",
      waterReminderInterval: "Water Reminder Interval",
      everyHour: "Every 1 hour", every2Hours: "Every 2 hours", every3Hours: "Every 3 hours", every4Hours: "Every 4 hours",
      savePreferences: "Save Preferences", blocked: "Notifications Blocked",
      enableInBrowser: "Please enable notifications in your browser settings",
      notSupported: "Push notifications not supported",
      testNotification: "Test Notification",
      stayOnTrack: "Stay on track with reminders",
      remindYouAbout: "I can remind you about water, medicine, bills, and tasks — but only if you allow notifications.",
      gentleReminders: "You'll receive gentle reminders for:",
      enabling: "Enabling...",
      maybeLater: "Maybe later",
      changeAnytime: "You can change this anytime in Settings. No spam, I promise.",
      enableSmartNotifications: "Enable Smart Notifications",
      getPersonalizedReminders: "Get personalized reminders even when the app is closed:",
      morningGreetings: "Morning greetings & daily briefing",
      hydrationReminders: "Hydration reminders every 2 hours",
      meetingAlerts: "Meeting & appointment alerts",
      eveningSummaries: "Evening summaries"
    },
    travel: {
      detected: "🌍 New Location Detected!",
      movedFrom: "Looks like you moved from",
      to: "to",
      updateLocation: "Update Location",
      gotIt: "Got it",
      timezoneUpdated: "Timezone updated automatically"
    },
    chat: {
      title: "Your Space", placeholder: "What's on your mind?", send: "Send", thinking: "Thinking...",
      newConversation: "New Conversation", history: "History", clearHistory: "Clear History",
      voiceInput: "Voice Input", attachFile: "Attach File"
    },
    locale: {
      detected: "We've set your language to",
      currency: "and currency to",
      changeAnytime: "You can change this anytime in Settings."
    }
  },
  ko: {
    greeting: { morning: "좋은 아침이에요", afternoon: "안녕하세요", evening: "좋은 저녁이에요", night: "좋은 밤 되세요" },
    subscription: { cta: "무료 체험 시작", upgrade: "Pro로 업그레이드", monthly: "월간", yearly: "연간", billedMonthly: "매월 청구", billedAnnually: "연간 청구", currentPlan: "현재 플랜", freeTrialActive: "무료 체험 중", trialDaysLeft: "일 남음", choosePlan: "플랜 선택", perMonth: "/월", perYear: "/년" },
    auth: {
      tagline: "어제의 감정을 기억하고",
      tagline2: "오늘을 더 잘 살도록 도와드려요.",
      welcomeBack: "돌아오셨군요",
      goodToSeeYou: "다시 만나서 반가워요",
      email: "이메일",
      password: "비밀번호",
      forgotPassword: "비밀번호를 잊으셨나요?",
      signingIn: "로그인 중...",
      signIn: "로그인",
      newToApp: "처음이신가요?",
      createAccount: "계정 만들기"
    },
    actions: { save: "저장", cancel: "취소", delete: "삭제", done: "완료", add: "추가", edit: "편집", update: "업데이트", create: "만들기", remove: "제거", confirm: "확인", search: "검색", filter: "필터", sort: "정렬", refresh: "새로고침", upload: "업로드", download: "다운로드", export: "내보내기", import: "가져오기", send: "보내기", connect: "연결", disconnect: "연결 해제", sync: "동기화", signOut: "로그아웃" },
    settings: { 
      profile: "프로필", integrations: "연동", subscription: "구독", support: "지원", memory: "메모리", language: "언어 및 지역", theme: "테마",
      gender: "성별", memberSince: "가입일", status: "상태", active: "활성", plan: "플랜",
      calendarIntegrations: "캘린더 연동", connectCalendars: "캘린더를 연결하여 자동으로 동기화하세요",
      googleCalendar: "구글 캘린더", appleCalendar: "애플 캘린더", syncEvents: "일정 및 약속 동기화",
      connected: "연결됨", notConnected: "연결 안됨",
      contactSupport: "고객 지원", sendMessage: "메시지를 보내주시면 도와드리겠습니다",
      requestFeature: "기능 요청", shareIdeas: "아이디어를 공유해 주세요",
      whatAlloraRemembers: "Allora가 기억하는 것", appTheme: "앱 테마",
      appleIdHint: "Apple ID 설정에서 앱 전용 비밀번호를 생성하세요",
      // Goals Settings
      goals: "목표", personalGoals: "개인 목표", personalGoalsDesc: "일일 및 월간 목표를 설정하여 진행 상황을 추적하세요",
      todaysProgress: "오늘의 진행", calorieGoal: "일일 칼로리 목표", calorieGoalDesc: "일일 칼로리 섭취량 추적",
      budgetGoal: "월간 예산 목표", budgetGoalDesc: "월간 지출 한도 설정",
      waterGoal: "일일 물 목표", waterGoalDesc: "하루 종일 수분을 유지하세요",
      stepsGoal: "일일 걸음 수 목표", stepsGoalDesc: "일일 활동량 추적",
      sleepGoal: "일일 수면 목표", sleepGoalDesc: "목표 수면 시간 설정",
      dailyTarget: "일일 목표", monthlyLimit: "월간 한도", targetHours: "목표 시간",
      recommended: "권장", glasses: "잔", saveGoals: "목표 저장",
      budgetTip: "라이프 모드의 지출이 이 목표에 대해 추적됩니다"
    },
    nav: { home: "홈", work: "업무", health: "건강", life: "생활", chat: "채팅", settings: "설정" },
    cards: { 
      chatDesc: "듣고 있어요.", 
      workDesc: "오늘 함께 정리해요.", 
      healthDesc: "당신의 에너지가 중요해요.", 
      lifeDesc: "함께 해결해요.",
      talkToAllora: "Allora와 대화하기"
    },
    home: {
      todayProgress: "오늘의 진행", hydration: "수분 섭취", nutrition: "영양", tasks: "할 일", points: "포인트",
      badges: "배지", viewAll: "전체 보기", noBadges: "아직 배지가 없어요", keepStreak: "꾸준히 하면 배지를 받을 수 있어요!",
      achievements: "업적", allCaughtUp: "모든 일정을 완료했어요!", enjoyMorning: "좋은 아침 되세요",
      morningBriefing: "오늘의 브리핑", whatsOnPlate: "오늘 해야 할 일이에요",
      suggestionForYou: "오늘의 제안",
      smallStepsCount: "작은 걸음도 중요해요. 함께 할게요.",
      todaysPulse: "오늘의 상태", energy: "에너지", mood: "기분", stress: "스트레스", focus: "집중",
      balancedAndReady: "균형 잡힌 상태", moderate: "보통", stable: "안정", low: "낮음", peak: "최고", high: "높음", ok: "양호",
      quickActions: "빠른 작업", mealPlanner: "식단 플래너", planYourMeals: "식단 계획하기",
      workouts: "운동", exerciseLibrary: "운동 라이브러리", medicines: "약", checkInteractions: "상호작용 확인",
      logWater: "물 기록", trackHydration: "수분 섭취 추적", goalsAndChallenges: "목표와 도전",
      activeChallenges: "진행 중인 도전", newChallenge: "새 도전", dashboard: "대시보드",
      sleep: "수면", history: "기록", water: "물", calories: "칼로리",
      talkToAllora: "Allora와 대화하기", youDontHaveToCarry: "혼자 짊어지지 않아도 돼요.",
      oneThingAtATime: "하나씩 하나씩. 가장 중요한 건 뭐예요?",
      showTodaysProgress: "오늘의 진행 보기", hideTodaysProgress: "오늘의 진행 숨기기",
      eveningReflection: "저녁 회고", howWasYourDay: "오늘 하루 어땠어요? 탭해서 체크인하세요.",
      great: "최고", good: "좋음", okay: "괜찮음", tough: "힘들었어요", maybeLater: "나중에",
      heyName: "안녕 {name}. 내가 여기 있어.", goodMorning: "좋은 아침이에요", morning: "아침", evening: "저녁",
      lateNight: "늦은 밤", readyWhenYouAre: "준비되면 언제든.", howAreYou: "오늘 어때요?",
      windingDown: "쉬는 중이에요? 아니면 아직 열심히?", imHere: "여기 있어요. 말해줘요.",
      imHereToHelp: "부담주려는 게 아니에요. 도와주려고 왔어요.", voice: "음성",
      tasksToComplete: "완료할 할 일", billsDueSoon: "곧 마감 청구서", appointmentsToday: "오늘 일정",
      rememberWater: "물 마시는 거 잊지 마세요", bestFocus: "최고 집중 시간",
      // Emotional nudge messages
      cantSleep: "잠이 안 와요? 여기 있어요. 판단 없이, 알림 없이.",
      letsTakeCare: "오늘은 당신을 돌봐요. 뭐가 필요해요?",
      howAreYouDoing: "지금까지 어때요? 필요하면 여기 있어요.",
      afternoonCheckin: "오후 체크인: 제가 도와드릴 일 있어요?",
      windingDownRest: "쉬고 있어요? 오늘 충분히 했어요. 쉬는 것도 생산적이에요.",
      imHereNoRush: "언제든 여기 있어요. 서두르지 않아도 돼요.",
      failedLogMood: "기분 기록에 실패했어요. 다시 시도해주세요.",
      takeYourTime: "천천히 해요. 준비되면 여기 있을게요.",
      tapToContinue: "계속하려면 탭하세요",
      alloraRemembers: "Allora가 기억해요...",
      badgesEarned: "획득한 배지",
      collector: "수집가"
    },
    work: {
      title: "업무 모드", tasks: "할 일", documents: "문서", addTask: "할 일 추가", newTask: "새 할 일", editTask: "할 일 편집",
      taskTitle: "제목", taskDescription: "설명", priority: "우선순위", dueDate: "마감일", status: "상태",
      high: "높음", medium: "보통", low: "낮음", urgent: "긴급", pending: "대기중", inProgress: "진행중", completed: "완료",
      addDocument: "문서 추가", uploadDocument: "문서 업로드", analyzeDocument: "문서 분석", documentSummary: "문서 요약",
      askQuestion: "이 문서에 대해 질문하기", todaysFocus: "오늘의 집중", productivityScore: "생산성 점수",
      tasksCompleted: "완료한 할 일", noTasks: "할 일이 없어요", noDocuments: "문서가 없어요", createFirstTask: "첫 번째 할 일을 만들어보세요",
      taskTemplates: "작업 템플릿", batchActions: "일괄 작업", aiDocumentAnalysis: "AI 문서 분석",
      documentLibrary: "문서 라이브러리",
      noItemsSelected: "선택된 항목 없음", pleaseSelectItems: "먼저 항목을 선택해주세요",
      batchOperationComplete: "일괄 작업 완료", appliedToItems: "적용됨", items: "항목",
      unableToBatch: "일괄 작업을 완료할 수 없습니다", batchOperations: "일괄 작업",
      selected: "선택됨", markComplete: "완료 표시", archive: "보관", addTag: "태그 추가",
      quickTaskTemplates: "빠른 작업 템플릿", loadingTemplates: "템플릿 로딩 중...",
      chooseTemplate: "템플릿을 선택하여 관련 작업을 즉시 생성하세요:",
      templateApplied: "템플릿 적용됨!", createdTasksFrom: "생성됨", tasksFromTemplate: "작업:",
      templateFailed: "템플릿 적용 실패", unableToCreateTasks: "작업을 생성할 수 없습니다. 다시 시도해주세요.",
      tasksCount: "작업", useTemplate: "템플릿 사용"
    },
    health: {
      title: "건강 모드", water: "물", food: "음식", exercise: "운동", sleep: "수면", medications: "약", vitals: "활력징후",
      addWater: "물 추가", logFood: "음식 기록", logExercise: "운동 기록", logSleep: "수면 기록", addMedication: "약 추가", logWorkout: "운동 기록", addFood: "음식 추가",
      glasses: "잔", calories: "칼로리", steps: "걸음", hours: "시간", goal: "목표", progress: "진행",
      waterGoal: "물 목표", calorieGoal: "칼로리 목표", stepsGoal: "걸음 목표", sleepGoal: "수면 목표",
      morningRoutine: "아침 루틴", eveningRoutine: "저녁 루틴", medicationReminder: "약 복용 알림",
      weight: "체중", bloodPressure: "혈압", heartRate: "심박수", bloodSugar: "혈당",
      noMedications: "추적중인 약이 없어요", noWorkouts: "오늘 기록된 운동이 없어요",
      nutritionTracker: "영양 추적기", protein: "단백질", carbs: "탄수화물", fats: "지방", fiber: "식이섬유", sugar: "당류",
      ofGoal: "목표량 중", limit: "제한", womensHealth: "여성 건강",
      startYourFirstChallenge: "첫 번째 도전 시작하기", healthGoals: "건강 목표", newGoal: "새 목표",
      noGoalsYet: "아직 목표가 없어요", clickNewGoal: "'새 목표'를 클릭해서 시작하세요", noActiveChallenges: "진행 중인 도전이 없어요", quickActions: "빠른 작업",
      createNewGoal: "새 건강 목표 만들기", goalTitle: "목표 제목", goalPlaceholder: "예: 주 5회 운동하기",
      targetValue: "목표 수치", unit: "단위", glassesWater: "잔 (물)", kg: "Kg", lbs: "Lbs",
      category: "카테고리", fitness: "피트니스", createGoal: "목표 만들기",
      foodScanner: "음식 스캐너", scanLogMeal: "스캔 / 식사 기록", snapPhoto: "사진을 찍어 즉시 영양 정보를 확인하세요",
      foodAnalysis: "음식 분석", captureUploadFood: "음식 사진 촬영 또는 업로드",
      addMedicine: "약 추가", medicineName: "약 이름", medicineNamePlaceholder: "예: 비타민 D",
      dosage: "복용량", dosagePlaceholder: "예: 1000 IU", frequency: "복용 빈도", time: "시간",
      daily: "매일", twiceDaily: "하루 두 번", weekly: "주 1회",
      describeMeal: "식사 설명", mealPlaceholder: "예: 닭가슴살 구이와 현미밥, 브로콜리",
      analyzing: "분석 중...", analyzeFood: "음식 분석",
      healthAssistant: "건강 도우미", askHealthQuestion: "건강 질문하기",
      getPersonalizedAdvice: "추적된 데이터를 기반으로 맞춤형 건강 조언을 받으세요",
      medicines: "약", noMedicinesAdded: "등록된 약이 없어요", addMedicinesToGetReminders: "약을 추가해서 복용 알림을 받으세요",
      takenToday: "복용 완료", takeNow: "지금 복용", logSymptoms: "증상 기록", symptomsLogged: "증상이 기록되었습니다",
      minutes: "분"
    },
    life: {
      title: "생활 모드", bills: "청구서", expenses: "지출", appointments: "일정", budget: "예산", goals: "목표",
      addBill: "청구서 추가", addExpense: "지출 추가", addAppointment: "일정 추가", setBudget: "예산 설정", addGoal: "목표 추가",
      billName: "청구서 이름", amount: "금액", dueDate: "마감일", category: "카테고리", recurring: "반복", paid: "결제완료", unpaid: "미결제",
      financialDashboard: "재정 대시보드", monthlyExpenses: "월간 지출", upcomingBills: "예정된 청구서",
      spendingTrends: "지출 추세", savingsGoals: "저축 목표", currentBalance: "현재 잔액",
      noBills: "청구서가 없어요", noExpenses: "기록된 지출이 없어요", noAppointments: "예정된 일정이 없어요",
      smartFinancial: "스마트 재정", billsAndGoals: "청구서 및 목표", calendar: "캘린더",
      smartFinancialDashboard: "스마트 재정 대시보드", aiPoweredInsights: "AI 기반 재정 관리 인사이트",
      thisMonthSpending: "이번 달 지출", topSpendingCategory: "최다 지출 카테고리", billStatus: "청구서 상태",
      upcoming: "예정된", overdue: "연체", sixMonthTrend: "6개월 지출 추세",
      totalExpenses: "총 지출", transactions: "거래", appointmentsCount: "일정", billsDue: "마감 청구서",
      monthlySpend: "월간 지출", totalCount: "총 개수", scheduled: "예정됨", pending: "대기 중",
      lifeCalendar: "생활 캘린더", trackExpensesAppointments: "지출, 일정, 청구서 추적",
      visualIndicators: "각 날짜의 시각적 표시",
      vsLastMonth: "지난달 대비", loadingFinancialInsights: "재정 인사이트 로딩 중...",
      spendingByCategory: "카테고리별 지출", noSpendingData: "지출 데이터가 없어요",
      budgetTracker: "예산 추적", used: "사용됨", setBudgetBtn: "예산 설정", left: "남음",
      totalMonthlyBudget: "월 총 예산", categoryBudgets: "카테고리별 예산",
      saveBudget: "예산 저장", overall: "전체",
      financialGoals: "재정 목표", addNewGoal: "새 목표 추가", goalName: "목표 이름",
      targetAmount: "목표 금액", currentAmount: "현재 금액", targetDate: "목표 날짜",
      goalCategory: "카테고리", createGoal: "목표 생성", noGoals: "재정 목표가 없어요",
      setFirstGoal: "첫 번째 저축 목표를 설정하세요",
      overdueBills: "연체 청구서", due: "마감", addAppointmentTitle: "일정 추가",
      type: "유형", date: "날짜", time: "시간", location: "장소",
      utilities: "공과금", groceries: "식료품", transportation: "교통비",
      entertainment: "오락", healthcare: "의료", other: "기타", savings: "저축",
      categories: {
        utilities: "공과금", groceries: "식료품", transportation: "교통비",
        entertainment: "오락", healthcare: "의료", other: "기타", savings: "저축"
      },
      theseAreHere: "준비되면 여기 있어요 — 서두르지 않아도 돼요",
      allClear: "모두 완료! 🎉", helpRemember: "청구서가 있을 때 알려드릴게요",
      noAppointmentsShort: "일정 없음", subscription: "구독", pro: "프로", freePlan: "무료 플랜",
      aiAccess: "AI 접근", unlimited: "무제한", credits: "크레딧",
      smartFinancialDashboardTitle: "💰 스마트 재정 대시보드",
      expensesMoneySpent: "지출 금액", appointmentsScheduled: "예정된 일정", billsPaymentReminders: "결제 알림",
      clickDateToView: "날짜를 클릭하여 지출, 일정, 청구서 확인",
      expensesFor: "지출 내역", totalSpent: "총 지출", categoryBreakdown: "카테고리 분류",
      allTransactions: "모든 거래", noExpensesRecorded: "이 날의 지출 기록이 없어요",
      startTrackingSpending: "추적을 시작하여 지출 패턴을 확인하세요",
      hasData: "데이터 있음", noData: "데이터 없음", protein: "단백질", carbs: "탄수화물", fat: "지방"
    },
    chat: {
      title: "나만의 공간", placeholder: "무엇이 궁금하세요?", send: "보내기", thinking: "생각중...",
      newConversation: "새 대화", history: "대화 기록", clearHistory: "기록 지우기",
      voiceInput: "음성 입력", attachFile: "파일 첨부"
    },
    common: { loading: "로딩 중...", error: "문제가 발생했어요", retry: "다시 시도", close: "닫기", back: "뒤로", next: "다음", done: "완료", pending: "대기중", completed: "완료", glasses: "잔", cal: "칼로리", today: "오늘", yesterday: "어제", thisWeek: "이번 주", thisMonth: "이번 달", all: "전체", none: "없음", total: "합계", average: "평균", creating: "생성 중...", add: "추가", save: "절약",
      youSaid: "말씀하신 내용:", processingCommand: "명령 처리 중...", exampleCommands: "예시 명령어:", action: "작업"
    },
    notifications: {
      title: "알림 설정", active: "알림 활성화됨",
      receiveReminders: "앱이 닫혀 있어도 알림을 받을 수 있어요",
      disable: "끄기", enable: "켜기", enableNotifications: "알림 켜기",
      customize: "알림 설정",
      morningGreeting: "아침 인사", morningDesc: "오전 8시에 오늘의 일정과 할 일 안내",
      eveningSummary: "저녁 요약", eveningDesc: "오후 8시에 오늘의 성과 정리",
      waterReminders: "물 마시기 알림", waterDesc: "정기적으로 수분 섭취를 알려드려요",
      medicineReminders: "약 복용 알림", medicineDesc: "약 복용을 놓치지 마세요",
      appointmentAlerts: "일정 알림", appointmentDesc: "회의와 약속 전에 알려드려요",
      motivationBoosts: "격려 메시지", motivationDesc: "오후에 활력을 불어넣어 드려요",
      taskReminders: "할 일 알림", taskDesc: "마감일이 다가오면 알려드려요",
      workoutReminders: "운동 알림", workoutDesc: "운동 목표를 달성할 수 있게 도와드려요",
      waterReminderInterval: "물 알림 간격",
      everyHour: "1시간마다", every2Hours: "2시간마다", every3Hours: "3시간마다", every4Hours: "4시간마다",
      savePreferences: "설정 저장", blocked: "알림 차단됨",
      enableInBrowser: "브라우저 설정에서 알림을 허용해주세요",
      notSupported: "푸시 알림이 지원되지 않아요",
      testNotification: "테스트 알림",
      stayOnTrack: "알림으로 일정을 관리하세요",
      remindYouAbout: "물, 약, 청구서, 할 일을 알려드릴 수 있어요 — 알림을 허용해주세요.",
      gentleReminders: "다음 항목에 대한 알림을 받을 수 있어요:",
      enabling: "설정 중...",
      maybeLater: "나중에 할게요",
      changeAnytime: "언제든지 설정에서 변경할 수 있어요. 스팸 없어요, 약속해요.",
      enableSmartNotifications: "스마트 알림 활성화",
      getPersonalizedReminders: "앱이 닫혀 있어도 맞춤 알림을 받으세요:",
      morningGreetings: "아침 인사 & 일일 브리핑",
      hydrationReminders: "2시간마다 수분 섭취 알림",
      meetingAlerts: "회의 & 일정 알림",
      eveningSummaries: "저녁 요약"
    },
    travel: {
      detected: "🌍 새로운 위치가 감지되었어요!",
      movedFrom: "이동한 것 같아요",
      to: "에서",
      updateLocation: "위치 업데이트",
      gotIt: "확인",
      timezoneUpdated: "시간대가 자동으로 업데이트되었어요"
    },
    locale: { detected: "언어가", currency: "통화가", changeAnytime: "설정에서 언제든지 변경할 수 있습니다." }
  },
  ja: {
    greeting: { morning: "おはようございます", afternoon: "こんにちは", evening: "こんばんは", night: "おやすみなさい" },
    auth: {
      tagline: "昨日の気持ちを覚えています",
      tagline2: "今日をより良く過ごすお手伝いをします。",
      welcomeBack: "おかえりなさい",
      goodToSeeYou: "また会えて嬉しいです",
      email: "メール",
      password: "パスワード",
      forgotPassword: "パスワードをお忘れですか？",
      signingIn: "ログイン中...",
      signIn: "ログイン",
      newToApp: "初めての方？",
      createAccount: "アカウント作成"
    },
    subscription: { cta: "無料トライアル開始", upgrade: "Proにアップグレード", monthly: "月額", yearly: "年額", billedMonthly: "毎月請求", billedAnnually: "年間請求", currentPlan: "現在のプラン", freeTrialActive: "無料トライアル中", trialDaysLeft: "日残り", choosePlan: "プランを選択", perMonth: "/月", perYear: "/年",
      lastDayTrial: "トライアル最終日", trialEndsToday: "トライアル終了日", daysLeftTrial: "日残り", then: "その後", mo: "月"
    },
    nudge: {
      iNoticed: "気づいたことがあります...",
      suggestedAction: "おすすめのアクション：",
      wantMeToHelp: "お手伝いしましょうか？",
      quickHydrationCheck: "水分チェック",
      hadGlassesToday: "今日は{count}杯飲みました。水分補給はいかがですか？",
      tryShortBursts: "短時間から始めてみましょう"
    },
    actions: { save: "保存", cancel: "キャンセル", delete: "削除", done: "完了", add: "追加", edit: "編集", update: "更新", create: "作成", remove: "削除", confirm: "確認", search: "検索", filter: "フィルター", sort: "並べ替え", refresh: "更新", upload: "アップロード", download: "ダウンロード", export: "エクスポート", import: "インポート", send: "送信", connect: "接続", disconnect: "切断", sync: "同期", signOut: "サインアウト" },
    settings: { 
      profile: "プロフィール", integrations: "連携", subscription: "サブスク", support: "サポート", memory: "メモリ", language: "言語と地域", theme: "テーマ",
      gender: "性別", memberSince: "登録日", status: "ステータス", active: "アクティブ", plan: "プラン",
      calendarIntegrations: "カレンダー連携", connectCalendars: "カレンダーを接続して自動同期",
      googleCalendar: "Googleカレンダー", appleCalendar: "Appleカレンダー", syncEvents: "予定と約束を同期",
      connected: "接続済み", notConnected: "未接続",
      contactSupport: "サポート連絡", sendMessage: "メッセージを送信してください",
      requestFeature: "機能リクエスト", shareIdeas: "アイデアを共有してください",
      whatAlloraRemembers: "Alloraが覚えていること", appTheme: "アプリテーマ",
      appleIdHint: "Apple ID設定でアプリ専用パスワードを作成",
      editProfile: "プロフィール編集", manageSubscription: "サブスク管理", paymentHistory: "支払い履歴", logOut: "ログアウト",
      notifications: "通知",
      chooseLanguageRegion: "パーソナライズされた体験のために言語と地域を選択してください",
      currentSettings: "現在の設定", detectedLocation: "検出された場所と設定",
      country: "国", currency: "通貨", timezone: "タイムゾーン",
      chooseLanguage: "お好みの言語を選択してください", updateProfile: "プロフィール情報を更新",
      // Goals Settings
      goals: "目標", personalGoals: "個人目標", personalGoalsDesc: "日々と月次の目標を設定して進捗を追跡",
      todaysProgress: "今日の進捗", calorieGoal: "1日のカロリー目標", calorieGoalDesc: "毎日のカロリー摂取量を追跡",
      budgetGoal: "月の予算目標", budgetGoalDesc: "月の支出上限を設定",
      waterGoal: "1日の水分目標", waterGoalDesc: "1日を通して水分補給を",
      stepsGoal: "1日の歩数目標", stepsGoalDesc: "毎日の活動量を追跡",
      sleepGoal: "1日の睡眠目標", sleepGoalDesc: "目標睡眠時間を設定",
      dailyTarget: "1日の目標", monthlyLimit: "月の上限", targetHours: "目標時間",
      recommended: "推奨", glasses: "杯", saveGoals: "目標を保存",
      budgetTip: "ライフモードの支出がこの目標に対して追跡されます"
    },
    nav: { home: "ホーム", work: "仕事", health: "健康", life: "生活", chat: "チャット", settings: "設定" },
    cards: { 
      chatDesc: "聴いています。", 
      workDesc: "一緒に整理しましょう。", 
      healthDesc: "あなたのエネルギーが大切です。", 
      lifeDesc: "一緒に解決しましょう。",
      talkToAllora: "Alloraに話しかける"
    },
    home: {
      todayProgress: "今日の進捗", hydration: "水分補給", nutrition: "栄養", tasks: "タスク", points: "ポイント",
      badges: "バッジ", viewAll: "すべて見る", noBadges: "バッジはまだありません", keepStreak: "続けてバッジを獲得しよう！",
      achievements: "実績", allCaughtUp: "すべて完了！", enjoyMorning: "良い朝を",
      morningBriefing: "今日のブリーフィング", whatsOnPlate: "今日の予定",
      suggestionForYou: "今日の提案",
      smallStepsCount: "小さな一歩も大切。いつもそばにいます。",
      todaysPulse: "今日の調子", energy: "エネルギー", mood: "気分", stress: "ストレス", focus: "集中",
      balancedAndReady: "バランスが取れています", moderate: "普通", stable: "安定", low: "低い", peak: "ピーク", high: "高い", ok: "OK",
      quickActions: "クイックアクション", mealPlanner: "食事プランナー", planYourMeals: "食事を計画",
      workouts: "ワークアウト", exerciseLibrary: "エクササイズライブラリ", medicines: "薬", checkInteractions: "相互作用を確認",
      logWater: "水を記録", trackHydration: "水分補給を追跡", goalsAndChallenges: "目標とチャレンジ",
      activeChallenges: "アクティブなチャレンジ", newChallenge: "新しいチャレンジ", dashboard: "ダッシュボード",
      sleep: "睡眠", history: "履歴", water: "水", calories: "カロリー",
      talkToAllora: "Alloraに話しかける", youDontHaveToCarry: "一人で抱え込まなくていい。",
      oneThingAtATime: "一つずつ。今一番大切なことは？", 
      showTodaysProgress: "今日の進捗を表示", hideTodaysProgress: "今日の進捗を隠す",
      eveningReflection: "夜の振り返り", howWasYourDay: "今日はどうでしたか？タップしてチェックイン",
      great: "最高", good: "良い", okay: "まあまあ", tough: "大変だった", maybeLater: "また後で",
      heyName: "やあ、{name}。ここにいるよ。", goodMorning: "おはよう", morning: "おはよう", evening: "こんばんは",
      lateNight: "こんな遅くまで", readyWhenYouAre: "準備できたらいつでも。", howAreYou: "調子はどう？",
      windingDown: "リラックスタイム？それともまだ頑張る？", imHere: "ここにいるよ。話しかけて。",
      imHereToHelp: "無理させないよ。ここにいるから。", voice: "音声",
      tasksToComplete: "完了するタスク", billsDueSoon: "まもなく期限の請求書", appointmentsToday: "今日の予定",
      rememberWater: "水分補給を忘れずに", bestFocus: "最高の集中",
      // Emotional nudge messages
      cantSleep: "眠れない？ここにいるよ。判断も、リマインダーもなし。",
      letsTakeCare: "今日はあなたのケアをしよう。何が必要？",
      howAreYouDoing: "今のところどう？必要なら、ここにいるよ。",
      afternoonCheckin: "午後のチェックイン：何か手伝えることある？",
      windingDownRest: "リラックスしてる？今日は十分頑張ったよ。休むことも生産的。",
      imHereNoRush: "いつでもここにいるよ。急がなくていい。",
      failedLogMood: "気分の記録に失敗しました。もう一度お試しください。",
      takeYourTime: "ゆっくりでいいよ。準備ができたらここにいるね。",
      tapToContinue: "タップして続ける",
      alloraRemembers: "Alloraは覚えてる...",
      badgesEarned: "獲得バッジ",
      collector: "コレクター"
    },
    work: {
      title: "仕事モード", tasks: "タスク", documents: "文書", addTask: "タスク追加", newTask: "新しいタスク", editTask: "タスク編集",
      taskTitle: "タイトル", taskDescription: "説明", priority: "優先度", dueDate: "期限", status: "ステータス",
      high: "高", medium: "中", low: "低", urgent: "緊急", pending: "保留中", inProgress: "進行中", completed: "完了",
      addDocument: "文書追加", uploadDocument: "文書アップロード", analyzeDocument: "文書分析", documentSummary: "文書の要約",
      askQuestion: "この文書について質問する", todaysFocus: "今日のフォーカス", productivityScore: "生産性スコア",
      tasksCompleted: "完了したタスク", noTasks: "タスクはありません", noDocuments: "文書はありません", createFirstTask: "最初のタスクを作成",
      allCaughtUp: "すべて完了！", noUrgentTasks: "緊急タスクはありません。素晴らしい！",
      taskTemplates: "タスクテンプレート", batchActions: "一括操作", aiDocumentAnalysis: "AI文書分析",
      documentLibrary: "文書ライブラリ", uploadHint: "MP3/MP4録音、PDF文書をアップロード、またはテキストを貼り付けてAI分析",
      noItemsSelected: "選択されていません", pleaseSelectItems: "まずアイテムを選択してください",
      batchOperationComplete: "一括操作完了", appliedToItems: "に適用", items: "アイテム",
      unableToBatch: "一括操作を完了できませんでした", batchOperations: "一括操作",
      selected: "選択済み", markComplete: "完了にする", archive: "アーカイブ", addTag: "タグを追加",
      quickTaskTemplates: "クイックタスクテンプレート", loadingTemplates: "テンプレートを読み込み中...",
      chooseTemplate: "テンプレートを選んで関連タスクを即座に作成：",
      templateApplied: "テンプレート適用完了！", createdTasksFrom: "作成済み", tasksFromTemplate: "タスク：",
      templateFailed: "テンプレート適用失敗", unableToCreateTasks: "タスクを作成できませんでした。もう一度お試しください。",
      tasksCount: "タスク", useTemplate: "テンプレートを使用",
      dueToday: "今日期限", dueTomorrow: "明日期限", overdue: "期限切れ", estimatedTime: "予定時間",
      comingUp: "今後", next3Days: "今後3日間", dueIn: "残り", moreUpcomingTasks: "件の今後のタスク"
    },
    health: {
      title: "健康モード", water: "水", food: "食事", exercise: "運動", sleep: "睡眠", medications: "薬", vitals: "バイタル",
      addWater: "水を追加", logFood: "食事を記録", logExercise: "運動を記録", logSleep: "睡眠を記録", addMedication: "薬を追加", logWorkout: "運動を記録", addFood: "食事を追加",
      glasses: "杯", calories: "カロリー", steps: "歩", hours: "時間", goal: "目標", progress: "進捗",
      waterGoal: "水の目標", calorieGoal: "カロリー目標", stepsGoal: "歩数目標", sleepGoal: "睡眠目標",
      morningRoutine: "朝のルーティン", eveningRoutine: "夜のルーティン", medicationReminder: "薬のリマインダー",
      weight: "体重", bloodPressure: "血圧", heartRate: "心拍数", bloodSugar: "血糖値",
      noMedications: "追跡中の薬はありません", noWorkouts: "今日の運動記録はありません",
      avgWater: "平均水分", avgCalories: "平均カロリー", daysTracked: "追跡日数",
      keepMoving: "動き続けよう！", stayHydrated: "水分補給を！", burnIt: "燃やそう！", keepLogging: "記録を続けよう！",
      healthCalendar: "健康カレンダー", trackYourHealthJourney: "健康の旅を追跡",
      autoSleepTracking: "自動睡眠追跡有効", recentSleepLogs: "最近の睡眠記録",
      noSleepLogs: "睡眠記録がまだありません。追跡を始めましょう！", logYourFirstSleep: "最初の睡眠を記録",
      nutritionTracker: "栄養トラッカー", protein: "タンパク質", carbs: "炭水化物", fats: "脂質", fiber: "食物繊維", sugar: "糖質",
      ofGoal: "目標の", limit: "制限", womensHealth: "女性の健康",
      startYourFirstChallenge: "最初のチャレンジを始めよう", healthGoals: "健康目標", newGoal: "新しい目標",
      noGoalsYet: "まだ目標がありません", clickNewGoal: "「新しい目標」をクリックして始めましょう", noActiveChallenges: "進行中のチャレンジはありません", quickActions: "クイックアクション",
      foodScanner: "食品スキャナー", scanLogMeal: "スキャン / 食事記録", snapPhoto: "写真を撮って栄養情報を取得",
      healthAssistant: "健康アシスタント", askHealthQuestion: "健康について質問",
      getPersonalizedAdvice: "追跡データに基づいたパーソナライズされた健康アドバイス",
      medicines: "薬", noMedicinesAdded: "まだ薬が追加されていません", addMedicinesToGetReminders: "リマインダーを受けるには薬を追加してください",
      takenToday: "服用済み", takeNow: "今すぐ服用", logSymptoms: "症状を記録", symptomsLogged: "症状が記録されました",
      foodDeleted: "食事が削除されました", waterIntake: "水分摂取", burned: "消費", caloriesIntake: "摂取カロリー",
      nutrition: "栄養", workoutsCount: "運動回数", mealsCount: "食事", noHealthData: "この日の健康データはありません",
      healthDataFor: "健康データ", duration: "時間", minutes: "分",
      // Calorie goals
      setCalorieGoal: "カロリー目標を設定", dailyCalorieTarget: "1日のカロリー目標",
      calorieGoalSet: "カロリー目標を設定しました", trackAgainstFoodLogs: "食事記録と照合",
      wantToTrackMeals: "今日の食事を記録しますか？", remainingCalories: "残り",
      calorieProgress: "カロリー進捗", ofDailyGoal: "目標の", mealsLogged: "食事記録数",
      updateGoal: "目標を更新", calorieRange: "500〜10,000 kcalの間で入力",
      paymentHistory: "支払い履歴", viewTransactions: "過去の取引と定期購読の支払いを確認", 
      noPaymentsYet: "まだ支払いがありません", paymentHistoryAppears: "最初の取引後に支払い履歴がここに表示されます",
      addToDiary: "日記に追加", tryAgain: "もう一度"
    },
    womensHealth: {
      cycleDay: "日目", phase: "相", welcome: "ようこそ", logPeriodToStart: "追跡を開始するには生理を記録してください",
      menstrual: "月経期", follicular: "卵胞期", ovulation: "排卵期", luteal: "黄体期",
      nextPeriodIn: "次の生理まで", days: "日", logPeriod: "生理を記録", dailyLog: "毎日の記録",
      todaysMood: "今日の気分", symptomsLogged: "症状を記録", today: "今日", calendar: "カレンダー",
      insights: "インサイト", settings: "設定", articles: "記事", tips: "ヒント",
      pregnancyMode: "妊娠モード", trackPregnancy: "妊娠の経過を追跡",
      partnerAccess: "パートナーアクセス", shareCycleInfo: "パートナーと周期情報を共有", share: "共有",
      reminders: "リマインダー", pillPeriodAlerts: "ピル、生理、妊娠可能期間のアラート",
      week: "週", trimester: "トリメスター", dueDate: "予定日", weeksToGo: "週間後",
      logSymptoms: "症状を記録", exitPregnancyMode: "妊娠モードを終了", weekTips: "ヒント",
      babyDevelopment: "赤ちゃんの発育", selfCare: "セルフケア",
      period: "生理", predicted: "予測", fertile: "妊娠可能",
      fertileWindow: "妊娠可能期間", ovulationDate: "排卵日", nextPeriodPredicted: "次の生理予測",
      recentPeriods: "最近の生理", noPeriodsLogged: "生理が記録されていません", ongoing: "進行中", flow: "量",
      cycleInsights: "周期インサイト", averageCycle: "平均周期", averagePeriod: "平均生理期間", cyclesTracked: "追跡した周期",
      enablePregnancyMode: "妊娠モードを有効にする", disablePregnancyMode: "妊娠モードを無効にしますか？",
      enterDueDate: "予定日を入力してください", switchToPregnancy: "妊娠追跡モードに切り替えます。",
      switchToPeriod: "通常の生理追跡に戻ります。",
      shareWithPartner: "パートナーと共有", generateShareLink: "共有リンクを生成",
      partnerCanSee: "パートナーと周期情報を共有するための安全なリンクを生成します。表示できる内容：",
      currentPhase: "現在の周期相", predictedDates: "予測される生理日", fertileWindowOptional: "妊娠可能期間（オプション）",
      addReminder: "リマインダーを追加", reminderType: "リマインダーの種類", pillReminder: "ピルリマインダー", periodReminder: "生理リマインダー",
      fertilityWindow: "妊娠可能期間", custom: "カスタム", time: "時間", labelOptional: "ラベル（オプション）",
      saveReminder: "リマインダーを保存", howAreYouFeeling: "今日の気分は？", energyLevel: "エネルギーレベル",
      notes: "メモ", additionalNotes: "追加のメモ...", saveDailyLog: "記録を保存",
      startDate: "開始日", endDate: "終了日", endDateOptional: "終了日（オプション）", flowIntensity: "経血量",
      light: "軽い", medium: "普通", heavy: "多い", savePeriod: "生理を保存", saving: "保存中...",
      great: "最高", good: "良い", okay: "まあまあ", low: "低い", bad: "悪い",
      physical: "身体", discharge: "おりもの", mood: "気分", sleepQuality: "睡眠", activity: "活動", intimacy: "親密さ",
      cramps: "腹痛", headache: "頭痛", fatigue: "疲労", bloating: "むくみ", breastTenderness: "胸の張り",
      acne: "ニキビ", backPain: "腰痛", nausea: "吐き気", dizziness: "めまい",
      none: "なし", creamy: "クリーム状", eggWhite: "卵白状", watery: "水っぽい", sticky: "粘り気", spotting: "少量出血",
      happy: "幸せ", calm: "落ち着き", energetic: "元気", moodSwings: "気分の変動", irritable: "イライラ",
      anxious: "不安", sad: "悲しい", sensitive: "敏感",
      goodSleep: "良い睡眠", insomnia: "不眠", restless: "落ち着かない", oversleep: "寝すぎ",
      vividDreams: "鮮明な夢", nightSweats: "寝汗",
      exercise: "運動", yoga: "ヨガ", walking: "ウォーキング", running: "ランニング", swimming: "水泳", restDay: "休息日",
      protected: "避妊あり", unprotected: "避妊なし", highDrive: "性欲高い", lowDrive: "性欲低い",
      tipsFor: "のヒント", phaseTips: "相",
      dataPrivate: "データはプライベートで安全に保護されています 💜",
      // Phase tips - menstrual
      tipMenstrualFood: "生理中におすすめの食べ物", tipMenstrualFoodDesc: "鉄分豊富で抗炎症作用のある食品で痛みを和らげます",
      tipMenstrualExercise: "生理中の軽い運動", tipMenstrualExerciseDesc: "不快感を和らげる低負荷ワークアウト",
      tipMenstrualSelfCare: "生理中のセルフケア", tipMenstrualSelfCareDesc: "休息、温かさ、快適さが大切です",
      // Phase tips - follicular
      tipFollicularEnergy: "エネルギーアップ活動", tipFollicularEnergyDesc: "エネルギーが上昇中 - 新しいプロジェクトに最適な時期",
      tipFollicularSkin: "卵胞期のスキンケア", tipFollicularSkinDesc: "肌がクリアになる時期 - 角質ケアに最適",
      tipFollicularSocial: "おすすめの社交活動", tipFollicularSocialDesc: "エストロゲン上昇で社交的になる良い時期",
      // Phase tips - ovulation
      tipOvulationFertility: "妊娠可能日のピーク", tipOvulationFertilityDesc: "最も妊娠しやすい時期を理解する",
      tipOvulationWorkout: "高強度ワークアウト", tipOvulationWorkoutDesc: "体が最高のパフォーマンスを発揮する時期",
      tipOvulationCommunication: "コミュニケーションと自信", tipOvulationCommunicationDesc: "排卵期は言語能力がピークに達します",
      // Phase tips - luteal
      tipLutealPMS: "PMS症状の管理", tipLutealPMSDesc: "月経前の時期のためのヒント",
      tipLutealFood: "おすすめのコンフォートフード", tipLutealFoodDesc: "健康的なコンフォートフードの代替品",
      tipLutealStress: "ストレス管理テクニック", tipLutealStressDesc: "黄体期のリラクゼーション方法"
    },
    life: {
      title: "生活モード", bills: "請求書", expenses: "支出", appointments: "予定", budget: "予算", goals: "目標",
      addBill: "請求書追加", addExpense: "支出追加", addAppointment: "予定追加", setBudget: "予算設定", addGoal: "目標追加",
      billName: "請求書名", amount: "金額", dueDate: "期限", category: "カテゴリ", recurring: "定期", paid: "支払済", unpaid: "未払",
      financialDashboard: "財務ダッシュボード", monthlyExpenses: "月間支出", upcomingBills: "今後の請求書",
      spendingTrends: "支出傾向", savingsGoals: "貯蓄目標", currentBalance: "現在残高",
      noBills: "請求書はありません", noExpenses: "支出記録なし", noAppointments: "予定なし",
      smartFinancial: "スマート財務", billsAndGoals: "請求書と目標", calendar: "カレンダー",
      smartFinancialDashboard: "スマート財務ダッシュボード", aiPoweredInsights: "AIによる財務管理のインサイト",
      thisMonthSpending: "今月の支出", topSpendingCategory: "最多支出カテゴリ", billStatus: "請求書状況",
      upcoming: "今後", overdue: "期限超過", sixMonthTrend: "6ヶ月間の支出傾向",
      totalExpenses: "総支出", transactions: "取引数", appointmentsCount: "予定数", billsDue: "請求書期限",
      monthlySpend: "月間支出", totalCount: "合計", scheduled: "予定済み", pending: "保留中",
      lifeCalendar: "ライフカレンダー", trackExpensesAppointments: "支出、予定、請求書を追跡",
      visualIndicators: "各日付に視覚的インジケーター",
      sun: "日", mon: "月", tue: "火", wed: "水", thu: "木", fri: "金", sat: "土",
      vsLastMonth: "先月比", loadingFinancialInsights: "財務インサイトを読み込み中...",
      spendingByCategory: "カテゴリ別支出", noSpendingData: "支出データがありません",
      budgetTracker: "予算トラッカー", used: "使用済み", setBudgetBtn: "予算設定",
      totalMonthlyBudget: "月間予算総額", categoryBudgets: "カテゴリ別予算",
      saveBudget: "予算を保存", overall: "全体", left: "残り",
      // Budget goals translations
      setBudgetGoal: "予算目標を設定", monthlySpendingLimit: "月間支出上限",
      budgetGoalSet: "予算目標を設定しました", trackAgainstExpenses: "支出と請求書と照合",
      budgetProgress: "予算進捗", ofMonthlyBudget: "月間予算の",
      passedBudgetGoal: "予算を超えたようです。来月の目標を調整しますか？",
      overBudget: "予算超過", underBudget: "予算内", onTrack: "順調",
      categoryLimits: "カテゴリ上限", food: "食費", travel: "旅行",
      shopping: "買い物", billsCategory: "請求書", spentThisMonth: "今月の支出",
      daysLeftInMonth: "月末まであと", noBudgetSet: "予算がまだ設定されていません",
      setBudgetToTrack: "支出を追跡するには月間予算を設定してください",
      financialGoals: "財務目標", addNewGoal: "新しい目標を追加", goalName: "目標名",
      targetAmount: "目標金額", currentAmount: "現在の金額", targetDate: "目標日",
      goalCategory: "カテゴリ", createGoal: "目標を作成", noGoals: "財務目標がありません",
      setFirstGoal: "最初の貯蓄目標を設定して追跡を始めましょう",
      overdueBills: "期限超過の請求書", due: "期限", addAppointmentTitle: "予定を追加",
      type: "種類", date: "日付", time: "時間", location: "場所",
      utilities: "光熱費", groceries: "食料品", transportation: "交通費",
      entertainment: "娯楽", healthcare: "医療費", other: "その他", savings: "貯蓄",
      categories: {
        utilities: "光熱費", groceries: "食料品", transportation: "交通費",
        entertainment: "娯楽", healthcare: "医療費", other: "その他", savings: "貯蓄"
      },
      theseAreHere: "準備ができたらここにあるよ — 急がないでね",
      allClear: "すべて完了！🎉", helpRemember: "請求書があれば教えますね",
      noAppointmentsShort: "予定なし", subscription: "サブスクリプション", pro: "Pro", freePlan: "無料プラン",
      aiAccess: "AIアクセス", unlimited: "無制限", credits: "クレジット",
      smartFinancialDashboardTitle: "💰 スマート財務ダッシュボード",
      expensesMoneySpent: "支出金額", appointmentsScheduled: "予定されたイベント", billsPaymentReminders: "支払いリマインダー",
      clickDateToView: "日付をクリックして支出、予定、請求書を確認",
      expensesFor: "支出詳細", totalSpent: "総支出", categoryBreakdown: "カテゴリ内訳",
      allTransactions: "すべての取引", noExpensesRecorded: "この日の支出記録はありません",
      startTrackingSpending: "追跡を始めて支出パターンを確認",
      hasData: "データあり", noData: "データなし", protein: "タンパク質", carbs: "炭水化物", fat: "脂質"
    },
    notifications: {
      title: "通知設定", active: "通知有効",
      receiveReminders: "アプリを閉じていてもリマインダーを受け取れます",
      disable: "無効にする", enable: "有効にする", enableNotifications: "通知を有効にする",
      customize: "通知をカスタマイズ",
      morningGreeting: "朝のあいさつ", morningDesc: "朝8時にタスクと予定のブリーフィング",
      eveningSummary: "夜のまとめ", eveningDesc: "夜8時に今日の成果をまとめ",
      waterReminders: "水分補給リマインダー", waterDesc: "定期的な水分補給のリマインダー",
      medicineReminders: "薬のリマインダー", medicineDesc: "薬を飲み忘れない",
      appointmentAlerts: "予定アラート", appointmentDesc: "会議や予定の前にリマインダー",
      motivationBoosts: "モチベーションブースト", motivationDesc: "午後の元気づけメッセージ",
      taskReminders: "タスクリマインダー", taskDesc: "締め切りの通知を受け取る",
      workoutReminders: "ワークアウトリマインダー", workoutDesc: "フィットネス目標を達成しよう",
      waterReminderInterval: "水分補給リマインダーの間隔",
      everyHour: "1時間ごと", every2Hours: "2時間ごと", every3Hours: "3時間ごと", every4Hours: "4時間ごと",
      savePreferences: "設定を保存", blocked: "通知がブロックされています",
      enableInBrowser: "ブラウザの設定で通知を有効にしてください",
      notSupported: "プッシュ通知はサポートされていません",
      testNotification: "テスト通知",
      maybeLater: "また今度",
      enabling: "有効化中...",
      enableSmartNotifications: "スマート通知を有効にする",
      getPersonalizedReminders: "アプリを閉じていてもパーソナライズされたリマインダーを受け取る:",
      hydrationReminders: "2時間ごとの水分補給リマインダー",
      meetingAlerts: "会議・予定のアラート",
      eveningSummaries: "夕方のまとめ",
      motivationalBoosts: "モチベーションブースト"
    },
    chat: {
      title: "あなたのスペース", placeholder: "何か気になることは？", send: "送信", thinking: "考え中...",
      newConversation: "新しい会話", history: "履歴", clearHistory: "履歴を消去",
      voiceInput: "音声入力", attachFile: "ファイルを添付"
    },
    common: { loading: "読み込み中...", error: "エラーが発生しました", retry: "再試行", close: "閉じる", back: "戻る", next: "次へ", done: "完了", pending: "未完了", completed: "完了", glasses: "杯", cal: "カロリー", today: "今日", yesterday: "昨日", thisWeek: "今週", thisMonth: "今月", all: "すべて", none: "なし", total: "合計", average: "平均", add: "追加", save: "保存",
      youSaid: "あなたの発言:", processingCommand: "コマンド処理中...", exampleCommands: "コマンド例:", action: "アクション",
      yes: "はい", no: "いいえ", skip: "スキップ", adjust: "調整", day: "日", days: "日"
    },
    locale: { detected: "言語を", currency: "通貨を", changeAnytime: "設定からいつでも変更できます。" }
  },
  de: {
    greeting: { morning: "Guten Morgen", afternoon: "Guten Tag", evening: "Guten Abend", night: "Gute Nacht" },
    auth: {
      tagline: "Ich erinnere mich an gestern",
      tagline2: "und helfe dir, heute besser zu leben.",
      welcomeBack: "Willkommen zurück",
      goodToSeeYou: "Schön, dich wiederzusehen",
      email: "E-Mail",
      password: "Passwort",
      forgotPassword: "Passwort vergessen?",
      signingIn: "Anmeldung läuft...",
      signIn: "Anmelden",
      newToApp: "Neu hier?",
      createAccount: "Konto erstellen"
    },
    subscription: { cta: "Kostenlos testen", upgrade: "Auf Pro upgraden" },
    actions: { save: "Speichern", cancel: "Abbrechen", delete: "Löschen", done: "Fertig", add: "Hinzufügen", edit: "Bearbeiten", update: "Aktualisieren", create: "Erstellen", remove: "Entfernen", confirm: "Bestätigen", search: "Suchen", filter: "Filtern", sort: "Sortieren", refresh: "Aktualisieren", upload: "Hochladen", download: "Herunterladen", export: "Exportieren", import: "Importieren" },
    settings: { profile: "Profil", integrations: "Integrationen", subscription: "Abonnement", support: "Hilfe", memory: "Speicher", language: "Sprache & Region", theme: "Design" },
    nav: { home: "Startseite", work: "Arbeit", health: "Gesundheit", life: "Leben", chat: "Chat", settings: "Einstellungen" },
    cards: { chatDesc: "Rede darüber", workDesc: "Schritt für Schritt", healthDesc: "Pass auf dich auf", lifeDesc: "Bleib organisiert", talkToAllora: "Mit Allora sprechen" },
    home: {
      todayProgress: "Heutiger Fortschritt", hydration: "Hydration", nutrition: "Ernährung", tasks: "Aufgaben", points: "Punkte",
      badges: "Abzeichen", viewAll: "Alle anzeigen", noBadges: "Noch keine Abzeichen", keepStreak: "Mach weiter um Abzeichen zu verdienen!",
      achievements: "Erfolge", allCaughtUp: "Du bist auf dem neuesten Stand!", enjoyMorning: "Genieße deinen Morgen",
      morningBriefing: "Dein Morgenbriefing", whatsOnPlate: "Das steht heute an",
      suggestionForYou: "Vorschlag für dich",
      smallStepsCount: "Kleine Schritte zählen. Ich bin für dich da.",
      todaysPulse: "Heutiger Status", energy: "Energie", mood: "Stimmung", stress: "Stress", focus: "Fokus",
      balancedAndReady: "Ausgeglichen und bereit", moderate: "Moderat", stable: "Stabil", low: "Niedrig", peak: "Spitze", high: "Hoch", ok: "OK",
      quickActions: "Schnellaktionen", mealPlanner: "Mahlzeitenplaner", planYourMeals: "Plane deine Mahlzeiten",
      workouts: "Workouts", exerciseLibrary: "Übungsbibliothek", medicines: "Medikamente", checkInteractions: "Wechselwirkungen prüfen",
      logWater: "Wasser protokollieren", trackHydration: "Hydration verfolgen", goalsAndChallenges: "Ziele & Herausforderungen",
      activeChallenges: "Aktive Herausforderungen", newChallenge: "Neue Herausforderung", dashboard: "Dashboard",
      sleep: "Schlaf", history: "Verlauf", water: "Wasser", calories: "Kalorien",
      talkToAllora: "Mit Allora sprechen", youDontHaveToCarry: "Du musst das nicht alleine tragen.",
      oneThingAtATime: "Eins nach dem anderen. Was ist am wichtigsten?",
      showTodaysProgress: "Heutigen Fortschritt anzeigen", hideTodaysProgress: "Heutigen Fortschritt verbergen",
      eveningReflection: "Abendreflexion", howWasYourDay: "Wie war dein Tag? Tippe zum Einchecken.",
      great: "Super", good: "Gut", okay: "Okay", tough: "Schwer", maybeLater: "Vielleicht später",
      heyName: "Hey {name}. Ich bin hier.", goodMorning: "Guten Morgen", morning: "Morgen", evening: "Abend",
      lateNight: "Späte Nacht", readyWhenYouAre: "Bereit wenn du bist.", howAreYou: "Wie geht es dir?",
      windingDown: "Entspannst du oder noch aktiv?", imHere: "Ich bin hier. Sprich mit mir.",
      imHereToHelp: "Ich bin hier um zu helfen, nicht zu überfordern.", voice: "Stimme",
      tasksToComplete: "Aufgaben zu erledigen", billsDueSoon: "Bald fällige Rechnungen", appointmentsToday: "Termine heute",
      rememberWater: "Denk daran Wasser zu trinken", bestFocus: "Beste Fokuszeit",
      // Emotional nudge messages
      cantSleep: "Kannst nicht schlafen? Ich bin hier. Kein Urteil, keine Erinnerungen.",
      letsTakeCare: "Lass uns heute für dich sorgen. Was brauchst du?",
      howAreYouDoing: "Wie läuft's bis jetzt? Ich bin hier, wenn du mich brauchst.",
      afternoonCheckin: "Nachmittags-Check: Kann ich etwas für dich tun?",
      windingDownRest: "Entspannst du dich? Du hast heute genug getan. Ruhe ist auch produktiv.",
      imHereNoRush: "Ich bin jederzeit hier. Keine Eile, keine Agenda.",
      failedLogMood: "Stimmung konnte nicht protokolliert werden. Bitte erneut versuchen.",
      takeYourTime: "Lass dir Zeit. Ich bin hier, wenn du bereit bist.",
      tapToContinue: "Tippen, um fortzufahren",
      alloraRemembers: "Allora erinnert sich...",
      badgesEarned: "Verdiente Abzeichen",
      collector: "Sammler"
    },
    work: {
      title: "Arbeitsmodus", tasks: "Aufgaben", documents: "Dokumente", addTask: "Aufgabe hinzufügen", newTask: "Neue Aufgabe", editTask: "Aufgabe bearbeiten",
      taskTitle: "Titel", taskDescription: "Beschreibung", priority: "Priorität", dueDate: "Fälligkeitsdatum", status: "Status",
      high: "Hoch", medium: "Mittel", low: "Niedrig", urgent: "Dringend", pending: "Ausstehend", inProgress: "In Bearbeitung", completed: "Erledigt",
      addDocument: "Dokument hinzufügen", uploadDocument: "Dokument hochladen", analyzeDocument: "Dokument analysieren", documentSummary: "Dokumentzusammenfassung",
      askQuestion: "Frage zu diesem Dokument stellen", todaysFocus: "Heutiger Fokus", productivityScore: "Produktivitätswert",
      tasksCompleted: "Erledigte Aufgaben", noTasks: "Keine Aufgaben", noDocuments: "Keine Dokumente", createFirstTask: "Erstelle deine erste Aufgabe",
      taskTemplates: "Aufgabenvorlagen", batchActions: "Massenaktionen", aiDocumentAnalysis: "KI-Dokumentenanalyse",
      documentLibrary: "Dokumentenbibliothek",
      noItemsSelected: "Keine Elemente ausgewählt", pleaseSelectItems: "Bitte wähle zuerst Elemente aus",
      batchOperationComplete: "Massenaktion abgeschlossen", appliedToItems: "angewendet auf", items: "Elemente",
      unableToBatch: "Massenaktion konnte nicht abgeschlossen werden", batchOperations: "Massenaktionen",
      selected: "ausgewählt", markComplete: "Als erledigt markieren", archive: "Archivieren", addTag: "Tag hinzufügen",
      quickTaskTemplates: "Schnelle Aufgabenvorlagen", loadingTemplates: "Vorlagen werden geladen...",
      chooseTemplate: "Wähle eine Vorlage um sofort zugehörige Aufgaben zu erstellen:",
      templateApplied: "Vorlage angewendet!", createdTasksFrom: "Erstellt", tasksFromTemplate: "Aufgaben aus",
      templateFailed: "Vorlage fehlgeschlagen", unableToCreateTasks: "Aufgaben konnten nicht erstellt werden. Bitte erneut versuchen.",
      tasksCount: "Aufgaben", useTemplate: "Vorlage verwenden"
    },
    health: {
      title: "Gesundheitsmodus", water: "Wasser", food: "Essen", exercise: "Bewegung", sleep: "Schlaf", medications: "Medikamente", vitals: "Vitalwerte",
      addWater: "Wasser hinzufügen", logFood: "Essen protokollieren", logExercise: "Training protokollieren", logSleep: "Schlaf protokollieren", addMedication: "Medikament hinzufügen",
      glasses: "Gläser", calories: "Kalorien", steps: "Schritte", hours: "Stunden", goal: "Ziel", progress: "Fortschritt",
      waterGoal: "Wasserziel", calorieGoal: "Kalorienziel", stepsGoal: "Schrittziel", sleepGoal: "Schlafziel",
      morningRoutine: "Morgenroutine", eveningRoutine: "Abendroutine", medicationReminder: "Medikamentenerinnerung",
      weight: "Gewicht", bloodPressure: "Blutdruck", heartRate: "Herzfrequenz", bloodSugar: "Blutzucker",
      noMedications: "Keine Medikamente verfolgt", noWorkouts: "Heute keine Trainings protokolliert",
      // Health challenges and goals
      startYourFirstChallenge: "Starte deine erste Herausforderung", healthGoals: "Gesundheitsziele", newGoal: "Neues Ziel",
      noGoalsYet: "Noch keine Ziele gesetzt", clickNewGoal: "Klicke auf 'Neues Ziel' um zu beginnen", noActiveChallenges: "Keine aktiven Herausforderungen",
      // Food scanner
      foodScanner: "Lebensmittel-Scanner", scanLogMeal: "Scannen / Mahlzeit erfassen", snapPhoto: "Mache ein Foto für sofortige Nährwertanalyse"
    },
    life: {
      title: "Lebensmodus", bills: "Rechnungen", expenses: "Ausgaben", appointments: "Termine", budget: "Budget", goals: "Ziele",
      addBill: "Rechnung hinzufügen", addExpense: "Ausgabe hinzufügen", addAppointment: "Termin hinzufügen", setBudget: "Budget festlegen", addGoal: "Ziel hinzufügen",
      billName: "Rechnungsname", amount: "Betrag", dueDate: "Fälligkeitsdatum", category: "Kategorie", recurring: "Wiederkehrend", paid: "Bezahlt", unpaid: "Unbezahlt",
      financialDashboard: "Finanz-Dashboard", monthlyExpenses: "Monatliche Ausgaben", upcomingBills: "Anstehende Rechnungen",
      spendingTrends: "Ausgabentrends", savingsGoals: "Sparziele", currentBalance: "Aktueller Kontostand",
      noBills: "Keine Rechnungen", noExpenses: "Keine Ausgaben protokolliert", noAppointments: "Keine Termine geplant",
      utilities: "Nebenkosten", groceries: "Lebensmittel", transportation: "Transport",
      entertainment: "Unterhaltung", healthcare: "Gesundheit", other: "Sonstiges", savings: "Ersparnisse",
      categories: {
        utilities: "Nebenkosten", groceries: "Lebensmittel", transportation: "Transport",
        entertainment: "Unterhaltung", healthcare: "Gesundheit", other: "Sonstiges", savings: "Ersparnisse"
      }
    },
    chat: {
      title: "Dein Raum", placeholder: "Was beschäftigt dich?", send: "Senden", thinking: "Denke nach...",
      newConversation: "Neue Unterhaltung", history: "Verlauf", clearHistory: "Verlauf löschen",
      voiceInput: "Spracheingabe", attachFile: "Datei anhängen"
    },
    common: { loading: "Wird geladen...", error: "Etwas ist schiefgelaufen", retry: "Erneut versuchen", close: "Schließen", back: "Zurück", next: "Weiter", done: "Fertig", pending: "ausstehend", completed: "erledigt", glasses: "Gläser", cal: "kcal", today: "Heute", yesterday: "Gestern", thisWeek: "Diese Woche", thisMonth: "Dieser Monat", all: "Alle", none: "Keine", total: "Gesamt", average: "Durchschnitt",
      youSaid: "Sie sagten:", processingCommand: "Befehl wird verarbeitet...", exampleCommands: "Beispielbefehle:", action: "Aktion"
    },
    locale: { detected: "Wir haben Ihre Sprache auf", currency: "und Währung auf", changeAnytime: "Sie können dies jederzeit in den Einstellungen ändern." }
  },
  fr: {
    greeting: { morning: "Bonjour", afternoon: "Bon après-midi", evening: "Bonsoir", night: "Bonne nuit" },
    auth: {
      tagline: "Je me souviens d'hier",
      tagline2: "et je t'aide à mieux vivre aujourd'hui.",
      welcomeBack: "Bon retour",
      goodToSeeYou: "Content de te revoir",
      email: "E-mail",
      password: "Mot de passe",
      forgotPassword: "Mot de passe oublié ?",
      signingIn: "Connexion en cours...",
      signIn: "Se connecter",
      newToApp: "Nouveau ici ?",
      createAccount: "Créer un compte"
    },
    subscription: { cta: "Essai gratuit", upgrade: "Passer à Pro" },
    actions: { save: "Enregistrer", cancel: "Annuler", delete: "Supprimer", done: "Terminé", add: "Ajouter", edit: "Modifier", update: "Mettre à jour", create: "Créer", remove: "Retirer", confirm: "Confirmer", search: "Rechercher", filter: "Filtrer", sort: "Trier", refresh: "Actualiser", upload: "Télécharger", download: "Télécharger", export: "Exporter", import: "Importer" },
    settings: { profile: "Profil", integrations: "Intégrations", subscription: "Abonnement", support: "Aide", memory: "Mémoire", language: "Langue & Région", theme: "Thème" },
    nav: { home: "Accueil", work: "Travail", health: "Santé", life: "Vie", chat: "Chat", settings: "Paramètres" },
    cards: { chatDesc: "Parlons-en", workDesc: "Un pas à la fois", healthDesc: "Prenez soin de vous", lifeDesc: "Restez organisé", talkToAllora: "Parler à Allora" },
    home: {
      todayProgress: "Progrès du jour", hydration: "Hydratation", nutrition: "Nutrition", tasks: "Tâches", points: "Points",
      badges: "Badges", viewAll: "Voir tout", noBadges: "Pas encore de badges", keepStreak: "Continuez pour gagner des badges!",
      achievements: "réalisations", allCaughtUp: "Vous êtes à jour!", enjoyMorning: "Profitez de votre matinée",
      morningBriefing: "Briefing du matin", whatsOnPlate: "Voici ce qui vous attend aujourd'hui",
      suggestionForYou: "Suggestion pour vous",
      smallStepsCount: "Les petits pas comptent. Je suis là pour vous.",
      todaysPulse: "Pouls du jour", energy: "Énergie", mood: "Humeur", stress: "Stress", focus: "Concentration",
      balancedAndReady: "Équilibré et prêt", moderate: "Modéré", stable: "Stable", low: "Bas", peak: "Pic", high: "Élevé", ok: "OK",
      quickActions: "Actions rapides", mealPlanner: "Planificateur de repas", planYourMeals: "Planifiez vos repas",
      workouts: "Entraînements", exerciseLibrary: "Bibliothèque d'exercices", medicines: "Médicaments", checkInteractions: "Vérifier les interactions",
      logWater: "Enregistrer l'eau", trackHydration: "Suivre l'hydratation", goalsAndChallenges: "Objectifs et défis",
      activeChallenges: "Défis actifs", newChallenge: "Nouveau défi", dashboard: "Tableau de bord",
      sleep: "Sommeil", history: "Historique", water: "Eau", calories: "Calories",
      talkToAllora: "Parler à Allora", youDontHaveToCarry: "Vous n'avez pas à porter ça seul.",
      oneThingAtATime: "Une chose à la fois. Qu'est-ce qui compte le plus?",
      showTodaysProgress: "Afficher les progrès du jour", hideTodaysProgress: "Masquer les progrès du jour",
      eveningReflection: "Réflexion du soir", howWasYourDay: "Comment était votre journée? Appuyez pour vous connecter.",
      great: "Super", good: "Bien", okay: "OK", tough: "Difficile", maybeLater: "Peut-être plus tard",
      heyName: "Hey {name}. Je suis là.", goodMorning: "Bonjour", morning: "Matin", evening: "Soir",
      lateNight: "Tard dans la nuit", readyWhenYouAre: "Prêt quand vous l'êtes.", howAreYou: "Comment allez-vous?",
      windingDown: "Vous vous détendez ou encore en forme?", imHere: "Je suis là. Parlez-moi.",
      imHereToHelp: "Je suis là pour aider, pas pour vous submerger.", voice: "Voix",
      tasksToComplete: "Tâches à accomplir", billsDueSoon: "Factures bientôt dues", appointmentsToday: "Rendez-vous aujourd'hui",
      rememberWater: "N'oubliez pas de boire de l'eau", bestFocus: "Meilleure concentration",
      // Emotional nudge messages
      cantSleep: "Tu n'arrives pas à dormir ? Je suis là. Pas de jugement, pas de rappels.",
      letsTakeCare: "Prenons soin de toi aujourd'hui. De quoi as-tu besoin ?",
      howAreYouDoing: "Comment ça va jusqu'ici ? Je suis là si tu as besoin.",
      afternoonCheckin: "Check-in de l'après-midi : besoin de quelque chose ?",
      windingDownRest: "Tu te détends ? Tu as assez fait aujourd'hui. Se reposer est aussi productif.",
      imHereNoRush: "Je suis là quand tu veux. Pas de pression.",
      failedLogMood: "Échec de l'enregistrement de l'humeur. Veuillez réessayer.",
      takeYourTime: "Prends ton temps. Je suis là quand tu es prêt.",
      tapToContinue: "Appuyez pour continuer",
      alloraRemembers: "Allora se souvient...",
      badgesEarned: "Badges gagnés",
      collector: "Collectionneur"
    },
    work: {
      title: "Mode Travail", tasks: "Tâches", documents: "Documents", addTask: "Ajouter une tâche", newTask: "Nouvelle tâche", editTask: "Modifier la tâche",
      taskTitle: "Titre", taskDescription: "Description", priority: "Priorité", dueDate: "Date limite", status: "Statut",
      high: "Haute", medium: "Moyenne", low: "Basse", urgent: "Urgent", pending: "En attente", inProgress: "En cours", completed: "Terminé",
      addDocument: "Ajouter un document", uploadDocument: "Télécharger un document", analyzeDocument: "Analyser le document", documentSummary: "Résumé du document",
      askQuestion: "Poser une question sur ce document", todaysFocus: "Focus du jour", productivityScore: "Score de productivité",
      tasksCompleted: "Tâches terminées", noTasks: "Pas de tâches", noDocuments: "Pas de documents", createFirstTask: "Créez votre première tâche",
      taskTemplates: "Modèles de tâches", batchActions: "Actions groupées", aiDocumentAnalysis: "Analyse de document IA",
      documentLibrary: "Bibliothèque de documents",
      noItemsSelected: "Aucun élément sélectionné", pleaseSelectItems: "Veuillez d'abord sélectionner des éléments",
      batchOperationComplete: "Opération groupée terminée", appliedToItems: "appliqué à", items: "éléments",
      unableToBatch: "Impossible de terminer l'opération groupée", batchOperations: "Opérations groupées",
      selected: "sélectionné(s)", markComplete: "Marquer comme terminé", archive: "Archiver", addTag: "Ajouter un tag",
      quickTaskTemplates: "Modèles de tâches rapides", loadingTemplates: "Chargement des modèles...",
      chooseTemplate: "Choisissez un modèle pour créer instantanément des tâches associées :",
      templateApplied: "Modèle appliqué !", createdTasksFrom: "Créé", tasksFromTemplate: "tâches depuis",
      templateFailed: "Échec du modèle", unableToCreateTasks: "Impossible de créer les tâches. Veuillez réessayer.",
      tasksCount: "tâches", useTemplate: "Utiliser le modèle"
    },
    health: {
      title: "Mode Santé", water: "Eau", food: "Alimentation", exercise: "Exercice", sleep: "Sommeil", medications: "Médicaments", vitals: "Signes vitaux",
      addWater: "Ajouter de l'eau", logFood: "Enregistrer un repas", logExercise: "Enregistrer un exercice", logSleep: "Enregistrer le sommeil", addMedication: "Ajouter un médicament",
      glasses: "verres", calories: "calories", steps: "pas", hours: "heures", goal: "Objectif", progress: "Progrès",
      waterGoal: "Objectif eau", calorieGoal: "Objectif calories", stepsGoal: "Objectif pas", sleepGoal: "Objectif sommeil",
      morningRoutine: "Routine matinale", eveningRoutine: "Routine du soir", medicationReminder: "Rappel médicament",
      weight: "Poids", bloodPressure: "Tension artérielle", heartRate: "Fréquence cardiaque", bloodSugar: "Glycémie",
      noMedications: "Aucun médicament suivi", noWorkouts: "Aucun exercice enregistré aujourd'hui",
      // Health challenges and goals
      startYourFirstChallenge: "Commencez votre premier défi", healthGoals: "Objectifs santé", newGoal: "Nouvel objectif",
      noGoalsYet: "Pas encore d'objectifs définis", clickNewGoal: "Cliquez sur 'Nouvel objectif' pour commencer", noActiveChallenges: "Aucun défi actif",
      // Food scanner
      foodScanner: "Scanner alimentaire", scanLogMeal: "Scanner / Enregistrer repas", snapPhoto: "Prenez une photo pour une analyse nutritionnelle instantanée"
    },
    life: {
      title: "Mode Vie", bills: "Factures", expenses: "Dépenses", appointments: "Rendez-vous", budget: "Budget", goals: "Objectifs",
      addBill: "Ajouter une facture", addExpense: "Ajouter une dépense", addAppointment: "Ajouter un rendez-vous", setBudget: "Définir le budget", addGoal: "Ajouter un objectif",
      billName: "Nom de la facture", amount: "Montant", dueDate: "Date d'échéance", category: "Catégorie", recurring: "Récurrent", paid: "Payé", unpaid: "Impayé",
      financialDashboard: "Tableau de bord financier", monthlyExpenses: "Dépenses mensuelles", upcomingBills: "Factures à venir",
      spendingTrends: "Tendances de dépenses", savingsGoals: "Objectifs d'épargne", currentBalance: "Solde actuel",
      noBills: "Pas de factures", noExpenses: "Pas de dépenses enregistrées", noAppointments: "Pas de rendez-vous prévus",
      utilities: "Services publics", groceries: "Épicerie", transportation: "Transport",
      entertainment: "Divertissement", healthcare: "Santé", other: "Autre", savings: "Épargne",
      categories: {
        utilities: "Services publics", groceries: "Épicerie", transportation: "Transport",
        entertainment: "Divertissement", healthcare: "Santé", other: "Autre", savings: "Épargne"
      }
    },
    chat: {
      title: "Votre Espace", placeholder: "Qu'avez-vous en tête?", send: "Envoyer", thinking: "Réflexion...",
      newConversation: "Nouvelle conversation", history: "Historique", clearHistory: "Effacer l'historique",
      voiceInput: "Entrée vocale", attachFile: "Joindre un fichier"
    },
    common: { loading: "Chargement...", error: "Une erreur s'est produite", retry: "Réessayer", close: "Fermer", back: "Retour", next: "Suivant", done: "Terminé", pending: "en attente", completed: "terminé", glasses: "verres", cal: "cal", today: "Aujourd'hui", yesterday: "Hier", thisWeek: "Cette semaine", thisMonth: "Ce mois", all: "Tout", none: "Aucun", total: "Total", average: "Moyenne",
      youSaid: "Vous avez dit:", processingCommand: "Traitement de la commande...", exampleCommands: "Exemples de commandes:", action: "Action"
    },
    locale: { detected: "Nous avons défini votre langue sur", currency: "et devise sur", changeAnytime: "Vous pouvez modifier cela à tout moment dans les Paramètres." }
  },
  pt: {
    greeting: { morning: "Bom dia", afternoon: "Boa tarde", evening: "Boa noite", night: "Boa noite" },
    auth: {
      tagline: "Lembro como ontem foi",
      tagline2: "e ajudo você a viver melhor hoje.",
      welcomeBack: "Bem-vindo de volta",
      goodToSeeYou: "Bom te ver de novo",
      email: "E-mail",
      password: "Senha",
      forgotPassword: "Esqueceu a senha?",
      signingIn: "Entrando...",
      signIn: "Entrar",
      newToApp: "Novo aqui?",
      createAccount: "Criar uma conta"
    },
    subscription: { cta: "Teste grátis", upgrade: "Atualizar para Pro" },
    actions: { save: "Salvar", cancel: "Cancelar", delete: "Excluir", done: "Concluído", add: "Adicionar", edit: "Editar", update: "Atualizar", create: "Criar", remove: "Remover", confirm: "Confirmar", search: "Pesquisar", filter: "Filtrar", sort: "Ordenar", refresh: "Atualizar", upload: "Enviar", download: "Baixar", export: "Exportar", import: "Importar" },
    settings: { profile: "Perfil", integrations: "Integrações", subscription: "Assinatura", support: "Suporte", memory: "Memória", language: "Idioma e Região", theme: "Tema",
      editProfile: "Editar Perfil", manageSubscription: "Gerenciar Assinatura", paymentHistory: "Histórico de Pagamentos", logOut: "Sair",
      notifications: "Notificações",
      chooseLanguageRegion: "Escolha seu idioma e região preferidos para uma experiência personalizada",
      currentSettings: "Configurações Atuais", detectedLocation: "Sua localização e preferências detectadas",
      country: "País", currency: "Moeda", timezone: "Fuso Horário",
      chooseLanguage: "Escolha seu idioma preferido", updateProfile: "Atualize suas informações pessoais",
      gender: "Gênero", memberSince: "Membro desde", status: "Status", active: "Ativo", plan: "Plano",
      calendarIntegrations: "Integrações de Calendário", connectCalendars: "Conecte seus calendários para sincronização automática",
      googleCalendar: "Google Agenda", appleCalendar: "Calendário Apple", syncEvents: "Sincronizar eventos e compromissos",
      connected: "Conectado", notConnected: "Não conectado",
      contactSupport: "Contatar Suporte", sendMessage: "Envie-nos uma mensagem e ajudaremos você",
      requestFeature: "Solicitar Recurso", shareIdeas: "Compartilhe suas ideias para nos ajudar a melhorar",
      whatAlloraRemembers: "O que Allora Lembra", appTheme: "Tema do App"
    },
    nav: { home: "Início", work: "Trabalho", health: "Saúde", life: "Vida", chat: "Chat", settings: "Configurações" },
    cards: { chatDesc: "Desabafe", workDesc: "Um passo de cada vez", healthDesc: "Cuide-se", lifeDesc: "Mantenha-se organizado", talkToAllora: "Falar com Allora" },
    home: {
      todayProgress: "Progresso de hoje", hydration: "Hidratação", nutrition: "Nutrição", tasks: "Tarefas", points: "Pontos",
      badges: "Medalhas", viewAll: "Ver tudo", noBadges: "Nenhuma medalha ainda", keepStreak: "Continue para ganhar medalhas!",
      achievements: "conquistas", allCaughtUp: "Você está em dia!", enjoyMorning: "Aproveite sua manhã",
      morningBriefing: "Resumo da manhã", whatsOnPlate: "O que você tem para hoje",
      suggestionForYou: "Sugestão para você",
      smallStepsCount: "Pequenos passos importam. Estou aqui para você.",
      todaysPulse: "Pulso de hoje", energy: "Energia", mood: "Humor", stress: "Estresse", focus: "Foco",
      balancedAndReady: "Equilibrado e pronto", moderate: "Moderado", stable: "Estável", low: "Baixo", peak: "Alto", high: "Alto", ok: "OK",
      quickActions: "Ações Rápidas", mealPlanner: "Planejador de Refeições", planYourMeals: "Planeje suas refeições",
      workouts: "Treinos", exerciseLibrary: "Biblioteca de exercícios", medicines: "Medicamentos", checkInteractions: "Verificar interações",
      logWater: "Registrar Água", trackHydration: "Acompanhar hidratação", goalsAndChallenges: "Metas e Desafios",
      activeChallenges: "Desafios Ativos", newChallenge: "Novo Desafio", dashboard: "Painel",
      sleep: "Sono", history: "Histórico", water: "Água", calories: "Calorias",
      talkToAllora: "Falar com Allora", youDontHaveToCarry: "Você não precisa carregar isso sozinha.",
      oneThingAtATime: "Uma coisa de cada vez. O que importa mais?",
      showTodaysProgress: "Mostrar progresso de hoje", hideTodaysProgress: "Esconder progresso de hoje",
      eveningReflection: "Reflexão da noite", howWasYourDay: "Como foi seu dia? Toque para registrar.",
      great: "Ótimo", good: "Bom", okay: "Ok", tough: "Difícil", maybeLater: "Talvez depois",
      heyName: "Oi {name}. Estou aqui.", goodMorning: "Bom dia", morning: "Manhã", evening: "Boa noite",
      lateNight: "Noite tardia", readyWhenYouAre: "Pronto quando você estiver.", howAreYou: "Como você está?",
      windingDown: "Relaxando ou ainda com energia?", imHere: "Estou aqui. Fale comigo.",
      imHereToHelp: "Estou aqui para ajudar, não para sobrecarregar.", voice: "Voz",
      tasksToComplete: "tarefas a concluir", billsDueSoon: "contas a vencer em breve", appointmentsToday: "compromissos hoje",
      rememberWater: "Lembre-se de beber água", bestFocus: "Melhor foco",
      // Emotional nudge messages
      cantSleep: "Não consegue dormir? Estou aqui. Sem julgamentos, sem lembretes.",
      letsTakeCare: "Vamos cuidar de você hoje. Do que você precisa?",
      howAreYouDoing: "Como está até agora? Estou aqui se precisar.",
      afternoonCheckin: "Check-in da tarde: precisa de algo?",
      windingDownRest: "Relaxando? Você já fez o suficiente hoje. Descansar também é produtivo.",
      imHereNoRush: "Estou aqui quando precisar. Sem pressa, sem agenda.",
      failedLogMood: "Falha ao registrar humor. Por favor, tente novamente.",
      takeYourTime: "Leve seu tempo. Estou aqui quando estiver pronto.",
      tapToContinue: "Toque para continuar",
      alloraRemembers: "Allora lembra...",
      badgesEarned: "Medalhas Conquistadas",
      collector: "Colecionador"
    },
    work: {
      title: "Modo Trabalho", tasks: "Tarefas", documents: "Documentos", addTask: "Adicionar tarefa", newTask: "Nova tarefa", editTask: "Editar tarefa",
      taskTitle: "Título", taskDescription: "Descrição", priority: "Prioridade", dueDate: "Data limite", status: "Status",
      high: "Alta", medium: "Média", low: "Baixa", urgent: "Urgente", pending: "Pendente", inProgress: "Em andamento", completed: "Concluído",
      addDocument: "Adicionar documento", uploadDocument: "Enviar documento", analyzeDocument: "Analisar documento", documentSummary: "Resumo do documento",
      askQuestion: "Fazer uma pergunta sobre este documento", todaysFocus: "Foco de hoje", productivityScore: "Pontuação de produtividade",
      tasksCompleted: "Tarefas concluídas", noTasks: "Sem tarefas", noDocuments: "Sem documentos", createFirstTask: "Crie sua primeira tarefa",
      allCaughtUp: "Tudo em dia!", noUrgentTasks: "Sem tarefas urgentes. Ótimo trabalho!",
      taskTemplates: "Modelos de Tarefa", batchActions: "Ações em Lote", aiDocumentAnalysis: "Análise de Documento IA",
      documentLibrary: "Biblioteca de Documentos", uploadHint: "Envie gravações MP3/MP4, documentos PDF ou cole texto para análise IA instantânea",
      noItemsSelected: "Nenhum item selecionado", pleaseSelectItems: "Por favor, selecione os itens primeiro",
      batchOperationComplete: "Operação em lote concluída", appliedToItems: "aplicado a", items: "itens",
      unableToBatch: "Não foi possível concluir a operação em lote", batchOperations: "Operações em lote",
      selected: "selecionado(s)", markComplete: "Marcar como concluído", archive: "Arquivar", addTag: "Adicionar tag",
      quickTaskTemplates: "Modelos de tarefas rápidas", loadingTemplates: "Carregando modelos...",
      chooseTemplate: "Escolha um modelo para criar tarefas relacionadas instantaneamente:",
      templateApplied: "Modelo aplicado!", createdTasksFrom: "Criado", tasksFromTemplate: "tarefas de",
      templateFailed: "Falha no modelo", unableToCreateTasks: "Não foi possível criar tarefas. Tente novamente.",
      tasksCount: "tarefas", useTemplate: "Usar modelo"
    },
    health: {
      title: "Modo Saúde", water: "Água", food: "Alimentação", exercise: "Exercício", sleep: "Sono", medications: "Medicamentos", vitals: "Sinais vitais",
      addWater: "Adicionar água", logFood: "Registrar refeição", logExercise: "Registrar exercício", logSleep: "Registrar sono", addMedication: "Adicionar medicamento", logWorkout: "Registrar Treino", addFood: "Adicionar Refeição",
      glasses: "copos", calories: "calorias", steps: "passos", hours: "horas", goal: "Meta", progress: "Progresso",
      waterGoal: "Meta de água", calorieGoal: "Meta de calorias", stepsGoal: "Meta de passos", sleepGoal: "Meta de sono",
      morningRoutine: "Rotina matinal", eveningRoutine: "Rotina noturna", medicationReminder: "Lembrete de medicamento",
      weight: "Peso", bloodPressure: "Pressão arterial", heartRate: "Frequência cardíaca", bloodSugar: "Glicemia",
      noMedications: "Nenhum medicamento rastreado", noWorkouts: "Nenhum treino registrado hoje",
      avgWater: "ÁGUA MÉDIA", avgCalories: "CALORIAS MÉDIAS", daysTracked: "DIAS RASTREADOS",
      keepMoving: "Continue se movendo!", stayHydrated: "Mantenha-se hidratado!", burnIt: "Queime!", keepLogging: "Continue registrando!",
      healthCalendar: "Calendário de Saúde", trackYourHealthJourney: "Acompanhe sua jornada de saúde",
      autoSleepTracking: "Rastreamento Automático de Sono Ativo", recentSleepLogs: "Registros de Sono Recentes",
      noSleepLogs: "Nenhum registro de sono ainda. Comece a rastrear para ver insights!", logYourFirstSleep: "Registre Seu Primeiro Sono",
      nutritionTracker: "Rastreador de Nutrição", protein: "Proteína", carbs: "Carboidratos", fats: "Gorduras", fiber: "Fibra", sugar: "Açúcar",
      ofGoal: "da meta", limit: "limite", womensHealth: "Saúde da Mulher",
      startYourFirstChallenge: "Comece Seu Primeiro Desafio", healthGoals: "Metas de Saúde", newGoal: "Nova Meta",
      noGoalsYet: "Nenhuma meta definida ainda", clickNewGoal: "Clique em 'Nova Meta' para começar",
      foodScanner: "Scanner de Alimentos", scanLogMeal: "Escanear / Registrar Refeição", snapPhoto: "Tire uma foto para obter informações nutricionais",
      healthAssistant: "Assistente de Saúde", askHealthQuestion: "Fazer Pergunta de Saúde",
      getPersonalizedAdvice: "Obtenha conselhos de saúde personalizados com base nos seus dados",
      medicines: "Medicamentos", noMedicinesAdded: "Nenhum medicamento adicionado", addMedicinesToGetReminders: "Adicione seus medicamentos diários para receber lembretes",
      takenToday: "Tomado", takeNow: "Tomar Agora", logSymptoms: "Registrar Sintomas", symptomsLogged: "Seus sintomas foram registrados",
      foodDeleted: "Entrada de alimento removida", waterIntake: "Ingestão de Água", burned: "Queimado", caloriesIntake: "Calorias Ingeridas",
      nutrition: "Nutrição", workoutsCount: "Treinos", mealsCount: "Refeições", noHealthData: "Nenhum dado de saúde registrado neste dia",
      healthDataFor: "Dados de Saúde para", duration: "duração", minutes: "minutos",
      paymentHistory: "Histórico de Pagamentos", viewTransactions: "Veja todas as suas transações passadas e pagamentos de assinatura",
      noPaymentsYet: "Nenhum pagamento ainda", paymentHistoryAppears: "Seu histórico de pagamentos aparecerá aqui após a primeira transação"
    },
    womensHealth: {
      cycleDay: "Dia", phase: "Fase", welcome: "Bem-vinda", logPeriodToStart: "Registre sua menstruação para começar o rastreamento",
      menstrual: "Menstrual", follicular: "Folicular", ovulation: "Ovulação", luteal: "Lútea",
      nextPeriodIn: "Próxima menstruação em", days: "dias", logPeriod: "Registrar Menstruação", dailyLog: "Registro Diário",
      todaysMood: "Humor de Hoje", symptomsLogged: "sintomas registrados", today: "Hoje", calendar: "Calendário",
      insights: "Insights", settings: "Configurações", articles: "Artigos",
      pregnancyMode: "Modo Gravidez", trackPregnancy: "Acompanhe sua jornada de gravidez",
      partnerAccess: "Acesso do Parceiro", shareCycleInfo: "Compartilhe informações do ciclo com parceiro", share: "Compartilhar",
      reminders: "Lembretes", pillPeriodAlerts: "Alertas de pílula, menstruação e fertilidade",
      week: "Semana", trimester: "Trimestre", dueDate: "Data Prevista", weeksToGo: "semanas restantes",
      logSymptoms: "Registrar Sintomas", exitPregnancyMode: "Sair do Modo Gravidez", weekTips: "Dicas",
      babyDevelopment: "Desenvolvimento do Bebê", selfCare: "Autocuidado",
      period: "Menstruação", predicted: "Previsto", fertile: "Fértil",
      fertileWindow: "Janela Fértil", ovulationDate: "Ovulação", nextPeriodPredicted: "Próxima Menstruação Prevista",
      recentPeriods: "Menstruações Recentes", noPeriodsLogged: "Nenhuma menstruação registrada", ongoing: "Em andamento", flow: "fluxo",
      cycleInsights: "Insights do Ciclo", averageCycle: "Ciclo Médio", averagePeriod: "Menstruação Média", cyclesTracked: "Ciclos Rastreados",
      enablePregnancyMode: "Ativar Modo Gravidez", disablePregnancyMode: "Desativar Modo Gravidez?",
      enterDueDate: "Insira sua data prevista de parto", switchToPregnancy: "Isso mudará para o modo de rastreamento de gravidez.",
      switchToPeriod: "Isso voltará para o rastreamento normal do ciclo.",
      shareWithPartner: "Compartilhar com Parceiro", generateShareLink: "Gerar Link de Compartilhamento",
      partnerCanSee: "Gere um link seguro para compartilhar informações do seu ciclo. Eles poderão ver:",
      currentPhase: "Sua fase atual do ciclo", predictedDates: "Datas previstas da menstruação", fertileWindowOptional: "Janela fértil (opcional)",
      addReminder: "Adicionar Lembrete", reminderType: "Tipo de Lembrete", pillReminder: "Lembrete de Pílula", periodReminder: "Lembrete de Menstruação",
      fertilityWindow: "Janela de Fertilidade", custom: "Personalizado", time: "Hora", labelOptional: "Rótulo (Opcional)",
      saveReminder: "Salvar Lembrete", howAreYouFeeling: "Como você está se sentindo?", energyLevel: "Nível de Energia",
      notes: "Notas", additionalNotes: "Notas adicionais...", saveDailyLog: "Salvar Registro Diário",
      startDate: "Data de Início", endDate: "Data de Término", endDateOptional: "Data de Término (Opcional)", flowIntensity: "Intensidade do Fluxo",
      light: "leve", medium: "médio", heavy: "intenso", savePeriod: "Salvar Menstruação", saving: "Salvando...",
      great: "Ótimo", good: "Bom", okay: "Ok", low: "Baixo", bad: "Ruim",
      physical: "Físico", discharge: "Corrimento", mood: "Humor", sleepQuality: "Sono", activity: "Atividade", intimacy: "Intimidade",
      cramps: "Cólicas", headache: "Dor de Cabeça", fatigue: "Fadiga", bloating: "Inchaço", breastTenderness: "Sensibilidade nos Seios",
      acne: "Acne", backPain: "Dor nas Costas", nausea: "Náusea", dizziness: "Tontura",
      none: "Nenhum", creamy: "Cremoso", eggWhite: "Clara de Ovo", watery: "Aquoso", sticky: "Pegajoso", spotting: "Spotting",
      happy: "Feliz", calm: "Calma", energetic: "Energética", moodSwings: "Mudanças de Humor", irritable: "Irritada",
      anxious: "Ansiosa", sad: "Triste", sensitive: "Sensível",
      goodSleep: "Sono Bom", insomnia: "Insônia", restless: "Inquieta", oversleep: "Dormiu Demais",
      vividDreams: "Sonhos Vívidos", nightSweats: "Suor Noturno",
      exercise: "Exercício", yoga: "Yoga", walking: "Caminhada", running: "Corrida", swimming: "Natação", restDay: "Dia de Descanso",
      protected: "Protegido", unprotected: "Desprotegido", highDrive: "Libido Alta", lowDrive: "Libido Baixa",
      tipsFor: "Dicas para", phaseTips: "Fase"
    },
    life: {
      title: "Modo Vida", bills: "Contas", expenses: "Despesas", appointments: "Compromissos", budget: "Orçamento", goals: "Metas",
      addBill: "Adicionar conta", addExpense: "Adicionar despesa", addAppointment: "Adicionar compromisso", setBudget: "Definir orçamento", addGoal: "Adicionar meta",
      billName: "Nome da conta", amount: "Valor", dueDate: "Data de vencimento", category: "Categoria", recurring: "Recorrente", paid: "Pago", unpaid: "Não pago",
      financialDashboard: "Painel financeiro", monthlyExpenses: "Despesas mensais", upcomingBills: "Contas a vencer",
      spendingTrends: "Tendências de gastos", savingsGoals: "Metas de economia", currentBalance: "Saldo atual",
      noBills: "Sem contas", noExpenses: "Sem despesas registradas", noAppointments: "Sem compromissos agendados",
      smartFinancial: "Finanças Inteligentes", billsAndGoals: "Contas e Metas", calendar: "Calendário",
      smartFinancialDashboard: "Painel Financeiro Inteligente", aiPoweredInsights: "Insights com IA para gerenciar suas finanças",
      thisMonthSpending: "Gastos Este Mês", topSpendingCategory: "Categoria Principal de Gastos", billStatus: "Status das Contas",
      upcoming: "Próximas", overdue: "Atrasadas", sixMonthTrend: "Tendência de 6 Meses",
      totalExpenses: "DESPESAS TOTAIS", transactions: "TRANSAÇÕES", appointmentsCount: "COMPROMISSOS", billsDue: "CONTAS A VENCER",
      monthlySpend: "Gasto mensal", totalCount: "Contagem total", scheduled: "Agendado", pending: "Pendente",
      lifeCalendar: "Calendário de Vida", trackExpensesAppointments: "Acompanhe despesas, compromissos e contas",
      visualIndicators: "Indicadores visuais em cada data",
      sun: "Dom", mon: "Seg", tue: "Ter", wed: "Qua", thu: "Qui", fri: "Sex", sat: "Sáb",
      vsLastMonth: "vs mês passado", loadingFinancialInsights: "Carregando insights financeiros...",
      spendingByCategory: "Gastos por Categoria", noSpendingData: "Nenhum dado de gastos ainda",
      budgetTracker: "Rastreador de Orçamento", used: "Usado", setBudgetBtn: "Definir Orçamento",
      totalMonthlyBudget: "Orçamento Mensal Total", categoryBudgets: "Orçamentos por Categoria",
      saveBudget: "Salvar Orçamento", overall: "Geral", left: "restante",
      financialGoals: "Metas Financeiras", addNewGoal: "Adicionar Nova Meta", goalName: "Nome da Meta",
      targetAmount: "Valor Alvo", currentAmount: "Valor Atual", targetDate: "Data Alvo",
      goalCategory: "Categoria", createGoal: "Criar Meta", noGoals: "Nenhuma meta financeira ainda",
      setFirstGoal: "Defina sua primeira meta de economia para começar a rastrear",
      overdueBills: "Contas Atrasadas", due: "Vencimento", addAppointmentTitle: "Adicionar Compromisso",
      type: "Tipo", date: "Data", time: "Hora", location: "Local",
      utilities: "Serviços", groceries: "Compras", transportation: "Transporte",
      entertainment: "Entretenimento", healthcare: "Saúde", other: "Outros", savings: "Poupança",
      categories: {
        utilities: "Serviços", groceries: "Compras", transportation: "Transporte",
        entertainment: "Entretenimento", healthcare: "Saúde", other: "Outros", savings: "Poupança"
      }
    },
    notifications: {
      title: "Configurações de Notificação", active: "Notificações Ativas",
      receiveReminders: "Você receberá lembretes mesmo com o app fechado",
      disable: "Desativar", enable: "Ativar", enableNotifications: "Ativar Notificações",
      customize: "Personalize Suas Notificações",
      morningGreeting: "Saudação Matinal", morningDesc: "Resumo diário às 8h com tarefas e compromissos",
      eveningSummary: "Resumo Noturno", eveningDesc: "Resumo diário às 20h com conquistas",
      waterReminders: "Lembretes de Água", waterDesc: "Mantenha-se hidratado com lembretes regulares",
      medicineReminders: "Lembretes de Medicamento", medicineDesc: "Nunca esqueça sua medicação",
      appointmentAlerts: "Alertas de Compromisso", appointmentDesc: "Lembretes antes de reuniões e compromissos",
      motivationBoosts: "Motivação", motivationDesc: "Mensagens da tarde para te manter animado",
      taskReminders: "Lembretes de Tarefas", taskDesc: "Seja notificado sobre prazos",
      workoutReminders: "Lembretes de Treino", workoutDesc: "Mantenha suas metas de fitness",
      waterReminderInterval: "Intervalo de Lembrete de Água",
      everyHour: "A cada 1 hora", every2Hours: "A cada 2 horas", every3Hours: "A cada 3 horas", every4Hours: "A cada 4 horas",
      savePreferences: "Salvar Preferências", blocked: "Notificações Bloqueadas",
      enableInBrowser: "Por favor, ative as notificações nas configurações do navegador",
      notSupported: "Notificações push não suportadas",
      testNotification: "Testar Notificação"
    },
    chat: {
      title: "Seu Espaço", placeholder: "O que está em sua mente?", send: "Enviar", thinking: "Pensando...",
      newConversation: "Nova conversa", history: "Histórico", clearHistory: "Limpar histórico",
      voiceInput: "Entrada de voz", attachFile: "Anexar arquivo"
    },
    common: { loading: "Carregando...", error: "Algo deu errado", retry: "Tentar novamente", close: "Fechar", back: "Voltar", next: "Próximo", done: "Concluído", pending: "pendente", completed: "concluído", glasses: "copos", cal: "cal", today: "Hoje", yesterday: "Ontem", thisWeek: "Esta semana", thisMonth: "Este mês", all: "Todos", none: "Nenhum", total: "Total", average: "Média",
      youSaid: "Você disse:", processingCommand: "Processando comando...", exampleCommands: "Exemplos de comandos:", action: "Ação"
    },
    locale: { detected: "Definimos seu idioma como", currency: "e moeda como", changeAnytime: "Você pode alterar isso a qualquer momento nas Configurações." }
  },
  id: {
    greeting: { morning: "Selamat pagi", afternoon: "Selamat siang", evening: "Selamat malam", night: "Selamat malam" },
    auth: {
      tagline: "Aku ingat perasaan kemarin",
      tagline2: "dan membantu kamu menjalani hari ini lebih baik.",
      welcomeBack: "Selamat datang kembali",
      goodToSeeYou: "Senang bertemu lagi",
      email: "Email",
      password: "Kata sandi",
      forgotPassword: "Lupa kata sandi?",
      signingIn: "Masuk...",
      signIn: "Masuk",
      newToApp: "Baru di sini?",
      createAccount: "Buat akun"
    },
    subscription: { cta: "Coba gratis", upgrade: "Upgrade ke Pro" },
    actions: { save: "Simpan", cancel: "Batal", delete: "Hapus", done: "Selesai" },
    settings: { profile: "Profil", integrations: "Integrasi", subscription: "Langganan", support: "Dukungan", memory: "Memori", language: "Bahasa & Wilayah", theme: "Tema" },
    nav: { home: "Beranda", work: "Kerja", health: "Kesehatan", life: "Kehidupan", chat: "Obrolan", settings: "Pengaturan" },
    cards: { chatDesc: "Curhat yuk", workDesc: "Selangkah demi selangkah", healthDesc: "Jaga kesehatanmu", lifeDesc: "Tetap teratur", talkToAllora: "Bicara dengan Allora" },
    home: {
      todayProgress: "Kemajuan Hari Ini", hydration: "Hidrasi", nutrition: "Nutrisi", tasks: "Tugas", points: "Poin",
      badges: "Lencana", viewAll: "Lihat semua", noBadges: "Belum ada lencana", keepStreak: "Terus lanjutkan untuk mendapat lencana!",
      achievements: "pencapaian", allCaughtUp: "Kamu sudah up to date!", enjoyMorning: "Selamat menikmati pagimu",
      morningBriefing: "Briefing Pagi", whatsOnPlate: "Ini yang perlu kamu lakukan hari ini",
      suggestionForYou: "Saran untukmu",
      smallStepsCount: "Langkah kecil itu penting. Aku di sini untukmu.",
      todaysPulse: "Kondisi Hari Ini", energy: "Energi", mood: "Suasana Hati", stress: "Stres", focus: "Fokus",
      balancedAndReady: "Seimbang dan siap", moderate: "Sedang", stable: "Stabil", low: "Rendah", peak: "Puncak", high: "Tinggi", ok: "OK",
      quickActions: "Aksi Cepat", mealPlanner: "Perencana Makanan", planYourMeals: "Rencanakan makananmu",
      workouts: "Latihan", exerciseLibrary: "Perpustakaan Olahraga", medicines: "Obat-obatan", checkInteractions: "Cek interaksi",
      logWater: "Catat Air", trackHydration: "Lacak Hidrasi", goalsAndChallenges: "Tujuan & Tantangan",
      activeChallenges: "Tantangan Aktif", newChallenge: "Tantangan Baru", dashboard: "Dasbor",
      sleep: "Tidur", history: "Riwayat", water: "Air", calories: "Kalori",
      talkToAllora: "Bicara dengan Allora", youDontHaveToCarry: "Kamu tidak perlu menanggung ini sendiri.",
      oneThingAtATime: "Satu per satu. Apa yang paling penting?",
      showTodaysProgress: "Tampilkan kemajuan hari ini", hideTodaysProgress: "Sembunyikan kemajuan hari ini",
      eveningReflection: "Refleksi Malam", howWasYourDay: "Bagaimana harimu? Ketuk untuk check-in.",
      great: "Luar Biasa", good: "Baik", okay: "Oke", tough: "Berat", maybeLater: "Nanti saja",
      heyName: "Hai {name}. Aku di sini.", goodMorning: "Selamat pagi", morning: "Pagi", evening: "Malam",
      lateNight: "Larut malam", readyWhenYouAre: "Siap kapanpun kamu siap.", howAreYou: "Bagaimana kabarmu?",
      windingDown: "Istirahat atau masih semangat?", imHere: "Aku di sini. Bicara padaku.",
      imHereToHelp: "Aku di sini untuk membantu, bukan membebani.", voice: "Suara",
      tasksToComplete: "Tugas yang harus diselesaikan", billsDueSoon: "Tagihan segera jatuh tempo", appointmentsToday: "Janji hari ini",
      rememberWater: "Ingat minum air", bestFocus: "Waktu fokus terbaik",
      // Emotional nudge messages
      cantSleep: "Tidak bisa tidur? Aku di sini. Tanpa penilaian, tanpa pengingat.",
      letsTakeCare: "Ayo jaga dirimu hari ini. Apa yang kamu butuhkan?",
      howAreYouDoing: "Bagaimana sampai sekarang? Aku di sini kalau kamu butuh.",
      afternoonCheckin: "Check-in sore: butuh sesuatu?",
      windingDownRest: "Rileks? Kamu sudah cukup hari ini. Istirahat juga produktif.",
      imHereNoRush: "Aku di sini kapanpun kamu butuh. Tidak ada tekanan.",
      failedLogMood: "Gagal mencatat suasana hati. Silakan coba lagi.",
      takeYourTime: "Pelan-pelan saja. Aku di sini saat kamu siap.",
      tapToContinue: "Ketuk untuk melanjutkan",
      alloraRemembers: "Allora mengingat...",
      badgesEarned: "Lencana Diperoleh",
      collector: "Kolektor"
    },
    work: {
      title: "Mode Kerja", tasks: "Tugas", documents: "Dokumen", addTask: "Tambah tugas", newTask: "Tugas baru", editTask: "Edit tugas",
      taskTitle: "Judul", taskDescription: "Deskripsi", priority: "Prioritas", dueDate: "Tenggat", status: "Status",
      high: "Tinggi", medium: "Sedang", low: "Rendah", urgent: "Mendesak", pending: "Tertunda", inProgress: "Berlangsung", completed: "Selesai",
      tasksCompleted: "Tugas selesai", noTasks: "Belum ada tugas", noDocuments: "Belum ada dokumen", createFirstTask: "Buat tugas pertamamu",
      taskTemplates: "Template Tugas", batchActions: "Aksi Massal", aiDocumentAnalysis: "Analisis Dokumen AI",
      documentLibrary: "Perpustakaan Dokumen",
      noItemsSelected: "Tidak ada item dipilih", pleaseSelectItems: "Silakan pilih item terlebih dahulu",
      batchOperationComplete: "Operasi massal selesai", appliedToItems: "diterapkan ke", items: "item",
      unableToBatch: "Tidak dapat menyelesaikan operasi massal", batchOperations: "Operasi Massal",
      selected: "dipilih", markComplete: "Tandai selesai", archive: "Arsipkan", addTag: "Tambah tag",
      quickTaskTemplates: "Template Tugas Cepat", loadingTemplates: "Memuat template...",
      chooseTemplate: "Pilih template untuk langsung membuat tugas terkait:",
      templateApplied: "Template diterapkan!", createdTasksFrom: "Dibuat", tasksFromTemplate: "tugas dari",
      templateFailed: "Template gagal", unableToCreateTasks: "Tidak dapat membuat tugas. Silakan coba lagi.",
      tasksCount: "tugas", useTemplate: "Gunakan template"
    },
    health: {
      // Health challenges and goals
      startYourFirstChallenge: "Mulai tantangan pertamamu", healthGoals: "Tujuan kesehatan", newGoal: "Tujuan baru",
      noGoalsYet: "Belum ada tujuan yang ditetapkan", clickNewGoal: "Klik 'Tujuan baru' untuk memulai", noActiveChallenges: "Tidak ada tantangan aktif",
      // Food scanner
      foodScanner: "Pemindai Makanan", scanLogMeal: "Pindai / Catat Makanan", snapPhoto: "Ambil foto untuk analisis nutrisi instan"
    },
    life: {
      title: "Mode Kehidupan", bills: "Tagihan", expenses: "Pengeluaran", appointments: "Janji", budget: "Anggaran", goals: "Tujuan",
      noBills: "Tidak ada tagihan", noExpenses: "Tidak ada pengeluaran tercatat", noAppointments: "Tidak ada janji terjadwal",
      utilities: "Utilitas", groceries: "Belanjaan", transportation: "Transportasi",
      entertainment: "Hiburan", healthcare: "Kesehatan", other: "Lainnya", savings: "Tabungan",
      categories: {
        utilities: "Utilitas", groceries: "Belanjaan", transportation: "Transportasi",
        entertainment: "Hiburan", healthcare: "Kesehatan", other: "Lainnya", savings: "Tabungan"
      }
    },
    common: { loading: "Memuat...", error: "Terjadi kesalahan", retry: "Coba lagi", close: "Tutup", back: "Kembali", next: "Lanjut", done: "Selesai", pending: "tertunda", completed: "selesai", glasses: "gelas", cal: "kal",
      youSaid: "Anda mengatakan:", processingCommand: "Memproses perintah...", exampleCommands: "Contoh Perintah:", action: "Aksi"
    },
    locale: { detected: "Kami telah mengatur bahasa Anda ke", currency: "dan mata uang ke", changeAnytime: "Anda dapat mengubahnya kapan saja di Pengaturan." }
  },
  es: {
    greeting: { morning: "Buenos días", afternoon: "Buenas tardes", evening: "Buenas noches", night: "Buenas noches" },
    auth: {
      tagline: "Recuerdo cómo fue ayer",
      tagline2: "y te ayudo a vivir mejor hoy.",
      welcomeBack: "Bienvenido de nuevo",
      goodToSeeYou: "Qué bueno verte otra vez",
      email: "Correo electrónico",
      password: "Contraseña",
      forgotPassword: "¿Olvidaste tu contraseña?",
      signingIn: "Iniciando sesión...",
      signIn: "Iniciar sesión",
      newToApp: "¿Eres nuevo?",
      createAccount: "Crear una cuenta"
    },
    subscription: { cta: "Prueba gratis", upgrade: "Actualizar a Pro" },
    actions: { save: "Guardar", cancel: "Cancelar", delete: "Eliminar", done: "Listo", add: "Añadir", edit: "Editar", update: "Actualizar", create: "Crear", remove: "Quitar", confirm: "Confirmar", search: "Buscar", filter: "Filtrar", sort: "Ordenar", refresh: "Actualizar", upload: "Subir", download: "Descargar", export: "Exportar", import: "Importar" },
    settings: { profile: "Perfil", integrations: "Integraciones", subscription: "Suscripción", support: "Soporte", memory: "Memoria", language: "Idioma y Región", theme: "Tema" },
    nav: { home: "Inicio", work: "Trabajo", health: "Salud", life: "Vida", chat: "Chat", settings: "Configuración" },
    cards: { chatDesc: "Hablemos", workDesc: "Paso a paso", healthDesc: "Cuídate", lifeDesc: "Mantente organizado", talkToAllora: "Hablar con Allora" },
    home: {
      todayProgress: "Progreso de hoy", hydration: "Hidratación", nutrition: "Nutrición", tasks: "Tareas", points: "Puntos",
      badges: "Insignias", viewAll: "Ver todo", noBadges: "Aún no hay insignias", keepStreak: "¡Sigue así para ganar insignias!",
      achievements: "logros", allCaughtUp: "¡Estás al día!", enjoyMorning: "Disfruta tu mañana",
      morningBriefing: "Resumen de la mañana", whatsOnPlate: "Esto es lo que tienes hoy",
      suggestionForYou: "Sugerencia para ti",
      smallStepsCount: "Los pequeños pasos cuentan. Estoy aquí para ti.",
      todaysPulse: "Pulso de hoy", energy: "Energía", mood: "Estado de ánimo", stress: "Estrés", focus: "Enfoque",
      balancedAndReady: "Equilibrado y listo", moderate: "Moderado", stable: "Estable", low: "Bajo", peak: "Pico", high: "Alto", ok: "OK",
      quickActions: "Acciones rápidas", mealPlanner: "Planificador de comidas", planYourMeals: "Planifica tus comidas",
      workouts: "Entrenamientos", exerciseLibrary: "Biblioteca de ejercicios", medicines: "Medicamentos", checkInteractions: "Verificar interacciones",
      logWater: "Registrar agua", trackHydration: "Seguir hidratación", goalsAndChallenges: "Metas y desafíos",
      activeChallenges: "Desafíos activos", newChallenge: "Nuevo desafío", dashboard: "Panel",
      sleep: "Sueño", history: "Historial", water: "Agua", calories: "Calorías",
      talkToAllora: "Hablar con Allora", youDontHaveToCarry: "No tienes que cargar con esto solo.",
      oneThingAtATime: "Una cosa a la vez. ¿Qué es lo más importante?",
      showTodaysProgress: "Mostrar progreso de hoy", hideTodaysProgress: "Ocultar progreso de hoy",
      eveningReflection: "Reflexión nocturna", howWasYourDay: "¿Cómo estuvo tu día? Toca para registrarte.",
      great: "Genial", good: "Bien", okay: "Bien", tough: "Difícil", maybeLater: "Quizás después",
      heyName: "Hola {name}. Estoy aquí.", goodMorning: "Buenos días", morning: "Mañana", evening: "Noche",
      lateNight: "Noche tarde", readyWhenYouAre: "Listo cuando lo estés.", howAreYou: "¿Cómo estás?",
      windingDown: "¿Relajándote o aún con energía?", imHere: "Estoy aquí. Háblame.",
      imHereToHelp: "Estoy aquí para ayudar, no para agobiarte.", voice: "Voz",
      tasksToComplete: "Tareas a completar", billsDueSoon: "Facturas próximas a vencer", appointmentsToday: "Citas de hoy",
      rememberWater: "Recuerda beber agua", bestFocus: "Mejor enfoque",
      // Emotional nudge messages
      cantSleep: "¿No puedes dormir? Estoy aquí. Sin juicios, sin recordatorios.",
      letsTakeCare: "Cuidémonos hoy. ¿Qué necesitas?",
      howAreYouDoing: "¿Cómo va hasta ahora? Estoy aquí si me necesitas.",
      afternoonCheckin: "Check-in de la tarde: ¿necesitas algo?",
      windingDownRest: "¿Relajándote? Has hecho suficiente hoy. Descansar también es productivo.",
      imHereNoRush: "Estoy aquí cuando me necesites. Sin prisa, sin presión.",
      failedLogMood: "Error al registrar el estado de ánimo. Por favor intenta de nuevo.",
      takeYourTime: "Tómate tu tiempo. Estoy aquí cuando estés listo.",
      tapToContinue: "Toca para continuar",
      alloraRemembers: "Allora recuerda...",
      badgesEarned: "Insignias Obtenidas",
      collector: "Coleccionista"
    },
    work: {
      title: "Modo Trabajo", tasks: "Tareas", documents: "Documentos", addTask: "Añadir tarea", newTask: "Nueva tarea", editTask: "Editar tarea",
      taskTitle: "Título", taskDescription: "Descripción", priority: "Prioridad", dueDate: "Fecha límite", status: "Estado",
      high: "Alta", medium: "Media", low: "Baja", urgent: "Urgente", pending: "Pendiente", inProgress: "En progreso", completed: "Completado",
      addDocument: "Añadir documento", uploadDocument: "Subir documento", analyzeDocument: "Analizar documento", documentSummary: "Resumen del documento",
      askQuestion: "Hacer una pregunta sobre este documento", todaysFocus: "Enfoque de hoy", productivityScore: "Puntuación de productividad",
      tasksCompleted: "Tareas completadas", noTasks: "Sin tareas", noDocuments: "Sin documentos", createFirstTask: "Crea tu primera tarea",
      taskTemplates: "Plantillas de tareas", batchActions: "Acciones masivas", aiDocumentAnalysis: "Análisis de documento IA",
      documentLibrary: "Biblioteca de documentos",
      noItemsSelected: "Ningún elemento seleccionado", pleaseSelectItems: "Por favor selecciona elementos primero",
      batchOperationComplete: "Operación masiva completada", appliedToItems: "aplicado a", items: "elementos",
      unableToBatch: "No se pudo completar la operación masiva", batchOperations: "Operaciones masivas",
      selected: "seleccionado(s)", markComplete: "Marcar como completado", archive: "Archivar", addTag: "Añadir etiqueta",
      quickTaskTemplates: "Plantillas de tareas rápidas", loadingTemplates: "Cargando plantillas...",
      chooseTemplate: "Elige una plantilla para crear tareas relacionadas instantáneamente:",
      templateApplied: "¡Plantilla aplicada!", createdTasksFrom: "Creado", tasksFromTemplate: "tareas de",
      templateFailed: "Plantilla fallida", unableToCreateTasks: "No se pudieron crear las tareas. Inténtalo de nuevo.",
      tasksCount: "tareas", useTemplate: "Usar plantilla"
    },
    health: {
      title: "Modo Salud", water: "Agua", food: "Comida", exercise: "Ejercicio", sleep: "Sueño", medications: "Medicamentos", vitals: "Signos vitales",
      addWater: "Añadir agua", logFood: "Registrar comida", logExercise: "Registrar ejercicio", logSleep: "Registrar sueño", addMedication: "Añadir medicamento",
      glasses: "vasos", calories: "calorías", steps: "pasos", hours: "horas", goal: "Meta", progress: "Progreso",
      waterGoal: "Meta de agua", calorieGoal: "Meta de calorías", stepsGoal: "Meta de pasos", sleepGoal: "Meta de sueño",
      morningRoutine: "Rutina matutina", eveningRoutine: "Rutina nocturna", medicationReminder: "Recordatorio de medicamento",
      weight: "Peso", bloodPressure: "Presión arterial", heartRate: "Frecuencia cardíaca", bloodSugar: "Glucosa en sangre",
      noMedications: "Sin medicamentos registrados", noWorkouts: "Sin entrenamientos registrados hoy",
      // Health challenges and goals
      startYourFirstChallenge: "Comienza tu primer desafío", healthGoals: "Metas de salud", newGoal: "Nueva meta",
      noGoalsYet: "Aún no hay metas establecidas", clickNewGoal: "Haz clic en 'Nueva meta' para comenzar", noActiveChallenges: "Sin desafíos activos",
      // Food scanner
      foodScanner: "Escáner de alimentos", scanLogMeal: "Escanear / Registrar comida", snapPhoto: "Toma una foto para análisis nutricional instantáneo"
    },
    life: {
      title: "Modo Vida", bills: "Facturas", expenses: "Gastos", appointments: "Citas", budget: "Presupuesto", goals: "Metas",
      addBill: "Añadir factura", addExpense: "Añadir gasto", addAppointment: "Añadir cita", setBudget: "Establecer presupuesto", addGoal: "Añadir meta",
      billName: "Nombre de factura", amount: "Monto", dueDate: "Fecha de vencimiento", category: "Categoría", recurring: "Recurrente", paid: "Pagado", unpaid: "Sin pagar",
      financialDashboard: "Panel financiero", monthlyExpenses: "Gastos mensuales", upcomingBills: "Facturas próximas",
      spendingTrends: "Tendencias de gastos", savingsGoals: "Metas de ahorro", currentBalance: "Saldo actual",
      noBills: "Sin facturas", noExpenses: "Sin gastos registrados", noAppointments: "Sin citas programadas",
      utilities: "Servicios", groceries: "Supermercado", transportation: "Transporte",
      entertainment: "Entretenimiento", healthcare: "Salud", other: "Otros", savings: "Ahorros",
      categories: {
        utilities: "Servicios", groceries: "Supermercado", transportation: "Transporte",
        entertainment: "Entretenimiento", healthcare: "Salud", other: "Otros", savings: "Ahorros"
      }
    },
    chat: {
      title: "Tu Espacio", placeholder: "¿Qué tienes en mente?", send: "Enviar", thinking: "Pensando...",
      newConversation: "Nueva conversación", history: "Historial", clearHistory: "Borrar historial",
      voiceInput: "Entrada de voz", attachFile: "Adjuntar archivo"
    },
    common: { loading: "Cargando...", error: "Algo salió mal", retry: "Reintentar", close: "Cerrar", back: "Atrás", next: "Siguiente", done: "Listo", pending: "pendiente", completed: "completado", glasses: "vasos", cal: "cal", today: "Hoy", yesterday: "Ayer", thisWeek: "Esta semana", thisMonth: "Este mes", all: "Todo", none: "Ninguno", total: "Total", average: "Promedio",
      youSaid: "Dijiste:", processingCommand: "Procesando comando...", exampleCommands: "Ejemplos de comandos:", action: "Acción"
    },
    locale: { detected: "Hemos configurado tu idioma a", currency: "y moneda a", changeAnytime: "Puedes cambiarlo en cualquier momento en Configuración." }
  },
  th: {
    greeting: { morning: "สวัสดีตอนเช้า", afternoon: "สวัสดีตอนบ่าย", evening: "สวัสดีตอนเย็น", night: "ราตรีสวัสดิ์" },
    subscription: { cta: "ทดลองฟรี", upgrade: "อัพเกรดเป็น Pro" },
    actions: { save: "บันทึก", cancel: "ยกเลิก", delete: "ลบ", done: "เสร็จ" },
    settings: { profile: "โปรไฟล์", integrations: "การเชื่อมต่อ", subscription: "การสมัครสมาชิก", support: "ช่วยเหลือ", memory: "ความจำ", language: "ภาษาและภูมิภาค", theme: "ธีม" },
    nav: { home: "หน้าหลัก", work: "งาน", health: "สุขภาพ", life: "ชีวิต", chat: "แชท", settings: "ตั้งค่า" },
    cards: { chatDesc: "พูดคุย", workDesc: "ทีละขั้น", healthDesc: "ดูแลตัวเอง", lifeDesc: "จัดระเบียบ" },
    home: {
      todayProgress: "ความคืบหน้าวันนี้", hydration: "การดื่มน้ำ", nutrition: "โภชนาการ", tasks: "งาน", points: "คะแนน",
      badges: "ตรา", viewAll: "ดูทั้งหมด", noBadges: "ยังไม่มีตรา", keepStreak: "ทำต่อเพื่อรับตรา!",
      achievements: "ความสำเร็จ", allCaughtUp: "เสร็จหมดแล้ว!", enjoyMorning: "สวัสดีตอนเช้า",
      morningBriefing: "สรุปตอนเช้า", whatsOnPlate: "นี่คือสิ่งที่ต้องทำวันนี้"
    },
    common: { loading: "กำลังโหลด...", error: "เกิดข้อผิดพลาด", retry: "ลองอีกครั้ง", close: "ปิด", back: "กลับ", next: "ถัดไป", done: "เสร็จ", pending: "รอดำเนินการ", completed: "เสร็จสิ้น", glasses: "แก้ว", cal: "แคล" },
    locale: { detected: "เราตั้งภาษาเป็น", currency: "และสกุลเงินเป็น", changeAnytime: "คุณสามารถเปลี่ยนได้ตลอดเวลาในการตั้งค่า" }
  },
  vi: {
    greeting: { morning: "Chào buổi sáng", afternoon: "Chào buổi chiều", evening: "Chào buổi tối", night: "Chúc ngủ ngon" },
    subscription: { cta: "Dùng thử miễn phí", upgrade: "Nâng cấp lên Pro" },
    actions: { save: "Lưu", cancel: "Hủy", delete: "Xóa", done: "Xong" },
    settings: { profile: "Hồ sơ", integrations: "Tích hợp", subscription: "Đăng ký", support: "Hỗ trợ", memory: "Bộ nhớ", language: "Ngôn ngữ & Vùng", theme: "Giao diện" },
    nav: { home: "Trang chủ", work: "Công việc", health: "Sức khỏe", life: "Cuộc sống", chat: "Trò chuyện", settings: "Cài đặt" },
    cards: { chatDesc: "Chia sẻ", workDesc: "Từng bước một", healthDesc: "Chăm sóc bản thân", lifeDesc: "Sắp xếp ngăn nắp" },
    home: {
      todayProgress: "Tiến độ hôm nay", hydration: "Uống nước", nutrition: "Dinh dưỡng", tasks: "Công việc", points: "Điểm",
      badges: "Huy hiệu", viewAll: "Xem tất cả", noBadges: "Chưa có huy hiệu", keepStreak: "Tiếp tục để nhận huy hiệu!",
      achievements: "thành tích", allCaughtUp: "Bạn đã hoàn thành!", enjoyMorning: "Chào buổi sáng",
      morningBriefing: "Tóm tắt buổi sáng", whatsOnPlate: "Đây là những gì bạn cần làm hôm nay"
    },
    common: { loading: "Đang tải...", error: "Đã xảy ra lỗi", retry: "Thử lại", close: "Đóng", back: "Quay lại", next: "Tiếp theo", done: "Xong", pending: "đang chờ", completed: "hoàn thành", glasses: "ly", cal: "cal" },
    locale: { detected: "Chúng tôi đã đặt ngôn ngữ của bạn thành", currency: "và tiền tệ thành", changeAnytime: "Bạn có thể thay đổi bất cứ lúc nào trong Cài đặt." }
  },
  zh: {
    greeting: { morning: "早上好", afternoon: "下午好", evening: "晚上好", night: "晚安" },
    auth: {
      tagline: "我记得昨天的感受",
      tagline2: "帮助你更好地度过今天。",
      welcomeBack: "欢迎回来",
      goodToSeeYou: "很高兴再次见到你",
      email: "邮箱",
      password: "密码",
      forgotPassword: "忘记密码？",
      signingIn: "登录中...",
      signIn: "登录",
      newToApp: "新用户？",
      createAccount: "创建账户"
    },
    subscription: { cta: "免费试用", upgrade: "升级到专业版",
      lastDayTrial: "试用最后一天", trialEndsToday: "试用今天结束", daysLeftTrial: "天剩余", then: "之后", mo: "月"
    },
    actions: { save: "保存", cancel: "取消", delete: "删除", done: "完成", add: "添加", edit: "编辑", update: "更新", create: "创建", remove: "移除", confirm: "确认", search: "搜索", filter: "筛选", sort: "排序", refresh: "刷新", upload: "上传", download: "下载", export: "导出", import: "导入" },
    settings: { 
      profile: "个人资料", integrations: "集成", subscription: "订阅", support: "支持", memory: "记忆", language: "语言和地区", theme: "主题",
      chooseLanguageRegion: "选择您喜欢的语言和地区以获得个性化体验",
      currentSettings: "当前设置", detectedLocation: "检测到的位置和偏好",
      country: "国家", currency: "货币", timezone: "时区",
      chooseLanguage: "选择您喜欢的语言", updateProfile: "更新您的个人信息",
      editProfile: "编辑资料", manageSubscription: "管理订阅", paymentHistory: "支付历史", logOut: "退出",
      notifications: "通知",
      // Goals Settings
      goals: "目标", personalGoals: "个人目标", personalGoalsDesc: "设置每日和每月目标以追踪进度",
      todaysProgress: "今日进度", calorieGoal: "每日卡路里目标", calorieGoalDesc: "追踪每日卡路里摄入",
      budgetGoal: "每月预算目标", budgetGoalDesc: "设置每月消费上限",
      waterGoal: "每日饮水目标", waterGoalDesc: "全天保持水分补充",
      stepsGoal: "每日步数目标", stepsGoalDesc: "追踪每日活动量",
      sleepGoal: "每日睡眠目标", sleepGoalDesc: "设置目标睡眠时长",
      dailyTarget: "每日目标", monthlyLimit: "每月上限", targetHours: "目标小时",
      recommended: "推荐", glasses: "杯", saveGoals: "保存目标",
      budgetTip: "生活模式的消费将对比此目标追踪"
    },
    nav: { home: "首页", work: "工作", health: "健康", life: "生活", chat: "聊天", settings: "设置" },
    cards: { chatDesc: "倾诉", workDesc: "一步一步来", healthDesc: "照顾好自己", lifeDesc: "保持有序", talkToAllora: "和Allora聊天" },
    home: {
      todayProgress: "今日进度", hydration: "饮水", nutrition: "营养", tasks: "任务", points: "积分",
      badges: "徽章", viewAll: "查看全部", noBadges: "还没有徽章", keepStreak: "继续保持以获得徽章！",
      achievements: "成就", allCaughtUp: "全部完成！", enjoyMorning: "享受你的早晨",
      morningBriefing: "早间简报", whatsOnPlate: "今天的安排",
      suggestionForYou: "为你推荐",
      smallStepsCount: "小步骤也很重要。我在这里陪你。",
      todaysPulse: "今日脉搏", energy: "精力", mood: "心情", stress: "压力", focus: "专注",
      balancedAndReady: "平衡就绪", moderate: "适中", stable: "稳定", low: "低", peak: "高峰", high: "高", ok: "好",
      quickActions: "快捷操作", mealPlanner: "膳食计划", planYourMeals: "规划你的膳食",
      workouts: "锻炼", exerciseLibrary: "运动库", medicines: "药物", checkInteractions: "检查相互作用",
      logWater: "记录饮水", trackHydration: "跟踪水分摄入", goalsAndChallenges: "目标和挑战",
      activeChallenges: "进行中的挑战", newChallenge: "新挑战", dashboard: "仪表板",
      sleep: "睡眠", history: "历史", water: "水", calories: "卡路里",
      talkToAllora: "和Allora聊天", youDontHaveToCarry: "你不必独自承担。",
      oneThingAtATime: "一件一件来。什么最重要？",
      showTodaysProgress: "显示今日进度", hideTodaysProgress: "隐藏今日进度",
      heyName: "嗨 {name}，我在这里。", goodMorning: "早上好", morning: "早上好", evening: "晚上好",
      lateNight: "深夜了", readyWhenYouAre: "准备好了就开始。", howAreYou: "你好吗？",
      windingDown: "放松一下还是继续加油？", imHere: "我在这里。和我聊聊。",
      imHereToHelp: "我是来帮忙的，不是来增加压力的。", voice: "语音",
      eveningReflection: "晚间反思", howWasYourDay: "今天怎么样？点击签到。",
      great: "很棒", good: "好", okay: "还好", tough: "艰难", maybeLater: "稍后",
      tasksToComplete: "待完成任务", billsDueSoon: "即将到期账单", appointmentsToday: "今日预约",
      rememberWater: "记得喝水", bestFocus: "最佳专注",
      // Emotional nudge messages
      cantSleep: "睡不着？我在这里。不评判，不提醒。",
      letsTakeCare: "今天让我们照顾好你。你需要什么？",
      howAreYouDoing: "到目前为止感觉怎么样？需要的话我在这里。",
      afternoonCheckin: "下午签到：需要我做什么吗？",
      windingDownRest: "放松了吗？今天你已经做够了。休息也是一种效率。",
      imHereNoRush: "我随时都在。不着急。",
      failedLogMood: "心情记录失败。请重试。",
      takeYourTime: "慢慢来。准备好了我就在。",
      tapToContinue: "点击继续",
      alloraRemembers: "Allora记得...",
      badgesEarned: "获得的徽章",
      collector: "收集者"
    },
    work: {
      title: "工作模式", tasks: "任务", documents: "文档", addTask: "添加任务", newTask: "新任务", editTask: "编辑任务",
      taskTitle: "标题", taskDescription: "描述", priority: "优先级", dueDate: "截止日期", status: "状态",
      high: "高", medium: "中", low: "低", urgent: "紧急", pending: "待处理", inProgress: "进行中", completed: "已完成",
      addDocument: "添加文档", uploadDocument: "上传文档", analyzeDocument: "分析文档", documentSummary: "文档摘要",
      askQuestion: "关于此文档提问", todaysFocus: "今日重点", productivityScore: "生产力评分",
      tasksCompleted: "已完成任务", noTasks: "暂无任务", noDocuments: "暂无文档", createFirstTask: "创建你的第一个任务",
      allCaughtUp: "全部完成！", noUrgentTasks: "目前没有紧急任务。做得好！",
      taskTemplates: "任务模板", batchActions: "批量操作", aiDocumentAnalysis: "AI文档分析",
      documentLibrary: "文档库",
      comingUp: "即将到来", next3Days: "未来3天", dueIn: "还有", moreUpcomingTasks: "更多即将到期的任务",
      dueToday: "今日截止", dueTomorrow: "明日截止", overdue: "已逾期", estimatedTime: "预计时间",
      uploadDescription: "上传MP3/MP4录音、PDF文档或粘贴文本，即可进行AI分析并生成任务",
      noDocumentsYet: "暂无已分析的文档",
      useAIDocumentAnalysis: "使用上方的AI文档分析来分析文件",
      compare: "比较",
      error: "错误", pleaseEnterTitle: "请输入任务标题",
      noItemsSelected: "未选择项目", pleaseSelectItems: "请先选择项目",
      batchOperationComplete: "批量操作完成", appliedToItems: "已应用于", items: "个项目",
      unableToBatch: "无法完成批量操作", batchOperations: "批量操作",
      selected: "已选择", markComplete: "标记完成", archive: "归档", addTag: "添加标签",
      quickTaskTemplates: "快速任务模板", loadingTemplates: "加载模板中...",
      chooseTemplate: "选择模板以快速创建相关任务：",
      templateApplied: "模板已应用！", createdTasksFrom: "已创建", tasksFromTemplate: "个任务来自",
      templateFailed: "模板应用失败", unableToCreateTasks: "无法创建任务，请重试。",
      tasksCount: "个任务", useTemplate: "使用模板",
      // New keys for i18n
      subscription: "订阅", pro: "专业版", freePlan: "免费版", aiAccess: "AI访问", unlimited: "无限制", credits: "积分",
      upgradeToPro: "升级到专业版", uploadFile: "上传文件 (MP3, MP4, PDF, TXT, DOC)", clickToSelect: "点击选择或拖放文件",
      audioVideoDetected: "检测到音频/视频", analyzeWhisper: "点击\"分析\"使用AI (Whisper)转录并分析您的音频",
      pasteUrl: "粘贴URL (文章、博客、Google文档等)", aiFetchContent: "AI将从URL获取并分析内容",
      documentTextOptional: "文档文本/转录（可选）", or: "或", insights: "洞察", suggestedTasks: "建议任务",
      askAnythingDoc: "关于此文档提问：", titleRequired: "标题 *", descriptionLabel: "描述",
      analyzing: "分析中...", analyze: "分析", summary: "摘要", keyPoints: "要点",
      generateTasks: "生成任务", saveAndDone: "保存完成"
    },
    health: {
      title: "健康模式", water: "水", food: "食物", exercise: "运动", sleep: "睡眠", medications: "药物", vitals: "生命体征",
      addWater: "添加饮水", logFood: "记录饮食", logExercise: "记录运动", logSleep: "记录睡眠", addMedication: "添加药物", logWorkout: "记录锻炼", addFood: "添加食物",
      glasses: "杯", calories: "卡路里", steps: "步", hours: "小时", goal: "目标", progress: "进度",
      waterGoal: "饮水目标", calorieGoal: "卡路里目标", stepsGoal: "步数目标", sleepGoal: "睡眠目标",
      morningRoutine: "晨间例程", eveningRoutine: "晚间例程", medicationReminder: "服药提醒",
      weight: "体重", bloodPressure: "血压", heartRate: "心率", bloodSugar: "血糖",
      noMedications: "暂无记录的药物", noWorkouts: "今天暂无锻炼记录",
      healthCalendar: "健康日历", trackYourHealthJourney: "追踪你的健康之旅",
      nutritionTracker: "营养追踪", protein: "蛋白质", carbs: "碳水", fats: "脂肪", fiber: "纤维", sugar: "糖",
      ofGoal: "目标", limit: "限制", womensHealth: "女性健康",
      medicines: "药物", noMedicinesAdded: "尚未添加药物", takenToday: "已服用", takeNow: "现在服用", taken: "已服用",
      logSymptoms: "记录症状", symptoms: "症状", describeSymptomsPlaceholder: "描述您的症状...", severity: "严重程度",
      mild: "轻微", moderate: "中度", severe: "严重", notesOptional: "备注（可选）", additionalNotesPlaceholder: "任何其他备注...",
      waterIntake: "饮水量", burned: "消耗", caloriesIntake: "摄入卡路里",
      nutrition: "营养", workoutsCount: "锻炼次数", mealsCount: "餐数", noHealthData: "此日暂无健康数据",
      healthDataFor: "健康数据", duration: "时长", minutes: "分钟",
      // Calorie goals
      setCalorieGoal: "设置卡路里目标", dailyCalorieTarget: "每日卡路里目标",
      calorieGoalSet: "卡路里目标设置成功", trackAgainstFoodLogs: "对照食物记录追踪",
      wantToTrackMeals: "想记录今天的餐食吗？", remainingCalories: "剩余",
      calorieProgress: "卡路里进度", ofDailyGoal: "每日目标的", mealsLogged: "已记录餐食数",
      updateGoal: "更新目标", calorieRange: "请输入500-10,000卡路里之间的数值",
      describeMeal: "描述您的餐食", mealPlaceholder: "例如：烤鸡胸肉配糙米饭和西兰花",
      analyzing: "分析中...", analyzeFood: "分析食物",
      healthAssistant: "健康助手", askHealthQuestion: "健康问题",
      getPersonalizedAdvice: "获取基于您追踪数据的个性化健康建议",
      avgWater: "平均饮水", avgCalories: "平均卡路里", daysTracked: "追踪天数",
      keepMoving: "继续运动！", stayHydrated: "保持水分！", burnIt: "燃烧吧！", keepLogging: "继续记录！",
      autoSleepTracking: "自动睡眠追踪已开启", recentSleepLogs: "最近睡眠记录",
      noSleepLogs: "暂无睡眠记录。开始追踪以获得洞察！", logYourFirstSleep: "记录你的第一次睡眠",
      lastNight: "昨晚", quality: "质量", bedtime: "就寝时间", wakeTime: "起床", log: "记录",
      sleepStages: "睡眠阶段", deepSleep: "深度睡眠", lightSleep: "浅睡眠", remSleep: "REM睡眠",
      good: "好", fair: "一般", poor: "差",
      // Health challenges and goals
      startYourFirstChallenge: "开始你的第一个挑战", healthGoals: "健康目标", newGoal: "新目标",
      noGoalsYet: "还没有设定目标", clickNewGoal: "点击'新目标'开始", noActiveChallenges: "暂无进行中的挑战",
      // Food scanner
      foodScanner: "食物扫描仪", scanLogMeal: "扫描 / 记录餐食", snapPhoto: "拍照获取即时营养分析",
      quickActions: "快捷操作",
      // New keys for i18n
      loadingHealthMode: "加载健康模式中...", subscription: "订阅", pro: "专业版", freePlan: "免费版",
      aiAccess: "AI访问", unlimited: "无限制", credits: "积分", upgradeToPro: "升级到专业版",
      takePhoto: "拍照", uploadPhoto: "上传照片", viewAIAnalysis: "查看AI分析",
      healthAssistantTitle: "您的个人健康助手", iHaveAccess: "我可以访问您的健康数据：",
      todaysActivity: "今日活动（步数、卡路里、饮水）", recentWorkouts: "最近的锻炼记录",
      currentMedicines: "当前药物和剂量", recentMeals: "最近的餐食和营养", loggedSymptoms: "已记录的症状",
      todaysMeals: "今日餐食", addToDiary: "添加到日记", tryAgain: "重试"
    },
    life: {
      title: "生活模式", bills: "账单", expenses: "支出", appointments: "预约", budget: "预算", goals: "目标",
      addBill: "添加账单", addExpense: "添加支出", addAppointment: "添加预约", setBudget: "设置预算", addGoal: "添加目标",
      billName: "账单名称", amount: "金额", dueDate: "到期日", category: "类别", recurring: "循环", paid: "已付", unpaid: "未付",
      financialDashboard: "财务仪表板", monthlyExpenses: "月度支出", upcomingBills: "即将到期账单",
      spendingTrends: "消费趋势", savingsGoals: "储蓄目标", currentBalance: "当前余额",
      noBills: "暂无账单", noExpenses: "暂无支出记录", noAppointments: "暂无预约",
      smartFinancial: "智能财务", billsAndGoals: "账单和目标", calendar: "日历",
      thisMonthSpending: "本月支出", topSpendingCategory: "最高支出类别", billStatus: "账单状态",
      upcoming: "即将到期", overdue: "已逾期", sixMonthTrend: "6个月趋势",
      vsLastMonth: "对比上月", loadingFinancialInsights: "加载财务洞察...",
      budgetTracker: "预算追踪", used: "已使用", left: "剩余",
      smartFinancialDashboard: "智能财务仪表板", aiPoweredInsights: "AI驱动的财务管理洞察",
      totalExpenses: "总支出", transactions: "交易", appointmentsCount: "预约", billsDue: "应付账单",
      monthlySpend: "月度支出", totalCount: "总数", scheduled: "已安排", pending: "待处理",
      lifeCalendar: "生活日历", trackExpensesAppointments: "追踪支出、预约和账单",
      visualIndicators: "每个日期上的视觉指示器",
      spendingByCategory: "按类别支出", noSpendingData: "暂无支出数据",
      setBudgetBtn: "设置预算", totalMonthlyBudget: "每月预算总额", categoryBudgets: "类别预算",
      saveBudget: "保存预算", overall: "总体",
      // Budget goals translations
      setBudgetGoal: "设置预算目标", monthlySpendingLimit: "每月支出上限",
      budgetGoalSet: "预算目标设置成功", trackAgainstExpenses: "对照支出和账单追踪",
      budgetProgress: "预算进度", ofMonthlyBudget: "每月预算的",
      passedBudgetGoal: "看起来你超出了目标。要调整下个月的目标吗？",
      overBudget: "超出预算", underBudget: "预算内", onTrack: "正常",
      categoryLimits: "类别上限", food: "餐饮", travel: "旅行",
      shopping: "购物", billsCategory: "账单", spentThisMonth: "本月支出",
      daysLeftInMonth: "本月剩余天数", noBudgetSet: "尚未设置预算",
      setBudgetToTrack: "设置每月预算以追踪您的支出",
      financialGoals: "财务目标", addNewGoal: "添加新目标", goalName: "目标名称",
      targetAmount: "目标金额", currentAmount: "当前金额", targetDate: "目标日期",
      goalCategory: "类别", createGoal: "创建目标", noGoals: "暂无财务目标",
      setFirstGoal: "设置您的第一个储蓄目标开始追踪",
      overdueBills: "逾期账单", due: "到期", addAppointmentTitle: "添加预约",
      type: "类型", date: "日期", time: "时间", location: "地点",
      utilities: "水电费", groceries: "杂货", transportation: "交通",
      entertainment: "娱乐", healthcare: "医疗", other: "其他", savings: "储蓄",
      categories: {
        utilities: "水电费", groceries: "杂货", transportation: "交通",
        entertainment: "娱乐", healthcare: "医疗", other: "其他", savings: "储蓄"
      },
      theseAreHere: "准备好就处理 — 不着急",
      allClear: "一切就绪！", helpRemember: "账单到期时我会提醒你",
      noAppointmentsShort: "暂无预约", subscription: "订阅", pro: "专业版", freePlan: "免费版",
      aiAccess: "AI访问", unlimited: "无限制", credits: "积分",
      smartFinancialDashboardTitle: "💰 智能财务仪表板",
      expensesMoneySpent: "花费金额", appointmentsScheduled: "已安排的活动", billsPaymentReminders: "付款提醒",
      clickDateToView: "点击日期查看支出、预约和账单",
      expensesFor: "支出详情", totalSpent: "总花费", categoryBreakdown: "类别明细",
      allTransactions: "所有交易", noExpensesRecorded: "当天无支出记录",
      startTrackingSpending: "开始追踪以查看您的消费模式",
      hasData: "有数据", noData: "无数据", protein: "蛋白质", carbs: "碳水化合物", fat: "脂肪"
    },
    notifications: {
      title: "通知设置", active: "通知已启用",
      receiveReminders: "即使关闭应用也能收到提醒",
      disable: "禁用", enable: "启用", enableNotifications: "启用通知",
      stayOnTrack: "用提醒保持正轨",
      remindYouAbout: "我可以提醒你关于饮水、药物、账单等事项",
      gentleReminders: "温馨提醒包括：",
      maybeLater: "以后再说",
      changeAnytime: "你可以随时在设置中更改",
      enabling: "正在启用...",
      customize: "自定义通知",
      morningGreeting: "早间问候", morningDesc: "每天早上8点的任务和预约简报",
      eveningSummary: "晚间总结", eveningDesc: "每天晚上8点的成就总结",
      waterReminders: "饮水提醒", waterDesc: "定期提醒保持水分",
      medicineReminders: "服药提醒", medicineDesc: "不要错过服药",
      appointmentAlerts: "预约提醒", appointmentDesc: "会议和预约前提醒",
      motivationBoosts: "励志推送", motivationDesc: "下午的鼓励消息",
      waterReminderInterval: "饮水提醒间隔",
      everyHour: "每1小时", every2Hours: "每2小时", every3Hours: "每3小时", every4Hours: "每4小时",
      savePreferences: "保存偏好", blocked: "通知被阻止",
      enableInBrowser: "请在浏览器设置中启用通知"
    },
    chat: {
      title: "你的空间", placeholder: "有什么想说的？", send: "发送", thinking: "思考中...",
      newConversation: "新对话", history: "历史", clearHistory: "清除历史",
      voiceInput: "语音输入", attachFile: "附加文件"
    },
    womensHealth: {
      cycleDay: "天", phase: "期", welcome: "欢迎", logPeriodToStart: "记录月经开始追踪",
      menstrual: "月经期", follicular: "卵泡期", ovulation: "排卵期", luteal: "黄体期",
      nextPeriodIn: "下次月经还有", days: "天", logPeriod: "记录月经", dailyLog: "每日记录",
      todaysMood: "今日心情", symptomsLogged: "症状已记录", today: "今天", calendar: "日历",
      insights: "洞察", settings: "设置", articles: "文章", tips: "小贴士",
      pregnancyMode: "孕期模式", trackPregnancy: "追踪您的孕期旅程",
      great: "很好", good: "好", okay: "还行", low: "低落", bad: "不好", none: "无",
      physical: "身体", discharge: "分泌物", mood: "心情", sleep: "睡眠", activity: "活动", intimacy: "亲密",
      cramps: "痛经", headache: "头痛", fatigue: "疲劳", bloating: "腹胀", breastTenderness: "乳房胀痛",
      acne: "痤疮", backPain: "背痛", nausea: "恶心", dizziness: "头晕",
      light: "少量", creamy: "乳白", eggWhite: "蛋清状", watery: "水状", sticky: "粘稠", spotting: "点滴出血",
      happy: "开心", calm: "平静", energetic: "精力充沛", moodSwings: "情绪波动", irritable: "烦躁", anxious: "焦虑", sad: "难过", sensitive: "敏感",
      goodSleep: "睡眠良好", insomnia: "失眠", restless: "不安", oversleep: "过度睡眠", vividDreams: "多梦", nightSweats: "夜间盗汗",
      exercise: "运动", yoga: "瑜伽", walking: "步行", running: "跑步", swimming: "游泳", restDay: "休息日",
      protected: "有保护", unprotected: "无保护", highDrive: "欲望强", lowDrive: "欲望弱",
      periodDays: "经期天数", cycleLength: "周期长度", averageCycle: "平均周期", averagePeriod: "平均经期", cyclesTracked: "已追踪周期",
      cycleInsights: "周期洞察",
      logNotes: "记录备注", notesPlaceholder: "今天有什么特别的事？",
      week: "周", trimester: "孕期", weeksToGo: "还有几周", dueDate: "预产期", setDueDate: "设置预产期",
      logSymptoms: "记录症状", exitPregnancyMode: "退出孕期模式",
      pregnancyTips: "孕期提示", yourBabyThisWeek: "宝宝这周", yourBodyThisWeek: "您的身体这周",
      partnerAccess: "伴侣访问", generateShareLink: "生成分享链接", linkCopied: "链接已复制",
      reminders: "提醒", addReminder: "添加提醒", pillReminder: "药物提醒", periodReminder: "经期提醒",
      appointmentReminder: "预约提醒", reminderTime: "提醒时间", reminderLabel: "提醒标签",
      selectStartDate: "选择开始日期", howManyDays: "持续几天？", periodStartDate: "经期开始日期", periodEndDate: "经期结束日期",
      confirm: "确认", cancel: "取消", save: "保存", delete: "删除",
      cycleHistory: "周期历史", noCycleData: "暂无周期数据", startTracking: "开始追踪以查看洞察",
      fertileWindow: "受孕窗口", highFertility: "高受孕率", lowFertility: "低受孕率",
      energyLevel: "能量水平", dataPrivate: "您的数据安全私密 💜",
      tipsFor: "小贴士", phaseTips: "期",
      // Phase tips - menstrual
      tipMenstrualFood: "经期饮食建议", tipMenstrualFoodDesc: "富含铁质和抗炎食物可缓解痛经",
      tipMenstrualExercise: "经期轻柔运动", tipMenstrualExerciseDesc: "低强度运动有助于缓解不适",
      tipMenstrualSelfCare: "经期自我护理", tipMenstrualSelfCareDesc: "经期需要休息、温暖和舒适",
      // Phase tips - follicular
      tipFollicularEnergy: "活力提升活动", tipFollicularEnergyDesc: "精力正在恢复 - 适合开始新项目",
      tipFollicularSkin: "卵泡期护肤", tipFollicularSkinDesc: "皮肤状态较好 - 适合去角质",
      tipFollicularSocial: "社交活动推荐", tipFollicularSocialDesc: "雌激素上升使这成为社交好时机",
      // Phase tips - ovulation
      tipOvulationFertility: "高受孕期", tipOvulationFertilityDesc: "了解您最易受孕的时间窗口",
      tipOvulationWorkout: "高强度锻炼", tipOvulationWorkoutDesc: "身体处于最佳状态",
      tipOvulationCommunication: "沟通与自信", tipOvulationCommunicationDesc: "排卵期语言能力达到高峰",
      // Phase tips - luteal
      tipLutealPMS: "经前综合征管理", tipLutealPMSDesc: "经前期的应对技巧",
      tipLutealFood: "有益的舒适食品", tipLutealFoodDesc: "健康的舒适食品替代品",
      tipLutealStress: "压力管理技巧", tipLutealStressDesc: "黄体期的放松方法"
    },
    nudge: {
      iNoticed: "我注意到了一些事情...",
      suggestedAction: "建议的行动：",
      wantMeToHelp: "需要我帮忙吗？",
      quickHydrationCheck: "快速水分检查",
      hadGlassesToday: "今天你喝了{count}杯水。喝点水怎么样？",
      tryShortBursts: "试试短时间开始"
    },
    common: { loading: "加载中...", error: "出错了", retry: "重试", close: "关闭", back: "返回", next: "下一步", done: "完成", pending: "待处理", completed: "已完成", glasses: "杯", cal: "卡", today: "今天", yesterday: "昨天", thisWeek: "本周", thisMonth: "本月", all: "全部", none: "无", total: "总计", average: "平均", day: "天", days: "天",
      youSaid: "您说的:", processingCommand: "正在处理命令...", exampleCommands: "示例命令:", action: "操作",
      yes: "是", no: "否", skip: "跳过", adjust: "调整"
    },
    locale: { detected: "我们已将您的语言设置为", currency: "货币设置为", changeAnytime: "您可以随时在设置中更改。" }
  }
};

// ==================== CULTURAL PERSONALIZATION ====================

const CULTURAL_DATA = {
  // Weekend days by region (0 = Sunday, 6 = Saturday)
  weekendDays: {
    default: [0, 6],           // Sun-Sat (most countries)
    middleEast: [5, 6],        // Fri-Sat (UAE, Saudi, etc.)
    israel: [5, 6],            // Fri-Sat
  },
  
  // Countries using each weekend pattern
  weekendByCountry: {
    AE: 'middleEast', SA: 'middleEast', QA: 'middleEast', KW: 'middleEast', BH: 'middleEast', OM: 'middleEast',
    IL: 'israel',
    // All others use 'default'
  },
  
  // Hemisphere by country (for seasons)
  hemisphere: {
    // Southern hemisphere countries
    AU: 'south', NZ: 'south', BR: 'south', AR: 'south', CL: 'south', ZA: 'south', ID: 'south',
    // All others are 'north' by default
  },
  
  // Seasonal adjustments (month ranges)
  seasons: {
    north: {
      spring: [3, 4, 5],     // Mar-May
      summer: [6, 7, 8],     // Jun-Aug
      autumn: [9, 10, 11],   // Sep-Nov
      winter: [12, 1, 2]     // Dec-Feb
    },
    south: {
      autumn: [3, 4, 5],     // Mar-May
      winter: [6, 7, 8],     // Jun-Aug
      spring: [9, 10, 11],   // Sep-Nov
      summer: [12, 1, 2]     // Dec-Feb
    }
  },
  
  // Regional food recommendations
  foodByRegion: {
    IN: {
      breakfast: ['Poha', 'Upma', 'Idli', 'Dosa', 'Paratha'],
      lunch: ['Dal Rice', 'Roti Sabzi', 'Biryani', 'Thali'],
      dinner: ['Khichdi', 'Chapati', 'Curry'],
      snacks: ['Samosa', 'Pakora', 'Bhel Puri', 'Chaat'],
      healthy: ['Sprouts', 'Raita', 'Buttermilk', 'Coconut Water']
    },
    JP: {
      breakfast: ['Miso Soup', 'Rice', 'Tamagoyaki', 'Natto'],
      lunch: ['Bento', 'Ramen', 'Udon', 'Onigiri'],
      dinner: ['Grilled Fish', 'Tempura', 'Shabu-shabu'],
      snacks: ['Edamame', 'Onigiri', 'Taiyaki'],
      healthy: ['Tofu', 'Seaweed', 'Green Tea', 'Miso']
    },
    CN: {
      breakfast: ['Congee', 'Baozi', 'Jianbing', 'Soy Milk'],
      lunch: ['Fried Rice', 'Noodles', 'Dumplings', 'Stir-fry'],
      dinner: ['Hot Pot', 'Peking Duck', 'Mapo Tofu'],
      snacks: ['Spring Rolls', 'Mooncakes', 'Bao'],
      healthy: ['Bok Choy', 'Green Tea', 'Goji Berries', 'Tofu']
    },
    KR: {
      breakfast: ['Kimchi', 'Rice', 'Soup', 'Banchan'],
      lunch: ['Bibimbap', 'Kimbap', 'Japchae'],
      dinner: ['Korean BBQ', 'Samgyetang', 'Jjigae'],
      snacks: ['Tteok', 'Hotteok', 'Bungeoppang'],
      healthy: ['Kimchi', 'Seaweed', 'Ginseng Tea', 'Bean Sprouts']
    },
    ID: {
      breakfast: ['Nasi Goreng', 'Bubur Ayam', 'Mie Goreng'],
      lunch: ['Nasi Campur', 'Gado-gado', 'Soto'],
      dinner: ['Rendang', 'Satay', 'Nasi Padang'],
      snacks: ['Pisang Goreng', 'Bakso', 'Martabak'],
      healthy: ['Tempeh', 'Tofu', 'Coconut Water', 'Fruits']
    },
    BR: {
      breakfast: ['Pão de Queijo', 'Açaí', 'Tapioca'],
      lunch: ['Feijoada', 'Churrasco', 'Rice and Beans'],
      dinner: ['Moqueca', 'Picanha', 'Coxinha'],
      snacks: ['Brigadeiro', 'Pastel', 'Açaí Bowl'],
      healthy: ['Açaí', 'Cassava', 'Heart of Palm', 'Tropical Fruits']
    },
    MX: {
      breakfast: ['Chilaquiles', 'Huevos Rancheros', 'Tacos'],
      lunch: ['Tacos', 'Enchiladas', 'Pozole'],
      dinner: ['Mole', 'Tamales', 'Carnitas'],
      snacks: ['Elote', 'Churros', 'Tostadas'],
      healthy: ['Avocado', 'Black Beans', 'Nopales', 'Papaya']
    },
    US: {
      breakfast: ['Eggs', 'Oatmeal', 'Smoothie', 'Toast'],
      lunch: ['Salad', 'Sandwich', 'Soup', 'Bowl'],
      dinner: ['Grilled Chicken', 'Salmon', 'Steak', 'Pasta'],
      snacks: ['Nuts', 'Yogurt', 'Fruit', 'Protein Bar'],
      healthy: ['Quinoa', 'Kale', 'Avocado', 'Berries']
    },
    AU: {
      breakfast: ['Avocado Toast', 'Eggs Benedict', 'Muesli'],
      lunch: ['Meat Pie', 'Fish and Chips', 'Salad'],
      dinner: ['BBQ', 'Lamb', 'Barramundi'],
      snacks: ['Tim Tams', 'Lamington', 'Anzac Biscuits'],
      healthy: ['Avocado', 'Macadamia', 'Kangaroo Meat', 'Seafood']
    },
    default: {
      breakfast: ['Eggs', 'Toast', 'Oatmeal', 'Smoothie'],
      lunch: ['Salad', 'Sandwich', 'Soup'],
      dinner: ['Grilled Protein', 'Vegetables', 'Rice'],
      snacks: ['Nuts', 'Fruit', 'Yogurt'],
      healthy: ['Vegetables', 'Lean Protein', 'Whole Grains', 'Water']
    }
  },
  
  // Work culture patterns
  workCulture: {
    JP: { lunchDuration: 60, workStart: '09:00', workEnd: '18:00', focusHours: [10, 11, 14, 15, 16] },
    KR: { lunchDuration: 60, workStart: '09:00', workEnd: '18:00', focusHours: [10, 11, 14, 15, 16] },
    US: { lunchDuration: 30, workStart: '09:00', workEnd: '17:00', focusHours: [9, 10, 11, 14, 15] },
    DE: { lunchDuration: 45, workStart: '08:00', workEnd: '17:00', focusHours: [9, 10, 11, 14, 15] },
    ES: { lunchDuration: 90, workStart: '09:00', workEnd: '19:00', focusHours: [10, 11, 12, 16, 17, 18] },
    FR: { lunchDuration: 60, workStart: '09:00', workEnd: '18:00', focusHours: [10, 11, 14, 15, 16] },
    IN: { lunchDuration: 45, workStart: '09:30', workEnd: '18:30', focusHours: [10, 11, 12, 15, 16, 17] },
    AE: { lunchDuration: 60, workStart: '08:00', workEnd: '17:00', focusHours: [9, 10, 11, 14, 15] },
    default: { lunchDuration: 45, workStart: '09:00', workEnd: '17:00', focusHours: [9, 10, 11, 14, 15] }
  },
  
  // AI tone variations by culture
  aiTone: {
    JP: { formality: 'high', directness: 'indirect', emotionalSupport: 'subtle' },
    KR: { formality: 'high', directness: 'indirect', emotionalSupport: 'warm' },
    DE: { formality: 'medium', directness: 'direct', emotionalSupport: 'practical' },
    US: { formality: 'low', directness: 'direct', emotionalSupport: 'encouraging' },
    BR: { formality: 'low', directness: 'direct', emotionalSupport: 'warm' },
    IN: { formality: 'medium', directness: 'balanced', emotionalSupport: 'warm' },
    FR: { formality: 'medium', directness: 'balanced', emotionalSupport: 'elegant' },
    ES: { formality: 'low', directness: 'direct', emotionalSupport: 'warm' },
    CN: { formality: 'high', directness: 'indirect', emotionalSupport: 'encouraging' },
    default: { formality: 'medium', directness: 'balanced', emotionalSupport: 'supportive' }
  }
};

// Default translations (English)
const defaultTranslations = TRANSLATIONS.en;

// ==================== LOCALE CONTEXT ====================

const LocaleContext = createContext(null);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  // State
  const [locale, setLocale] = useState({
    language: 'en',
    country: 'US',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    currency: 'USD',
    symbol: '$',
    source: 'auto' // 'auto', 'user', or 'detected'
  });
  
  const [pricing, setPricing] = useState(() => {
    const defaultPricing = getRegionalPricing('US');
    const savingsPercent = Math.round((1 - (defaultPricing.yearly / (defaultPricing.monthly * 12))) * 100);
    return {
      symbol: '$',
      monthly: defaultPricing.monthly,
      yearly: defaultPricing.yearly,
      currency: 'USD',
      savingsPercent,
      taxInclusive: false
    };
  });
  
  const [translations, setTranslations] = useState(defaultTranslations);
  const [showLocaleConfirmation, setShowLocaleConfirmation] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [travelDetection, setTravelDetection] = useState({
    detected: false,
    oldTimezone: null,
    newTimezone: null,
    suggestedCountry: null
  });

  // Initialize locale on mount
  useEffect(() => {
    initializeLocale();
  }, []);
  
  // Listen for locale updates from AuthContext (after login)
  useEffect(() => {
    const handleLocaleUpdate = (event) => {
      const { language, country } = event.detail;
      if (language) {
        console.log('Locale update received:', language, country);
        applyLocaleSettings(language, country || 'US', 'user');
      }
    };
    
    window.addEventListener('lifeflo-locale-updated', handleLocaleUpdate);
    return () => window.removeEventListener('lifeflo-locale-updated', handleLocaleUpdate);
  }, []);

  // Initialize locale with auto-detection
  const initializeLocale = async () => {
    // Step 1: ALWAYS try to fetch user's saved language from backend first (if logged in)
    // This ensures we sync with backend changes even if localStorage has old values
    try {
      const userLocaleResponse = await fetch(`${API_URL}/api/user/locale`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (userLocaleResponse.ok) {
        const userData = await userLocaleResponse.json();
        if (userData.success && userData.locale?.language) {
          const userLang = userData.locale.language;
          const userCountry = userData.locale.country_code || 'US';
          
          // Update localStorage to match backend (source of truth)
          localStorage.setItem('lifeflo_language', userLang);
          localStorage.setItem('lifeflo_country', userCountry);
          localStorage.setItem('lifeflo_locale_set', 'true');
          
          applyLocaleSettings(userLang, userCountry, 'user');
          setIsInitialized(true);
          return;
        }
      }
    } catch (e) {
      // User not logged in or API error - continue with localStorage or auto-detection
    }
    
    // Step 2: Fallback to localStorage if backend fetch failed
    const savedLanguage = localStorage.getItem('lifeflo_language');
    const savedCountry = localStorage.getItem('lifeflo_country');
    const hasUserSet = localStorage.getItem('lifeflo_locale_set') === 'true';
    
    if (savedLanguage && savedCountry && hasUserSet) {
      // User has explicitly set preferences - use those
      applyLocaleSettings(savedLanguage, savedCountry, 'user');
      setIsInitialized(true);
      return;
    }
    
    // Step 2: Auto-detect
    const browserLanguage = navigator.language?.split('-')[0] || 'en';
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    
    try {
      // Detect from backend (uses IP + timezone)
      const response = await fetch(`${API_URL}/api/localization/detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: browserTimezone }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const detected = data.detected;
        
        // Use browser language if available in translations, otherwise use detected
        const language = (savedLanguage || (TRANSLATIONS[browserLanguage] ? browserLanguage : detected.language)) || 'en';
        const country = savedCountry || detected.country_code || 'US';
        
        applyLocaleSettings(language, country, 'auto');
        
        // Update pricing
        if (data.pricing) {
          setPricing({
            symbol: data.pricing.symbol,
            monthly: data.pricing.monthly.price,
            yearly: data.pricing.yearly.price,
            currency: data.pricing.currency,
            savingsPercent: data.pricing.yearly.savings_percent || 17
          });
        }
        
        // Show soft confirmation for first-time users (after signup)
        if (!hasUserSet) {
          // We'll show this after user logs in, not immediately
          localStorage.setItem('lifeflo_show_locale_confirm', 'true');
        }
      }
    } catch (error) {
      console.log('Using browser defaults for locale');
      applyLocaleSettings(savedLanguage || browserLanguage, savedCountry || 'US', 'auto');
    }
    
    setIsInitialized(true);
  };

  // Apply locale settings
  const applyLocaleSettings = async (language, country, source) => {
    const countryConfig = COUNTRIES[country] || COUNTRIES.US;
    const lang = TRANSLATIONS[language] ? language : 'en';
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    
    // Get regional pricing for this country
    const regionalPrice = getRegionalPricing(country);
    const savingsPercent = Math.round((1 - (regionalPrice.yearly / (regionalPrice.monthly * 12))) * 100);
    
    setLocale({
      language: lang,
      country: country,
      timezone: browserTimezone,
      currency: countryConfig.currency,
      symbol: countryConfig.symbol,
      source: source
    });
    
    // Update pricing based on country
    setPricing({
      symbol: countryConfig.symbol,
      monthly: regionalPrice.monthly,
      yearly: regionalPrice.yearly,
      currency: countryConfig.currency,
      savingsPercent,
      taxInclusive: countryConfig.taxInclusive || false
    });
    
    setTranslations(TRANSLATIONS[lang] || defaultTranslations);
    
    // Apply RTL if needed
    const isRtl = RTL_LANGUAGES.includes(lang);
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
    
    // Auto-save to backend if this is auto-detection (not user override)
    if (source === 'auto') {
      try {
        await fetch(`${API_URL}/api/user/locale`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            language: lang,
            country: country,
            timezone: browserTimezone
          })
        });
      } catch (error) {
        // Silent fail - user may not be logged in yet
      }
    }
  };

  // User explicitly sets language
  const setLanguage = (lang) => {
    const newLang = TRANSLATIONS[lang] ? lang : 'en';
    
    setLocale(prev => ({ ...prev, language: newLang, source: 'user' }));
    setTranslations(TRANSLATIONS[newLang] || defaultTranslations);
    
    // Persist
    localStorage.setItem('lifeflo_language', newLang);
    localStorage.setItem('lifeflo_locale_set', 'true');
    
    // Apply RTL
    const isRtl = RTL_LANGUAGES.includes(newLang);
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', newLang);
    
    // Save to backend
    saveLocaleToBackend({ language: newLang });
  };

  // User explicitly sets country
  const setCountry = async (countryCode) => {
    const config = COUNTRIES[countryCode] || COUNTRIES.US;
    
    // Get regional pricing for this country
    const regionalPrice = getRegionalPricing(countryCode);
    const savingsPercent = Math.round((1 - (regionalPrice.yearly / (regionalPrice.monthly * 12))) * 100);
    
    setLocale(prev => ({
      ...prev,
      country: countryCode,
      currency: config.currency,
      symbol: config.symbol,
      source: 'user'
    }));
    
    // Update pricing with regional prices
    setPricing({
      symbol: config.symbol,
      monthly: regionalPrice.monthly,
      yearly: regionalPrice.yearly,
      currency: config.currency,
      savingsPercent,
      taxInclusive: config.taxInclusive || false
    });
    
    // Persist
    localStorage.setItem('lifeflo_country', countryCode);
    localStorage.setItem('lifeflo_locale_set', 'true');
    
    // Save to backend
    saveLocaleToBackend({ country: countryCode });
  };

  // Save preferences to backend
  const saveLocaleToBackend = async (updates) => {
    try {
      await fetch(`${API_URL}/api/user/locale`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.error('Failed to save locale preferences:', error);
    }
  };

  // User explicitly sets timezone
  const setTimezone = (tz) => {
    setLocale(prev => ({ ...prev, timezone: tz, source: 'user' }));
    localStorage.setItem('lifeflo_timezone', tz);
    localStorage.setItem('lifeflo_locale_set', 'true');
    
    // Save to backend - important for notifications
    saveLocaleToBackend({ timezone: tz });
  };

  // Save all locale settings to backend (called after detection)
  const saveFullLocaleToBackend = async () => {
    try {
      await fetch(`${API_URL}/api/user/locale`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          language: locale.language,
          country: locale.country,
          timezone: locale.timezone
        })
      });
    } catch (error) {
      console.error('Failed to save full locale preferences:', error);
    }
  };

  // Translation helper
  const t = (key) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English
        value = defaultTranslations;
        for (const fallbackKey of keys) {
          value = value?.[fallbackKey];
          if (value === undefined) break;
        }
        break;
      }
    }
    
    return value || key;
  };

  // Format price for display
  const formatPrice = (amount, showCurrency = true) => {
    const noDecimalCurrencies = ['JPY', 'KRW', 'IDR', 'VND', 'NGN', 'INR', 'PHP', 'TWD', 'MXN', 'THB'];
    const countryConfig = COUNTRIES[locale.country] || COUNTRIES.US;
    
    // Use Intl.NumberFormat for proper locale-based formatting
    try {
      const formatter = new Intl.NumberFormat(countryConfig.locale, {
        style: showCurrency ? 'currency' : 'decimal',
        currency: locale.currency,
        minimumFractionDigits: noDecimalCurrencies.includes(locale.currency) ? 0 : 2,
        maximumFractionDigits: noDecimalCurrencies.includes(locale.currency) ? 0 : 2
      });
      return formatter.format(amount);
    } catch (e) {
      // Fallback formatting
      const formatted = noDecimalCurrencies.includes(locale.currency)
        ? Math.round(amount).toLocaleString()
        : amount.toFixed(2);
      return showCurrency ? `${locale.symbol}${formatted}` : formatted;
    }
  };

  // Format date according to locale
  const formatDate = (date, options = {}) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const countryConfig = COUNTRIES[locale.country] || COUNTRIES.US;
    
    return dateObj.toLocaleDateString(countryConfig.locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    });
  };

  // Format time according to locale (12h vs 24h)
  const formatTime = (date, options = {}) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const countryConfig = COUNTRIES[locale.country] || COUNTRIES.US;
    
    return dateObj.toLocaleTimeString(countryConfig.locale, {
      hour: 'numeric',
      minute: '2-digit',
      ...options
    });
  };

  // Get time-appropriate greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const greetings = translations.greeting || defaultTranslations.greeting;
    
    if (hour >= 5 && hour < 12) return greetings.morning;
    if (hour >= 12 && hour < 17) return greetings.afternoon;
    if (hour >= 17 && hour < 21) return greetings.evening;
    return greetings.night;
  };

  // Check if current language is RTL
  const isRTL = () => RTL_LANGUAGES.includes(locale.language);

  // Get list of supported countries
  const getSupportedCountries = () => {
    return Object.entries(COUNTRIES).map(([code, config]) => ({
      code,
      ...config
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Get list of supported languages
  const getSupportedLanguages = () => {
    return Object.entries(LANGUAGES).map(([code, config]) => ({
      code,
      ...config,
      hasTranslations: !!TRANSLATIONS[code]
    })).filter(l => l.hasTranslations);
  };

  // Dismiss locale confirmation
  const dismissLocaleConfirmation = () => {
    setShowLocaleConfirmation(false);
    localStorage.removeItem('lifeflo_show_locale_confirm');
  };

  // Check if should show locale confirmation
  const checkLocaleConfirmation = () => {
    if (localStorage.getItem('lifeflo_show_locale_confirm') === 'true') {
      setShowLocaleConfirmation(true);
    }
  };

  // ==================== CULTURAL HELPERS ====================
  
  // Get weekend days for current country
  const getWeekendDays = () => {
    const pattern = CULTURAL_DATA.weekendByCountry[locale.country] || 'default';
    return CULTURAL_DATA.weekendDays[pattern];
  };
  
  // Check if a date is a weekend
  const isWeekend = (date) => {
    const dayOfWeek = (date instanceof Date ? date : new Date(date)).getDay();
    return getWeekendDays().includes(dayOfWeek);
  };
  
  // Get current season for user's location
  const getCurrentSeason = () => {
    const hemisphere = CULTURAL_DATA.hemisphere[locale.country] || 'north';
    const month = new Date().getMonth() + 1;
    const seasons = CULTURAL_DATA.seasons[hemisphere];
    
    for (const [season, months] of Object.entries(seasons)) {
      if (months.includes(month)) return season;
    }
    return 'spring';
  };
  
  // Get food recommendations for user's region
  const getFoodRecommendations = (mealType = 'breakfast') => {
    const foods = CULTURAL_DATA.foodByRegion[locale.country] || CULTURAL_DATA.foodByRegion.default;
    return foods[mealType] || foods.healthy;
  };
  
  // Get work culture settings
  const getWorkCulture = () => {
    return CULTURAL_DATA.workCulture[locale.country] || CULTURAL_DATA.workCulture.default;
  };
  
  // Get AI tone settings for cultural appropriateness
  const getAITone = () => {
    return CULTURAL_DATA.aiTone[locale.country] || CULTURAL_DATA.aiTone.default;
  };
  
  // Check if current time is within focus hours
  const isFocusHour = () => {
    const hour = new Date().getHours();
    const workCulture = getWorkCulture();
    return workCulture.focusHours.includes(hour);
  };
  
  // Get culturally appropriate lunch time suggestion
  const getLunchDuration = () => {
    return getWorkCulture().lunchDuration;
  };
  
  // Get all cultural context for AI
  const getCulturalContext = () => {
    return {
      country: locale.country,
      language: locale.language,
      timezone: locale.timezone,
      season: getCurrentSeason(),
      weekendDays: getWeekendDays(),
      aiTone: getAITone(),
      workCulture: getWorkCulture(),
      foodPreferences: {
        breakfast: getFoodRecommendations('breakfast'),
        lunch: getFoodRecommendations('lunch'),
        dinner: getFoodRecommendations('dinner'),
        healthy: getFoodRecommendations('healthy')
      },
      hemisphere: CULTURAL_DATA.hemisphere[locale.country] || 'north'
    };
  };
  
  // Travel detection - monitor timezone changes
  useEffect(() => {
    const checkTimezoneChange = () => {
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (currentTimezone !== locale.timezone && isInitialized && locale.timezone) {
        console.log('🌍 Timezone change detected:', locale.timezone, '->', currentTimezone);
        
        // Get suggested country from timezone mapping
        const tzInfo = TIMEZONE_TO_COUNTRY[currentTimezone];
        const oldTzInfo = TIMEZONE_TO_COUNTRY[locale.timezone];
        
        // Set travel detection state to show notification
        setTravelDetection({
          detected: true,
          oldTimezone: locale.timezone,
          newTimezone: currentTimezone,
          oldRegion: oldTzInfo?.region || getTimezoneDisplayName(locale.timezone),
          newRegion: tzInfo?.region || getTimezoneDisplayName(currentTimezone),
          suggestedCountry: tzInfo?.country || null,
          flag: tzInfo?.flag || '🌍'
        });
        
        // Update locale with new timezone
        setLocale(prev => ({ ...prev, timezone: currentTimezone }));
        
        // Update backend with new timezone (mark as travel update)
        saveLocaleToBackend({ timezone: currentTimezone, is_travel_update: true });
      }
    };
    
    // Check on focus (when user returns to app)
    window.addEventListener('focus', checkTimezoneChange);
    
    // Also check on visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTimezoneChange();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check periodically (every 5 minutes)
    const interval = setInterval(checkTimezoneChange, 5 * 60 * 1000);
    
    // Initial check after a short delay
    const initialCheck = setTimeout(checkTimezoneChange, 2000);
    
    return () => {
      window.removeEventListener('focus', checkTimezoneChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, [locale.timezone, isInitialized]);
  
  // Function to dismiss travel detection notification
  const dismissTravelDetection = () => {
    setTravelDetection({
      detected: false,
      oldTimezone: null,
      newTimezone: null,
      suggestedCountry: null
    });
  };
  
  // Function to accept timezone change and optionally update country
  const acceptTimezoneChange = (updateCountry = false) => {
    if (updateCountry && travelDetection.suggestedCountry) {
      setCountry(travelDetection.suggestedCountry);
    }
    dismissTravelDetection();
  };

  const value = {
    // State
    locale,
    pricing,
    translations,
    isInitialized,
    showLocaleConfirmation,
    
    // Setters
    setLanguage,
    setCountry,
    setTimezone,
    dismissLocaleConfirmation,
    checkLocaleConfirmation,
    saveFullLocaleToBackend,
    
    // Helpers
    t,
    formatPrice,
    formatDate,
    formatTime,
    getGreeting,
    isRTL,
    getSupportedCountries,
    getSupportedLanguages,
    
    // Cultural helpers
    getWeekendDays,
    isWeekend,
    getCurrentSeason,
    getFoodRecommendations,
    getWorkCulture,
    getAITone,
    isFocusHour,
    getLunchDuration,
    getCulturalContext,
    
    // Travel detection
    travelDetection,
    dismissTravelDetection,
    acceptTimezoneChange,
    getTimezoneDisplayName: (tz) => {
      const mapping = TIMEZONE_TO_COUNTRY[tz];
      if (mapping) return `${mapping.flag} ${mapping.region}`;
      return tz.replace(/_/g, ' ').split('/').pop();
    },
    getRegionalPricing,
    
    // Constants
    COUNTRIES,
    LANGUAGES,
    CULTURAL_DATA,
    TIMEZONE_TO_COUNTRY,
    REGIONAL_PRICING
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
